const translations = {
	imports: {
		history: {
			title: "Previous imports",
			helper: "Track uploaded files and the records they created.",
			loading: "Loading imports…",
			loadError: "Could not load imports.",
			retry: "Try again",
			loadMore: "Show older imports",
			empty: "No previous imports in this area.",
			unnamed: "Unnamed file",
			progress: "Reading: {{done}} of {{total}} parts complete",
			importedCount: "{{count}} records imported",
			viewRecords: "View created records",
			animalRecordsUnavailable:
				"Older imports retain the confirmed total, but not a link to each created animal.",
			continue: "Open import",
			cancel: "Discard import",
			cancelError: "Could not discard the import. Please try again.",
			cancelTitle: "Discard this import?",
			cancelDescription:
				"The file stays in your history as cancelled. Values you haven't confirmed yet will be lost.",
			keep: "Keep it",
			confirmCancel: "Discard",
			failures: {
				MEDIA_CONTENT_MISMATCH:
					"The uploaded content does not match this analysis. Please upload the correct file.",
				TOO_MANY_ROWS:
					"This file has more lines than we can read in one go. Split it into smaller files, or send the list as a spreadsheet.",
				UPLOAD_EXPIRED: "The file upload did not finish. Please try again.",
				NO_DATA:
					"No matching data was found. The file may be unreadable or unrelated to this import.",
			},
			status: {
				PROCESSING: "In progress",
				READY_FOR_REVIEW: "Ready to review",
				FAILED: "Reading failed",
				IMPORTED: "Imported",
				CANCELLED: "Cancelled",
			},
		},
		open: {
			title_one: "Finish or discard your open import to upload a new file",
			title_other: "{{count}} open imports are blocking a new upload",
			helper_one:
				"Only one import can be open at a time. Finish reviewing it, or discard it if you no longer need it. The upload area appears right after.",
			helper_other:
				"Only one import can be open at a time. Continue the one you need and discard the rest. The upload area appears as soon as nothing is open.",
			uploadedAt: "Uploaded {{date}}",
			continueReview: "Continue review",
			viewProgress: "View progress",
			discard: "Discard",
			discarded: "Import discarded",
		},
	},
	pwa: {
		updateAvailable: "New version available",
		update: "Update",
		updatePendingWork: "We'll update as soon as your work is synced.",
		offlineReady: "App ready for offline use",
		loading: "Loading…",
		sessionExpired: "Session expired",
		sessionExpiredDescription:
			"Your offline session has expired. Reconnect to continue.",
		firstUseOffline: "Connect once to get started",
		firstUseOfflineDescription:
			"Sign in while connected to set up offline access for this device.",
		installTitle: "Install RuralOps",
		installDescription: "Add to your home screen for fast offline access.",
		install: "Install",
		offlineAuthSession: "Offline — signed in as {{name}}",
		pendingChanges: "Back online — {{count}} change(s) waiting to sync.",
	},
	time: {
		now: "now",
		minutesShort: "{{count}}m",
		hoursShort: "{{count}}h",
		daysShort: "{{count}}d",
	},
	offline: {
		action: {
			unavailable: "Not available offline",
			checking: "Checking the connection…",
			restored: "Connection is back — tap again to continue.",
		},
		banner: {
			message: "You're offline — some actions are disabled.",
		},
		status: {
			offline: "Offline",
			queued: "{{count}} pending",
			syncing: "Syncing…",
		},
		mutations: {
			queued: "Saved offline — will sync when reconnected.",
			willBeLost:
				"You're offline. This change is not supported offline and will be lost if you close the app.",
			enqueueFailed: "Failed to save offline. Reconnect and try again.",
		},
		queue: {
			title: "Pending changes",
			syncAll: "Sync all",
			syncOne: "Sync",
			discard: "Discard",
			empty: "No pending changes.",
			retries: "{{count}} retries",
			stuck: "Stuck",
			kinds: {
				animalsCreate: "New animal",
				measurement: "Weighing",
				operation: "Handling record",
				paddockNote: "Pasture note",
				breakdown: "Breakdown report",
				taskDone: "Task completed",
				manualEntry: "Money in or out",
				unknown: "Pending change",
			},
		},
		logout: {
			blockedByQueue:
				"You have {{count}} pending change(s). Sync or discard them before signing out.",
		},
		center: {
			appVersion: { title: "App Version" },
			connectivity: {
				offline: "Offline",
				online: "Online",
				title: "Connectivity",
			},
			datasets: {
				animals: "Animals",
				herds: "Herds",
				paddocks: "Paddocks",
				equipment: "Equipment",
				parts: "Parts",
				tasks: "Tasks",
				financialCategories: "Financial categories",
				vendors: "Vendors",
				vaccinationRules: "Sanitary calendar",
				count: "{{count}} animals",
				estimatedSize: "~{{size}} MB",
				lastSynced: "Last synced {{time}}",
				neverSynced: "Never synced",
				overCap: "Too many records (>10,000) — reduce farm size",
				availableOffline: "Available offline",
				prepare: "Prepare",
				refresh: "Refresh",
				title: "Offline Data",
				scope: {
					allFarms: "All Farms",
				},
				unavailable: {
					description:
						"Prepare this data in the Offline Center while connected.",
					title: "Not available offline",
				},
			},
			prepareAllPartial:
				"{{failed}} of {{total}} farms could not be prepared: {{names}}",
			queue: { title: "Pending Changes" },
			storage: { title: "Storage", usage: "{{used}} of {{quota}} used" },
			title: "Offline Center",
		},
		datasets: {
			unavailable: {
				description:
					"Connect to the internet and prepare offline data in the Offline Center.",
				title: "Data not available offline",
			},
		},
		detail: {
			offlineNote:
				"Showing offline data — full history is available when connected.",
		},
		screen: {
			unavailable: {
				description: "Reconnect to the internet to continue.",
				title: "This feature requires an internet connection",
			},
		},
	},
	auth: {
		signIn: {
			title: "Sign in to RuralOps",
			subtitle: "Access your account to follow the work on your farm.",
		},
		scene: {
			titleFirst: "A platform for",
			titleSecond: "rural operations.",
			description:
				"Livestock, pasture, tasks, equipment, and costs together, so you can follow the work and make decisions with the records at hand.",
			photoAlt:
				"Open pasture at dusk with a Nelore herd grazing in the distance",
		},
	},
	footer: {
		copyright: "© {{year}} RuralOps. All rights reserved.",
		privacyPolicy: "Privacy Policy",
		termsOfService: "Terms of Service",
	},
	pricing: {
		badge: "Pricing",
		title: "Choose the Perfect Plan for Your Property",
		subtitle:
			"Flexible plans that grow with your business. Start free and upgrade as you need.",
		getStarted: "Get Started",
		startFree: "Start free",
		subscribeNow: "Subscribe now",
		perMonth: "/mo",
		perYear: "/yr",
		billedAnnually: "billed annually",
		billedMonthly: "billed monthly",
		monthly: "Monthly",
		annual: "Annual",
		save20: "Save 20%",
		mostPopular: "Most Popular",
		benefitsTitle: "Why Choose RuralOps?",
		benefits: [
			"Complete property management",
			"Real-time tracking and reports",
			"Mobile access anywhere",
			"Dedicated support",
			"Regular updates and new features",
		],
		contactTitle: "Questions?",
		contactDescription:
			"Our team is ready to help you find the best solution for your property.",
		emailSales: "Email Us",
		whatsappSales: "WhatsApp",
		responseTime: "We usually respond within 24 hours",
		faqTitle: "Frequently Asked Questions",
		faqDescription:
			"Can't find what you're looking for? Contact our support team.",
		contactUs: "Contact Us",
		plans: {
			starter: {
				name: "Starter",
				bestFor: "Very small farms / trials",
				description: "Perfect for getting started with farm management",
			},
			professional: {
				name: "Professional",
				bestFor: "Growing operations",
				description: "Full farm management with AI assistance",
			},
			enterprise: {
				name: "Enterprise",
				bestFor: "Large operations",
				description: "Unlimited scale with priority support",
			},
		},
		features: {
			oneEstablishment: "1 farm",
			oneUser: "1 user",
			upTo50Animals: "Up to 50 animals",
			upTo3Establishments: "Up to 3 farms",
			upTo3Users: "Up to 3 users",
			upTo200Animals: "Up to 200 animals",
			aiPoweredInsights: "AI-powered insights",
			emailSupport: "Standard reports",
			upTo10Users: "Up to 10 users",
			upTo500Animals: "Up to 500 animals",
			prioritySupport: "Priority support",
		},
		addons: {
			title: "Powerful Add-ons",
			subtitle: "Enhance your plan with additional features",
			aiOptimizer: {
				name: "AI Optimizer",
				description: "Unlock intelligent farm optimization",
				perMonth: "/mo",
				note: "Available for Professional or Enterprise plans",
			},
			overage: {
				title: "Grow Without Limits",
				animals: {
					title: "Additional Animals",
					description:
						"+{{price}} per additional {{step}} active animals/month",
				},
				farms: {
					title: "Additional Farms",
					description: "+{{price}} per additional farm/month",
				},
				aiCredits: {
					title: "Extra AI Actions",
					description: "Top up AI credits anytime — {{min}} to {{max}}",
				},
			},
		},
		trialAvailable: "{{days}}-day free trial",
		current: "Current Plan",
	},
	browserCompatibility: {
		title: "Outdated Browser Detected",
		message:
			"Your browser may not support all features of this application. Please update your browser for the best experience.",
	},
	selectOrganization: {
		title: "Your organizations",
		subtitle: "Pick an organization to open, or set up a new one.",
		ownedTitle: "You own",
		invitedTitle: "You're invited to",
		emptyOwned: "You don't own any organizations yet.",
		emptyInvited:
			"When another owner invites you into their organization, it'll show up here.",
		selectButton: "Open",
		deleteButton: "Delete organization",
		countLabel_one: "{{count}} organization",
		countLabel_other: "{{count}} organizations",
		explainer: {
			title: "What is an organization?",
			definition:
				"Think of an organization as your business inside RuralOps. It holds everything that belongs to it: farms, livestock, team, and finances.",
			rule: "If you have one or several farms within the same business, they should all live inside the same organization and share the same paid plan.",
			newOrg:
				"Only create a new organization when they're separate businesses, with distinct teams, books, and billing.",
			hide: "Hide",
			show: "Learn what an organization is",
		},
		status: {
			checking: "Checking access…",
			active: "Active",
			trialing: "Trial",
			trialingWithDays_one: "Trial · {{count}} day left",
			trialingWithDays_other: "Trial · {{count}} days left",
			setupRequired: "Setup required",
			paymentRequired: "Payment required",
			pastDue: "Payment overdue",
			inactive: "Inactive",
			awaitingOwner: "Awaiting owner action",
			unknown: "Status unavailable",
		},
		action: {
			open: "Open",
			choosePlan: "Choose a plan",
			updatePayment: "Update payment",
			reactivate: "Reactivate",
			awaitingOwner: "Awaiting owner",
		},
		plan: {
			free: "Free plan",
			starter: "Starter plan",
			professional: "Professional plan",
			enterprise: "Enterprise plan",
		},
		invitedInfo:
			"Owner is responsible for billing. If access is blocked, ask them to renew the subscription.",
		invitation: {
			pendingBadge: "Invitation",
			invitedBy: "Invited by {{inviterName}}",
			accept: "Accept",
			decline: "Decline",
			accepting: "Accepting invitation…",
			acceptSuccess: "Invitation accepted",
			declining: "Declining invitation…",
			declineSuccess: "Invitation declined",
		},
		deleteDialog: {
			title: "Delete Organization",
			description:
				"Are you sure you want to delete the organization '{{organizationName}}'?",
			irreversibleWarning: "This action is irreversible!",
			dataLossWarning:
				"All data associated with this organization will be permanently deleted, including farms, livestock, pastures, financial records, equipment, tasks, team members, and all other records.",
			confirmLabel: "Type '{{organizationName}}' to confirm:",
			deleting: "Deleting...",
			confirmButton: "Delete Organization",
		},
	},
	country: {
		BR: "Brazil",
		PY: "Paraguay",
		UY: "Uruguay",
		AR: "Argentina",
		CL: "Chile",
		BO: "Bolivia",
		CO: "Colombia",
		US: "United States",
	},
	organization: {
		createNew: "Create organization",
		createDescription:
			"Set up a new rural business in RuralOps — its own team, records, and subscription.",
		createExplainer: {
			title: "Before you continue",
			definition: "Think of an organization as your business inside RuralOps.",
			contents:
				"It's where everything about that business lives: farms, livestock, team, and finances.",
			rule: "It doesn't matter whether you have one farm or ten. As long as they all belong to the same business, they should live inside the same organization.",
			billing:
				"Each organization has its own subscription — the plan is billed separately for each one.",
			newOrgWhen:
				"So create a new organization only if you run separate businesses, with distinct teams and books.",
			newOrgWhenNot:
				"If you're just adding another farm to a business you already have, add that farm inside the current organization.",
		},
		name: "Organization name",
		nameHelp: "How your business will appear across RuralOps.",
		namePlaceholder: "e.g. Fazenda São Bento",
		description: "Description",
		descriptionHelp:
			"Optional. A short note to help you tell organizations apart.",
		descriptionPlaceholder: "e.g. Beef cattle operation in Mato Grosso",
		country: {
			label: "Country",
			placeholder: "Select a country",
			help: "Sets the organization's sanitary-calendar country. Changing it later does not replace existing vaccination rules.",
		},
		currency: {
			label: "Financial currency",
			placeholder: "Select a currency",
			brl: "Brazilian Real (R$)",
			usd: "US Dollar ($)",
			warning:
				"Used only for this organization's financial records (revenue, costs, reports). It can't be changed later, and it's separate from how you pay for your RuralOps subscription.",
		},
		noOrganizations: "You're not part of any organization yet",
		noOrganizationsDescription:
			"Create your first organization to start managing your operation, or ask another owner to invite you into theirs.",
		createFirst: "Create your first organization",
		basicInformation: "Basic Information",
		current: "Current",
		noDescription: "No description",
		leave: "Leave",
		leaveOrganization: "Leave Organization",
		leaveConfirmation:
			"Are you sure you want to leave the organization '{{name}}'? You will lose access to all its data.",
		ownerLeaveWarning:
			"As the owner, you cannot leave the organization. Transfer ownership or delete the organization instead.",
		confirmNameToLeave: "Type the organization name to confirm",
		typeOrgNameToConfirm: "Type the organization name exactly as shown above",
	},
	orgSettings: {
		title: "Organization Settings",
		profile: {
			nameLabel: "Organization name",
			namePlaceholder: "Organization name",
			nameHelp: "Shown across the app and in the organization switcher.",
		},
		validation: {
			nameRequired: "Organization name is required",
			nameTooLong: "Organization name must be 100 characters or less",
			descriptionTooLong:
				"Organization description must be 500 characters or less",
		},
		language: {
			selectLanguage: "Select a language",
		},
		currency: {
			title: "Financial currency",
			immutableNote:
				"Used for your farm's financial management. Set when the organization was created and cannot be changed. Not related to your subscription or billing.",
		},
	},
	notifications: {
		dailyDigest: {
			title: "Daily digest email",
			label: "Receive the daily digest",
			description:
				"Every morning we send an email with what needs attention on your farm: overdue weighings and vaccinations, herds that have stayed too long in a paddock, upcoming calvings and overdue tasks. If nothing is pending, no email is sent.",
			updateError: "Could not update your preference. Please try again.",
		},
	},
	userSettings: {
		title: "User Settings",
		profile: "Profile",
		profileDescription: "Manage your personal information and preferences",
		organizations: "Organizations",
		organizationsDescription: "Manage your organizations and memberships",
		validation: {
			firstNameRequired: "First name is required",
			lastNameRequired: "Last name is required",
		},
	},
	roles: {
		org_owner: "Owner",
		org_administrator: "Administrator",
		org_manager: "Manager",
		org_medical_professional: "Medical Professional",
		org_accountant: "Accountant",
		org_viewer: "Viewer",
		descriptions: {
			org_owner: "Organization Owner",
			org_administrator: "Organization Administrator",
			org_manager: "Organization Manager",
			org_medical_professional: "Organization Medical Professional",
			org_accountant: "Organization Accountant",
			org_viewer: "Organization Viewer",
		},
	},
	permissions: {
		categories: {
			organization: "Organization",
			staff: "Staff",
			establishments: "Establishments",
			customers: "Customers",
			customerPlans: "Plans",
			medical: "Medical",
			finance: "Finance",
			reports: "Reports",
			schedules: "Schedules",
			enrollments: "Enrollments",
			attendance: "Attendance",
			other: "Other",
		},
		actions: {
			VIEW_SETTINGS: "View settings",
			MANAGE_SETTINGS: "Manage settings",
			DELETE_ORGANIZATION: "Delete organization",
			VIEW_STAFF: "View staff",
			MANAGE_STAFF: "Manage staff",
			INVITE_STAFF: "Invite staff",
			VIEW_PLATFORM_PLAN: "View platform plan",
			MANAGE_PLATFORM_PLAN: "Manage platform plan",
			VIEW_ESTABLISHMENTS: "View establishments",
			MANAGE_ESTABLISHMENTS: "Manage establishments",
			VIEW_ANALYTICS: "View analytics",
			VIEW_FARMS: "View farms",
			MANAGE_FARMS: "Manage farms",
			VIEW_PADDOCKS: "View paddocks",
			MANAGE_PADDOCKS: "Manage paddocks",
			VIEW_CULTIVARS: "View cultivars",
			MANAGE_CULTIVARS: "Manage cultivars",
			VIEW_OPERATIONS: "View operations",
			CREATE_OPERATIONS: "Create operations",
			MANAGE_OPERATIONS: "Manage operations",
			VIEW_MEASUREMENTS: "View measurements",
			CREATE_MEASUREMENTS: "Create measurements",
			MANAGE_MEASUREMENTS: "Manage measurements",
			VIEW_PLANS: "View plans",
			MANAGE_PLANS: "Manage plans",
			APPROVE_WORK_ORDERS: "Approve work orders",
			VIEW_FORECASTS: "View forecasts",
			VIEW_RECOMMENDATIONS: "View recommendations",
			APPROVE_RECOMMENDATIONS: "Approve recommendations",
			VIEW_EQUIPMENT: "View equipment",
			MANAGE_EQUIPMENT: "Manage equipment",
			VIEW_PARTS_INVENTORY: "View parts inventory",
			MANAGE_PARTS_INVENTORY: "Manage parts inventory",
			PERFORM_INSPECTIONS: "Perform inspections",
			REPORT_BREAKDOWNS: "Report breakdowns",
			VIEW_LIVESTOCK: "View livestock",
			VIEW_LIVESTOCK_RECORDS: "View livestock records",
			VIEW_LIVESTOCK_MOVEMENTS: "View livestock movements",
			MANAGE_LIVESTOCK: "Manage livestock",
			VIEW_FEASIBILITY: "View feasibility",
			MANAGE_FEASIBILITY: "Manage feasibility",
			USE_AI_ASSISTANT: "Use AI assistant",
		},
	},
	invitations: {
		emailMismatchTitle: "Email Mismatch",
		emailMismatchDescription:
			"You're logged in as {{currentEmail}}, but this invitation was sent to {{expectedEmail}}.",
		emailMismatchInstructions:
			"Please sign in with the correct email to accept this invitation.",
		switchAccount: "Switch Account",
		invitationInvalid: "Invalid Invitation",
		alreadyAccepted: "This invitation has already been accepted.",
		tokenExpired: "This invitation has expired.",
		tokenRevoked: "This invitation has been revoked.",
		declined: "You declined this invitation.",
		alreadyMember: "You're already a member of this organization.",
		invitationNotFound: "Invitation not found.",
		goToDashboard: "Go to Dashboard",
		processingInvitation: "Processing Invitation",
		pleaseWait: "Please wait while we process your invitation...",
		invitationAccepted: "Invitation Accepted!",
		invitationError: "Error Processing Invitation",
		invitationDetails: "Invitation Details",
		invitationIntro: "You've been invited to join an organization:",
		email: "Email",
		organization: "Account",
		invitedBy: "Invited by",
		role: "Role",
		acceptInvitation: "Accept Invitation",
	},
	onboarding: {
		stepOf: "Step {{current}} of {{total}}",
	},
	setupGuide: {
		ariaLabel: "Setup guide",
		page: {
			goHome: "Go to home",
			eyebrow: "Step {{current}} of {{total}} · {{status}}",
			farmIntro:
				"Your organization has already been created. It can include more than one farm, for example, if you have farms in different cities.",
			farmFormBody:
				"Use the name you say every day. The rest can wait until later.",
			farmSavedTitle: "{{name}} is ready",
			farmSavedBody:
				"If you have another farm, add it now. They will stay together in this organization.",
			addAnotherFarm: "Add another farm",
			continueAfterFarms: "Continue",
		},
		progress_one: "{{count}} of {{total}} steps done",
		progress_other: "{{count}} of {{total}} steps done",
		later: "Do this later",
		steps: {
			ORG_READY: "Account ready",
			FARM_CREATED: "Add your farm",
			FIRST_RECORD: "Let's get started",
			// Someone who cannot create records reads the same step without being
			// told to do the impossible: the record belongs to the farm, not to them.
			FIRST_RECORD_READ_ONLY: "The farm's first record",
		},
		status: {
			done: "Done",
			now: "Now",
			later: "Later",
			attention: "Needs attention",
		},
		farmStep: {
			title: "Let's set up your farm",
			body: "It takes a few steps. You can stop and come back later.",
			action: "Add farm",
			saved: "Farm saved to RuralOps",
			saveFailed: "We could not save it. Check your connection and try again.",
			planLimitReached:
				"Your current plan does not allow another farm right now. You can upgrade at any time.",
			nextStep: "Next step",
			noPermission: "Ask whoever manages the account to add the farm.",
		},
		// Coming back after three days with a farm and no record (§7.7). A
		// different tone for the same guide: it never repeats the welcome, never
		// says the person is late, and never restarts the three steps.
		returning: {
			title: "Want to pick up where you left off?",
			body: "Your farm is already added. Now choose what to note.",
		},
		// First view for someone who joined through an invitation (§17, phase 6).
		// Names the place, who invited them, and what they can do — always in
		// everyday words, never with an internal role or permission name (§8).
		invited: {
			title: "You joined {{name}}",
			titleGeneric: "You joined this account",
			invitedBy: "{{name}} invited you.",
			scopeTitle: "Here you can:",
			scope: {
				LIVESTOCK: "Add animals",
				FINANCE: "Note money in and out",
				PLANNING: "Create tasks",
				PASTURE: "Add paddocks",
				EQUIPMENT: "Add machines",
			},
			scopeReadOnly: "See the farm data.",
			moreOptions: "See other options",
		},
		helpStep: {
			title: "Help is always close by",
			body: "After setup, use the highlighted Help button in the top-right navigation whenever you need guidance or want to contact us.",
			guidesTitle: "Find answers",
			guidesBody: "Open practical guides that show how to use RuralOps.",
			feedbackTitle: "Send feedback",
			feedbackBody:
				"In Help, open Support to submit a suggestion, request or complaint as a ticket and follow the reply.",
			feedbackCommitment:
				"Your feedback is very important to us. We review suggestions and complaints and consider them when improving RuralOps.",
			continue: "Let's get started",
		},
		homeTour: {
			progress: "{{current}} of {{total}}",
			skip: "Skip tour",
			next: "Next",
			done: "Start exploring",
			steps: {
				farmSelector: {
					title: "Farm selector",
					body: "Use this selector to choose which farm you are working with. Pick one farm to filter dashboards and records, or select All Farms for an organization-wide view.",
				},
				paddocks: {
					title: "Paddocks",
					body: "Plan and map paddocks, record forage measurements, and follow grazing rotations. Use this area to keep pasture conditions and field work organized over time.",
				},
				livestock: {
					title: "Livestock",
					body: "Manage animals and herds, including weights, health events, movements, and sales. Each record builds a reliable history for monitoring performance and making day-to-day decisions.",
				},
				planning: {
					title: "Planning",
					body: "Turn farm objectives into practical tasks, set due dates, and assign work to the team. Follow progress here so upcoming and overdue jobs stay visible.",
				},
				financial: {
					title: "Financial",
					body: "Record income and expenses, manage bills, receivables, payroll, and categories, and review farm results. Supported views follow the farm selected above.",
				},
				settings: {
					title: "Settings",
					body: "Configure farms, manage team members, adjust livestock preferences, and connect integrations. This is also where you maintain organization details and access rules.",
				},
				help: {
					title: "Help",
					body: "Open Help for step-by-step guides about RuralOps features. Use Support to ask a question, report a problem, or send a suggestion and follow the reply.",
				},
			},
		},
		recordStep: {
			title: "What do you want to do first?",
			body: "Pick one option. You can do something else later.",
			// Someone who cannot create records never reads a question they have
			// no answer to: the step becomes reading, and the guide still finishes
			// when a colleague notes something (§7.3, phase 6).
			readOnly: {
				title: "See your farm data",
				body: "When someone on the team notes something, it shows up here.",
			},
			actions: {
				LIVESTOCK: {
					label: "Add an animal",
					help: "Start with one animal from the farm.",
				},
				FINANCE: {
					label: "Note money in or out",
					help: "Store an amount that came in or went out.",
				},
				PLANNING: {
					label: "Create a task",
					help: "Write down a job that needs doing.",
				},
				PASTURE: {
					label: "Add a paddock",
					help: "Start organising the pasture area.",
				},
				EQUIPMENT: {
					label: "Add a machine",
					help: "Add one machine from the farm.",
				},
				import: "I already have a list ready",
				readOnly: {
					body: "You cannot create records on this farm yet.",
					action: "See the farm data",
				},
			},
			// Someone who has no list to import still has a first record to
			// make: this hands step 3 back to the other options, it never leaves
			// the guide.
			importPrompt: {
				title: "Do you already have an animal list?",
				body: "You can import an .xlsx or .csv spreadsheet, a PDF, or a photo (JPEG, PNG or WebP). You will review everything before it is saved.",
				importNow: "Import a list now",
				notNow: "Not now",
			},
			importSkipLater: "You can import later from Livestock › Import animals.",
			farmPicker: {
				title: "Which farm?",
				body: "Pick the farm this record belongs to.",
				cancel: "Go back",
			},
		},
		minimized: {
			title: "Setup paused",
			reopen: "Return to setup",
			dismiss: "Hide guide",
		},
		recap: {
			title: "Setup steps finished",
			body: "You have added the farm and saved the first record.",
			hide: "Hide guide",
		},
		success: {
			title: "First record saved",
			body: "RuralOps now holds real data from your farm.",
			close: "Close guide",
			notConfirmedYet:
				"We are still confirming the record. Try again in a moment.",
			closeFailed: "We could not close it now. Try again.",
			proof: {
				LIVESTOCK: "The animals already show up in the herd list.",
				FINANCE: "The money in or out already shows up in finance.",
				PLANNING: "The task already shows up in planning.",
				PASTURE: "The paddock already shows up in the pasture area.",
				EQUIPMENT: "The machine already shows up in the fleet.",
			},
			view: {
				LIVESTOCK: "See animals",
				FINANCE: "See finance",
				PLANNING: "See planning",
				PASTURE: "See pastures",
				EQUIPMENT: "See machines",
			},
		},
	},
	errors: {
		generic_Title: "An unexpected error occurred.",
		generic_Message: "An unexpected error occurred. Please try again later.",
		no_subscription_title: "No active subscription found",
		no_subscription_message:
			"You need an active subscription to access the app. Please renew your subscription or contact support.",
		subscription_inactive_title: "Subscription is not active",
		subscription_inactive_description:
			"Your subscription is not active. Please renew your subscription or contact support.",
		notFound_Title: "Page not found",
		notFound_Message: "Sorry, the page you're looking for was not found.",
		forbidden_Title: "Access denied",
		forbidden_Message: "You don't have permission to access this page.",
		serverError_Title: "Server error",
		serverError_Message:
			"An error occurred on the server. Please try again later.",
		networkError_Title: "Network error",
		networkError_Message:
			"A network error occurred. Please check your connection and try again.",
		api: {
			UNAUTHORIZED: "You are not authorized. Please log in again.",
			VALIDATION_ERROR: "The data you submitted is invalid.",
			PARSE_ERROR: "Could not read the request. Please try again.",
			PERMISSION_DENIED: "You don't have permission for this action.",
			LIMIT_EXCEEDED:
				"You've reached the limit for your plan. Consider upgrading.",
			LLM_QUOTA_BLOCKED:
				"You've reached your AI analysis quota. Upload without analysis, or upgrade your plan.",
			NOT_FOUND: "The requested resource was not found.",
			CONFLICT: "This action conflicts with existing data.",
			DUPLICATE: "This record already exists.",
			INACTIVE_HERD:
				"This lot is inactive and takes no new animals or records. Pick an active lot or leave the lot empty.",
			MARKETPLACE_ANIMAL_ALREADY_LISTED:
				"One or more of these animals are already in another active offer. Cancel that offer or remove the animals, then try again.",
			INTERNAL_ERROR: "Something went wrong. Please try again later.",
			NO_SUBSCRIPTION:
				"You need an active subscription to perform this action.",
			SUBSCRIPTION_INACTIVE:
				"Your subscription is not active. Please renew it.",
			TRIAL_EXPIRED: "Your trial has expired. Please upgrade your plan.",
			PLAN_TIER_INSUFFICIENT:
				"Your current plan does not support this feature. Consider upgrading.",
			FEATURE_NOT_ENABLED: "This feature is not enabled for your plan.",
			// Assistant-proposal codes: reached from the in-form `root.server` slot
			// (an item edit) as well as from the card's own conflict banner, so both
			// surfaces say the same thing about the same code.
			PROPOSAL_EXPIRED:
				"This proposal has expired and can no longer be changed.",
			PROPOSAL_INVALID_PAYLOAD:
				"These values don't match what this kind of change accepts.",
			PROPOSAL_EXECUTION_FAILED:
				"One change couldn't be applied, so nothing was written.",
			PROPOSAL_TARGET_CHANGED:
				"The record this change edits was modified by someone else.",
			PROPOSAL_ACTION_DISABLED:
				"This kind of change is currently switched off.",
			PROPOSAL_GROUP_INDIVISIBLE:
				"These changes are applied together — drop the whole group instead.",
			PROPOSAL_LAST_ITEM:
				"This is the only change left. Reject the card instead of dropping it.",
			PROPOSAL_LIMIT_EXCEEDED:
				"This card would take you past your plan's limit.",
			// BYOK save: the body was fine, the provider refused the key. Says who
			// refused and what fixes it — never echoes provider text or the key.
			PROVIDER_REJECTED_KEY:
				"The provider rejected this API key. Check that it is current, has not been revoked, and has the permissions this organization needs.",
		},
		offlineReplay: {
			networkError: "Still offline — will sync when connected.",
			versionMismatch:
				"This change is out of date and can't be synced. Discard it and try again.",
			unknownKey: "This change can't be synced. Discard it and try again.",
		},
	},
	pagination: {
		itemsPerPage: "Items per page",
		pageOfTotal: "Page {{page}} of {{total}}",
		noItems: "No items",
		showingItems: "Showing {{start}} to {{end}} of {{total}} items",
	},
	settings: {
		aiKeys: {
			tabLabel: "AI Keys",
			title: "AI Provider Keys",
			description:
				"Connect your own provider API key (BYOK). Usage on your key is billed directly by the provider and isn't counted against your plan's AI budget. Keys are encrypted at rest and never shown again — only the last 4 digits.",
			providers: {
				openai: "OpenAI",
			},
			keyOnFile: "Key on file",
			noKey: "No key set — using the platform key.",
			// The key is stored but the provider rejected it at runtime, so billing
			// silently fell back to the platform key. Without this the card shows a
			// green "key on file" and the owner never learns their key stopped being
			// used.
			keyRejected: "Key rejected by the provider",
			keyRejectedBody:
				"The provider refused this key while serving a request, so it is no longer being used and AI usage is being billed against your plan's budget.",
			keyRejectedSince: "Rejected on {{date}}",
			remediation: {
				RESAVE_KEY:
					"Paste a valid key below and save it — that restores it immediately.",
			},
			addLabel: "API key",
			replaceLabel: "Replace API key",
			test: "Test",
			testing: "Testing key…",
			testValid: "API key is valid.",
			testInvalid: "API key is invalid or unreachable.",
			saveSuccess: "API key saved.",
			deleteSuccess: "API key removed.",
			securityNote:
				"Your key is encrypted and stored securely. It is never logged or displayed again.",
		},
	},
	forms: {
		validation: {
			required: "This field is required",
			invalidNumber: "Enter a valid number",
			wholeNumber: "Enter a whole number",
			invalidEmail: "Enter a valid email address",
			exceedsOutstanding: "Amount exceeds the outstanding balance",
			mustBePositive: "Enter a value greater than 0",
			mustBeNonNegative: "Enter a value of 0 or more",
			percentRange: "Enter a value between 0 and 100",
			dayOfMonthRange: "Enter a day between 1 and 31",
			daysWorkedRange: "Enter between 0 and 31 days",
			invalidDate: "Enter a valid date",
			dateInFuture: "The date cannot be in the future",
			minItems: "Add at least the required number of entries",
			maxItems: "Too many entries",
			serverError: "Could not save. Please try again.",
		},
		draft: {
			restoreTitle: "Draft found",
			restoreDescription:
				"You have unsaved changes for this form. Restore them?",
			restore: "Restore",
			discard: "Discard",
		},
		confirmDestructive: {
			typeToConfirm: "Type {{name}} to confirm",
		},
	},
	common: {
		brStates: {
			AC: "Acre",
			AL: "Alagoas",
			AP: "Amapá",
			AM: "Amazonas",
			BA: "Bahia",
			CE: "Ceará",
			DF: "Distrito Federal",
			ES: "Espírito Santo",
			GO: "Goiás",
			MA: "Maranhão",
			MT: "Mato Grosso",
			MS: "Mato Grosso do Sul",
			MG: "Minas Gerais",
			PA: "Pará",
			PB: "Paraíba",
			PR: "Paraná",
			PE: "Pernambuco",
			PI: "Piauí",
			RJ: "Rio de Janeiro",
			RN: "Rio Grande do Norte",
			RS: "Rio Grande do Sul",
			RO: "Rondônia",
			RR: "Roraima",
			SC: "Santa Catarina",
			SP: "São Paulo",
			SE: "Sergipe",
			TO: "Tocantins",
		},
		appName: "RuralOps",
		loading: "Loading...",
		notApplicable: "N/A",
		noData: "No data available",
		error: "An error occurred",
		save: "Save",
		status: "Status",
		createdAt: "Created At",
		updatedAt: "Updated At",
		saving: "Saving...",
		saveSuccess: "Saved successfully!",
		saveChanges: "Save Changes",
		cancel: "Cancel",
		delete: "Delete",
		confirmDeleteWord: "delete",
		deleteSuccess: "Deleted successfully!",
		deleteError: "Failed to delete. Please try again.",
		home: "Home",
		about: "About",
		sectionInfo: "About this section",
		pricing: "Pricing",
		accessApp: "Access App",
		settings: "Settings",
		theme: "Theme",
		modules: "Modules",
		account: "Account",
		manageAccount: "Manage account",
		preferences: "Preferences",
		confirmDelete: "Confirm Delete",
		deleteConfirmDescription:
			"Are you sure you want to delete this item? This action cannot be undone.",
		edit: "Edit",
		expand: "Expand",
		collapse: "Collapse",
		editObjective: "Edit Objective",
		yearCannotBeChanged: "Year cannot be changed after creation",
		dashboard: "Dashboard",
		establishments: "Farms",
		organizationId: "Organization ID",
		copyId: "Copy ID",
		idCopied: "ID copied",
		idCopyFailed: "Could not copy ID",
		staff: "Staff",
		inviteNew: "Invite New",
		role: "Role",
		calendarView: "Calendar View",
		listView: "List View",
		kanbanView: "Kanban View",
		draft: "Draft",
		approved: "Approved",
		in_progress: "In Progress",
		completed: "Completed",
		scheduled: "Scheduled",
		cancelled: "Cancelled",
		member: "Member",
		active: "Active",
		inactive: "Inactive",
		pending: "Pending",
		joinedAt: "Joined At",
		create: "Create",
		sendInvite: "Send Invite",
		switch: "Switch",
		switchOrganization: "Switch Organization",
		manageSubscription: "Manage Subscription",
		contactSupport: "Contact Support",
		startDate: "Start Date",
		endDate: "End Date",
		currentPlan: "Current Plan",
		currentPeriod: "Current Period",
		renewsOn: "Renews On",
		daysRemaining: "Days Remaining",
		endsOn: "Ends On",
		willCancelOn: "Will cancel on {{date}}",
		days: "days",
		unlimited: "Unlimited",
		used: "Used",
		availablePlans: "Available Plans",
		permissions: "Permissions",
		organizationRole: "Organization Role",
		organizationRoleDescription: "The role you have within the organization",
		months: "months",
		year: "Year",
		years: "years",
		viewBoard: "Board",
		viewList: "List",
		viewCalendar: "Calendar",
		viewObjectives: "Objectives",
		noItems: "No items",
		noTasksThisMonth: "No tasks this month",
		monthly: "Monthly",
		yearly: "Yearly",
		yearlyDiscount: "{{percent}}% off",
		renewSubscription_title: "Subscription Management",
		general: "General",
		subscription: "Subscription",
		choosePlanDescription:
			"Choose a plan to unlock all features and start managing your farm.",
		enterprise: "Enterprise",
		professional: "Professional",
		starter: "Starter",
		free: "Free",
		partner_basic: "Partner Basic",
		createSuccess: "Created successfully!",
		createError: "Failed to create. Please try again.",
		creating: "Creating...",
		update: "Update",
		updateSuccess: "Updated successfully!",
		updateError: "Failed to update. Please try again.",
		updating: "Updating...",
		editTask: "Edit Task",
		priority: "Priority",
		adding: "Adding...",
		assigning: "Assigning...",
		search: "Search",
		clear: "Clear",
		searchPageOnly:
			"Search filters the loaded page only — use pagination to find more.",
		noResults: "No results found",
		defaultLanguage: "Default Language",
		emailLanguageNote:
			"The language selected here will be used for email communications.",
		language: "Language",
		minutes: "minutes",
		month: "month",
		actions: "Actions",
		open: "Open",
		closed: "Closed",
		close: "Close",
		on: "ON",
		off: "OFF",
		view: "View",
		plan: "Plan",
		previous: "Previous",
		next: "Next",
		showing: "Showing",
		of: "of",
		entries: "entries",
		page: "Page",
		noSearchResults: "No search results found",
		deleting: "Deleting...",
		back: "Back",
		pullToRefresh: "Pull to refresh",
		releaseToRefresh: "Release to refresh",
		refreshing: "Refreshing...",
		typeToConfirm: "Type '{{word}}' to confirm:",
		typeWord: "Type '{{word}}'",
		filter: "Filter",
		filters: "Filters",
		apply: "Apply",
		reset: "Reset",
		signIn: "Sign In",
		signOut: "Sign Out",
		signUp: "Sign Up",
		dontHaveAccount: "Don't have an account?",
		alreadyHaveAccount: "Already have an account?",
		total: "Total",
		signInRequired: "You must be signed in to view this page",
		signInRequiredDescription: "Please sign in to continue",
		learnMore: "Learn More",
		comingSoon: "Coming soon",
		viewAll: "View All",
		underDevelopment: "Under Development",
		underDevelopmentDescription:
			"This feature is currently being built. Please check back soon!",
		contact: "Contact",
		date: "Date",
		notes: "Notes",
		noDescription: "No description",
		name: "Name",
		firstName: "First Name",
		lastName: "Last Name",
		description: "Description",
		address: "Address",
		phone: "Phone",
		email: "Email",
		success: "Success",
		removing: "Removing...",
		sending: "Sending...",
		leaving: "Leaving...",
		leaveSuccess: "Left successfully!",
		leaveError: "Failed to leave. Please try again.",
		revoking: "Revoking...",
		pleaseSpecify: "Please specify...",
		profileInformation: "Profile Information",
		accountInformation: "Account Information",
		viewYourAccountDetails: "View your account details",
		updateYourPersonalInformation: "Update your personal information",
		userId: "User ID",
		memberSince: "Member Since",
		lastSignIn: "Last Sign In",
		never: "Never",
		none: "None",
		select: "Select",
		required: "Required",
		optional: "Optional",
		show: "Show",
		add: "Add",
		hide: "Hide",
		yes: "Yes",
		no: "No",
		continue: "Continue",
		retry: "Retry",
		loadFailed: "Couldn't load",
		loadFailedDescription: "Check your connection and try again.",
		refresh: "Refresh",
		refreshFailedShowingSaved: "Couldn't refresh — showing saved data",
		tabNavigation: "Tab navigation",
		goHome: "Go Home",
		emailCannotBeChanged: "Email cannot be changed",
		all: "All",
		dismiss: "Dismiss",
		selectAll: "Select All",
		selectedCount: "{{count}} selected",
		deselectAll: "Deselect All",
		sortByName: "Sort by Name",
		sortByPrice: "Sort by Price",
		sortBySessions: "Sort by Sessions",
		showingResults: "Showing {{count}} of {{total}} results",
		showMore: "Show More",
		showLess: "Show Less",
		// Shared entity-list kit (components/list): table, phone list, toolbar,
		// sort sheet and bulk-action bar.
		list: {
			selectAll: "Select all",
			select: "Select",
			cancelSelect: "Cancel selection",
			clearSelection: "Clear selection",
			selectedCount_one: "{{count}} selected",
			selectedCount_other: "{{count}} selected",
			sortBy: "Sort by {{column}}",
			sortAction: "Sort",
			sortSheetTitle: "Sort by",
			sortSheetDescription:
				"Choose the column that orders the list. Tapping the active option flips the direction.",
			ascending: "ascending",
			descending: "descending",
			showingRange: "Showing {{from}}–{{to}} of {{total}}",
			showMore: "Show {{count}} more",
			showAll: "Show all {{count}}",
			densityComfortable: "Comfortable rows",
			densityCompact: "Compact rows",
			clearSearch: "Clear search",
		},
		remaining: "remaining",
		remove: "Remove",
		createSchedule: "Create Schedule",
		selectSchedules: "Select Schedules",
		selectSchedulesEnrollmentDescription: "Select one or more schedules",
		existingSchedules: "Existing Schedules",
		noSchedules: "No schedules found",
		noSchedulesDescription: "No schedules found. Create a new one?",
		establishment: "Establishment",
		planning: "Planning",
		grassProduction: "Grass Production",
		feasibilityAnalysis: "Feasibility Analysis",
		selectEstablishment: "Select Establishment",
		group: "Group",
		current: "Current",
		individual: "Individual",
		time: "Schedule",
		enrollments: "Enrollments",
		schedule: "Schedule",
		schedules: "Schedules",
		weeklySessionsLimit: "Weekly Sessions Limit",
		price: "Price",
		monthlyAverage: "Monthly Average",
		billingCycle: "Billing Cycle",
		features: "Features",
		type: "Type",
		period: "Period",
		to: "to",
		monday: "Monday",
		tuesday: "Tuesday",
		wednesday: "Wednesday",
		thursday: "Thursday",
		friday: "Friday",
		saturday: "Saturday",
		sunday: "Sunday",
		monday_short: "Mon",
		tuesday_short: "Tue",
		wednesday_short: "Wed",
		thursday_short: "Thu",
		friday_short: "Fri",
		saturday_short: "Sat",
		sunday_short: "Sun",
		mon_short_1: "Jan",
		mon_short_2: "Feb",
		mon_short_3: "Mar",
		mon_short_4: "Apr",
		mon_short_5: "May",
		mon_short_6: "Jun",
		mon_short_7: "Jul",
		mon_short_8: "Aug",
		mon_short_9: "Sep",
		mon_short_10: "Oct",
		mon_short_11: "Nov",
		mon_short_12: "Dec",
		createNew: "Create New",
		basicInfo: "Basic Information",
		location: "Location",
		descriptionPlaceholder: "Enter description...",
		addressPlaceholder: "Enter address...",
		phonePlaceholder: "Enter phone number...",
		emailPlaceholder: "Enter email address...",
		namePlaceholder: "Enter name...",
		overview: "Overview",
		quickActions: "Quick Actions",
		items: "items",
		more: "more",
		today: "Today",
		yesterday: "Yesterday",
		last7Days: "Last 7 days",
		older: "Older",
		// Planning section translations
		planningDescription: "Manage your objectives, tactical plans, and tasks",
		objectives: "Objectives",
		objectivesDescription: "Manage your annual and tactical objectives",
		objectivesEmpty: "No objectives yet",
		objectivesEmptyDescription:
			"Create your first objective to start planning your activities.",
		createFirstObjective: "Create First Objective",
		createObjective: "Create Objective",
		createObjectiveDescription: "Define annual and tactical goals",
		tasks: "Tasks",
		tasksEmpty: "No tasks found",
		tasksDescription: "View and manage your tasks across all views",
		createTask: "Create Task",
		createTaskDescription: "Manage and track task progress",
		objective: "Objective",
		selectObjective: "Select Objective",
		title: "Title",
		scheduledDate: "Scheduled Date",
		activeObjectives: "Active Objectives",
		pendingTasks: "Pending Tasks",
		completedThisMonth: "Completed This Month",
		calendarDescription: "View tasks scheduled on the calendar",
		selectEstablishmentPrompt: "Select an establishment to view tasks",
		allEstablishments: "All Establishments",
		allFarms: "All Farms",
		allFarmsCount: "All Farms ({{count}})",
		farmsCount: "{{count}} farms",
		viewFarmDashboard: "View full dashboard",
	},
	navigation: {
		groups: {
			myFarm: "My Farm",
			management: "Management",
			ai: "AI",
		},
		settings: "Settings",
		help: "Help",
		more: "More",
		synced: "Synced",
		capture: "Capture",
		captureDescription: "What would you like to add?",
		captureFinancial: "Financial",
		captureManualEntry: "Entry",
		captureScanReceipt: "Scan receipt",
		capturePlanning: "Planning",
		captureLivestock: "Livestock",
		captureLivestockRecords: "Livestock Records",
		captureAllRecordTypes: "All types…",
		captureEquipmentMedia: "Equipment & Media",
		registerAnimal: "Register Animal",
		createHerd: "Create Herd",
		addEquipment: "Add Equipment",
		takePhoto: "Take a Photo",
		photoQuestion: "What is this photo of?",
		photoAnimal: "Animal",
		photoPaddock: "Paddock",
		photoEquipment: "Equipment",
		selectFarm: "Select a farm",
		selectFarmDescription: "Choose which farm to add to",
		selectRecordTarget: "Select herd or animal",
		selectRecordTargetHerd: "Select herd",
		selectRecordTargetDescription: "Choose who this record is for",
		searchRecordTarget: "Search herds and animals…",
		searchRecordTargetHerd: "Search herds…",
		recordTargetEmpty: "No matches found",
		home: "Home",
		plan: "Plan",
		livestock: "Livestock",
	},
	dashboard: {
		summaryPill: {
			farms_one: "farm",
			farms_other: "farms",
			head_one: "head",
			head_other: "head",
			ha: "ha",
		},
		paddocks: {
			title: "Paddocks",
			helpLabel: "Understand paddock indicators",
			help: "Head: active animals in the herds currently in the paddock. The number beside each herd is its head count.\nArea (ha): the paddock's registered size in hectares.\nAU (Animal Unit, UA in Portuguese): equals 450 kg of live weight and lets you compare stocking rates for animals of different weights. A 450 kg animal represents 1 AU; a 225 kg animal represents 0.5 AU.\nStocking rate (AU/ha): total available or estimated animal weights divided by 450 kg and by paddock area. For example, 10 AU on 5 ha = 2 AU/ha.",
			count_one: "{{count}} paddock",
			count_other: "{{count}} paddocks",
			onGrass: "{{onGrass}} of {{total}} head on the grass",
			couldNotLoad:
				"Paddocks could not be loaded right now. The rest of the page is up to date.",
			viewAll: "View all paddocks",
			headEyebrow: "head",
			outsideAnyPaddock: "Outside any paddock",
			assignThem: "Assign them",
			legendInHerd: "In a herd · {{count}}",
			showing: "Showing {{shown}} of {{total}}",
			daysOnPasture_one: "{{count}} day",
			daysOnPasture_other: "{{count}} days",
			emptyTitle: "No paddocks yet",
			emptyBody: "Register a paddock with its size, then put a herd on it.",
			emptyAction: "Register a paddock",
		},
		attention: {
			title: "What needs you today",
			sortedBy: "Sorted by how long it has been waiting",
			tileAria: "{{count}} {{label}}, view list",
			seeEveryItem: "See every item ({{count}})",
			allClear:
				"Nothing is waiting on you. New alerts show up here as records come in.",
			farmsAffected_one: "{{count}} farm",
			farmsAffected_other: "{{count}} farms",
			tiles: {
				tasks: {
					label: "Tasks overdue",
					oldest: "Oldest since {{date}}",
					empty: "Tasks past their date",
				},
				livestock: {
					label: "Livestock alerts",
					empty: "Rotation, weighing, calving",
					parts: {
						PADDOCK_OVERSTAY_one: "{{count}} rotation",
						PADDOCK_OVERSTAY_other: "{{count}} rotations",
						WEIGHING_OVERDUE_one: "{{count}} weighing",
						WEIGHING_OVERDUE_other: "{{count}} weighings",
						OVER_CAPACITY_one: "{{count}} over capacity",
						OVER_CAPACITY_other: "{{count}} over capacity",
						CALVING_WINDOW_one: "{{count}} calving",
						CALVING_WINDOW_other: "{{count}} calvings",
						VACCINATION_DUE_one: "{{count}} vaccination",
						VACCINATION_DUE_other: "{{count}} vaccinations",
					},
				},
				review: {
					label: "Records to review",
					caption: "Posted automatically, not yet checked",
				},
				proposals: {
					label: "AI proposals",
					caption: "Waiting for your decision",
				},
			},
			groups: {
				tasks: {
					title_one: "{{count}} task is overdue",
					title_other: "{{count}} tasks are overdue",
					subtitle: "{{title}} — due {{date}} · {{farm}}",
					badge: "Oldest {{date}}",
				},
				PADDOCK_OVERSTAY: {
					title_one: "{{count}} herd is past its rotation date",
					title_other: "{{count}} herds are past their rotation date",
				},
				WEIGHING_OVERDUE: {
					title_one: "{{count}} herd is due for weighing",
					title_other: "{{count}} herds are due for weighing",
					coverage:
						"Only {{numerator}} of {{denominator}} head weighed in the last 90 days — weight gain figures are incomplete",
					badge: "Affects ADG",
				},
				OVER_CAPACITY: {
					title_one: "{{count}} herd is over capacity",
					title_other: "{{count}} herds are over capacity",
				},
				CALVING_WINDOW: {
					title_one: "{{count}} calving expected",
					title_other: "{{count}} calvings expected",
					badge: "Next 30 days",
				},
				VACCINATION_DUE: {
					title_one: "{{count}} vaccination is due",
					title_other: "{{count}} vaccinations are due",
				},
				unassigned: {
					title_one: "{{count}} head is not on any paddock",
					title_other: "{{count}} head are not on any paddock",
					subtitle:
						"{{share}}% of the herd. Stocking and production figures above leave them out.",
					badge: "Missing data",
				},
			},
		},
		title: "Manage your organization from one place",
		welcome: "Welcome to {{organization}}",
		quickActions_establishments: "Farms",
		quickActions_establishments_description:
			"Manage your organization's farms and locations",
		quickActions_staff: "Staff",
		quickActions_staff_description:
			"Manage your organization's team members and their roles",
		quickActions_planning: "Planning & Tasks",
		quickActions_planning_description:
			"Plan objectives and track the work that gets them done",
		quickActions_assistant: "AI Assistant",
		quickActions_assistant_description:
			"Ask questions and get help running your operation",
		greeting: {
			morning: "Good morning, {{name}}",
			afternoon: "Good afternoon, {{name}}",
			evening: "Good evening, {{name}}",
			morning_noName: "Good morning",
			afternoon_noName: "Good afternoon",
			evening_noName: "Good evening",
		},
		subtitle: "Here's what needs your attention today",
		updated: "Updated {{when}}",
		scope: {
			allFarms: "All farms",
			viewing: "Viewing {{farm}}",
		},
		capture: {
			record: "New record",
		},
		sync: {
			syncing: "Syncing...",
			queued_one: "{{count}} record to send",
			queued_other: "{{count}} records to send",
			offline: "No connection",
			offlineQueued_one: "No connection · {{count}} queued",
			offlineQueued_other: "No connection · {{count}} queued",
		},
		activity: {
			title: "Recorded on the farm",
			empty: "Nothing recorded in the last 7 days.",
			entry: "{{verb}}: {{subject}}",
			systemActor: "System",
			unknownActor: "Unidentified author",
			verbs: {
				created: "New record",
				updated: "Update",
				deleted: "Removal",
				removed: "Removal",
				completed: "Completed",
				approved: "Approved",
				rejected: "Rejected",
				invited: "Invitation",
				exported: "Export",
				toggled: "Adjustment",
				executed: "Run",
				changed: "Change",
			},
			subjects: {
				livestock_animal: "Animal",
				livestock_measurement: "Herd measurement",
				livestock_operation: "Herd operation",
				grass_measurement: "Pasture measurement",
				grass_operation: "Pasture operation",
				grass_paddock: "Paddock",
				equipment: "Equipment",
				equipment_part: "Part",
				planning_task: "Task",
				soil_analysis: "Soil analysis",
				assistant_proposal: "Assistant proposal",
				assistant_proposal_action: "Assistant proposal",
				staff: "Staff",
				staff_member: "Team member",
				staff_role: "Access role",
				staff_invitation: "Staff invitation",
				vendor: "Vendor",
				payroll_run: "Payroll run",
				payroll_worker: "Worker",
				user_profile: "Profile",
				financialhub_autopost: "Automatic posting",
				animals: "Animal",
				herds: "Herd",
				measurements: "Measurement",
				operations: "Operation",
				weighing: "Weighing",
				paddocks: "Paddock",
				tasks: "Task",
				objectives: "Objective",
				parts: "Part",
				establishments: "Farm",
				sales: "Sale",
			},
		},
		herdSnapshot: {
			title: "Herd",
			head: "Head",
			herds: "Herds",
			records30d: "Records (30 d)",
			viewAll: "Go to livestock",
		},
		livestockKpi: {
			title: "How the herd is doing",
			window12m: "last 12 months",
			windowAllFarms: "Last 12 months · all farms",
			windowFarm: "Last 12 months · {{farm}}",
			definition: {
				gmd90: "average daily gain, last 90 days",
				stocking: "animal units per hectare",
				production: "kilos of beef per hectare per day",
				pregnancy: "cows confirmed pregnant",
				weaning: "calves weaned per cow",
				mortality: "deaths in the herd",
			},
			footnote: {
				headWeighed: "{{numerator}} of {{denominator}} head weighed",
				cowsChecked: "{{numerator}} of {{denominator}} cows checked",
				deathsOfHead: "{{deaths}} of {{total}} head",
			},
			tiles: {
				gmd90: "ADG 90d",
				stocking: "Stocking",
				production: "Production",
				pregnancy: "Pregnancy",
				weaning: "Weaning",
				mortality: "Mortality",
			},
			empty: {
				gmd90: "no recent weighing",
				stocking: "register paddock area",
				production: "no weighing or pasture area",
				pregnancy: "no diagnosis in the period",
				weaning: "no weaning in the period",
				mortality: "no herd in the period",
			},
			help: {
				aboutSection: "About herd indicators",
				aboutTile: "About {{label}}",
				section:
					"Six indicators that summarise how the herd is performing, computed from the records you keep here (weighings, paddock areas, pregnancy diagnoses, weanings and deaths).\n\nNumbers like 12/40 show how many animals or paddocks had enough data. A ⚑ flag means the coverage is thin, so read that value with care.",
				gmd90:
					"Average daily gain over the last 90 days: how many kilograms each animal put on per day, based on its weighings in that window.\n\nAnimals need at least two weighings in the period to count.",
				stocking:
					"Stocking rate: how many animal units (AU) graze each hectare of pasture. One AU is 450 kg of live weight.\n\nWhen weights are missing, the head-per-hectare figure is shown instead.",
				production:
					"Pasture productivity: kilograms of live weight produced per hectare per day. It multiplies the herd's average daily gain by its stocking rate in head per hectare.",
				pregnancy:
					"Pregnancy rate: share of breeding females diagnosed pregnant among those with a pregnancy diagnosis in the last 12 months.",
				weaning:
					"Weaning rate: calves weaned per 100 breeding females in the last 12 months, with the total weaned weight and the weight weaned per hectare of pasture.",
				mortality:
					"Mortality: deaths in the last 12 months as a share of the animals present in the period (present head plus deaths).",
			},
			starterNote:
				"These indicators fill in as you record weighings, paddock areas, pregnancy diagnoses, weanings and deaths.",
			couldNotLoad: "could not load",
			thin: "thin coverage",
			unassigned: "Unassigned",
			headWithoutPaddock: "{{count}} head not on any paddock",
			seeHerds: "See herds",
			seePaddocks: "See paddocks",
			basis: {
				rolling12m: "last 12 months",
				presentPlusDeaths: "present + deaths",
				farmGrazingArea: "farm grazing area",
			},
			staleWeight: "Oldest weighing is {{days}} days old",
			units: {
				kgPerDay: "kg/day",
				auPerHa: "AU/ha",
				headPerHa: "head/ha",
				ha: "ha",
				kgPerHaPerDay: "kg/ha/day",
				percent: "%",
			},
			coverage: "{{numerator}}/{{denominator}}",
			head: "{{count}} hd",
			weanedKg: "{{value}} kg",
			weanedKgPerHa: "{{value}} kg/ha",
			deathsOf: "{{deaths}} of {{total}}",
			percent: "{{value}} %",
			productionBasis: "basis: ADG×head/ha",
			paddockHerd: "{{name}} · {{days}} d",
			pregnancyLabel: "pregnancy {{value}} %",
		},
		marketPrice: {
			title: "Finished cattle price",
			yourState_one: "Your state",
			yourState_other: "Your states",
			dailyQuotes: "Daily quotes",
			source: "Source:",
			perArroba: "/@",
			up: "Up",
			down: "Down",
			since: "since {{date}}",
			referenceDate: "ref. {{date}}",
			stale: "outdated",
			otherStates_one: "Other states ({{count}})",
			otherStates_other: "Other states ({{count}})",
			gapStateUnknown_one:
				"{{count}} farm with no identified state — check its address in Settings.",
			gapStateUnknown_other:
				"{{count}} farms with no identified state — check their addresses in Settings.",
			noPublicQuote_one: "No public quote for this state ({{count}} farm).",
			noPublicQuote_other: "No public quote for this state ({{count}} farms).",
		},
		askAssistant: {
			title: "Ask the farm",
			label: "Question for the assistant",
			placeholder: "How many head in paddock 4?",
			submit: "Ask",
			hint: "Answered from your own records. The AI proposes; you decide.",
		},
		equipment: {
			title: "Machines",
			nextService: "{{type}} · next service",
			noOpenJobs: "No jobs open",
			dueHours_one: "In {{count}} run hour",
			dueHours_other: "In {{count}} run hours",
			dueNow: "Overdue",
			openTasks_one: "{{count}} open job",
			openTasks_other: "{{count}} open jobs",
			more_one: "+{{count}} maintenance item",
			more_other: "+{{count}} maintenance items",
			viewAll: "Go to maintenance",
		},
		firstWeightNudge: {
			title: "Record the first weight",
			body_one:
				"Your farm already has {{count}} animal. With the first weighing, RuralOps starts tracking weight gain.",
			body_other:
				"Your farm already has {{count}} animals. With the first weighing, RuralOps starts tracking weight gain.",
			farmChip: "Farm: {{name}}",
			action: "Record first weight",
			dismiss: "Not now",
		},
		livestockAlerts: {
			title: "Livestock attention",
			viewAll: "Go to livestock",
		},
		myTasks: {
			title: "Your tasks",
			overdue: "Overdue",
			today: "Due today",
			upcoming: "Upcoming",
			noDueDate: "No due date",
			due: "Due {{date}}",
			empty: "No tasks assigned to you right now.",
			emptyHint: "Tasks assigned to you across your farms will show up here.",
			viewAll: "View all tasks",
			more: "+{{count}} more",
		},
		financialSummary: {
			title: "Financial",
			viewAll: "View all",
			monthResult: "This month's result",
			monthSoFar: "{{month}}, so far",
			projectedBalance: "Projected balance",
			projectedBalanceCaption: "Where the month ends at this pace",
			cashBalanceNudge: "Projected balance is not showing",
			cashBalanceNudgeBody: "Set your opening balance once and this fills in.",
			cashBalanceNudgeCta: "Set it now",
			upcomingObligations: "Bills due in 7 days",
			nothingToPay: "Nothing to pay this week",
			obligationsCount_one: "{{count}} bill to pay",
			obligationsCount_other: "{{count}} bills to pay",
			expectedInflows: "Incoming in 7 days",
			inflowsCount_one: "{{count}} receivable due",
			inflowsCount_other: "{{count}} receivables due",
			toReview: "Records to review",
		},
		yourFarms: {
			title: "Your farms",
			allFarms: "All farms",
			viewAll: "Manage farms",
		},
	},
	staff: {
		title: "Staff Management",
		description: "Manage your organization's team members and their roles",
		searchPlaceholder: "Search by name or email...",
		inviteDescription:
			"Send an invitation to a new team member. They will receive an email with instructions to join your organization.",
		noStaff: "No staff members yet",
		noStaffDescription:
			"Invite team members to join your organization and help manage operations.",
		noStaffFound: "No staff members found",
		noStaffSearchDescription:
			"No staff members match your search criteria. Try a different search term.",
		revoke_title: "Remove Member",
		revoke_description:
			"Are you sure you want to remove {{name}} from your organization?",
		revoke_confirm: "Remove",
		invitation: {
			expires: "Expires",
		},
		toasts: {
			inviting: "Sending invitation...",
			inviteSuccess: "Invitation sent successfully",
			updating: "Saving changes...",
			updateSuccess: "Staff member updated successfully",
			removing: "Removing staff member...",
			removeSuccess: "Staff member removed successfully",
			revoking: "Revoking invitation...",
			revokeSuccess: "Invitation revoked successfully",
		},
		navigation: {
			title: "Staff Member",
			description: "View and manage this team member's details",
			staffData: "Details",
			permissions: "Permissions",
			unknownStaff: "Unknown staff member",
		},
		form: {
			email: "Email",
			joinedAt: "Joined on {{date}}",
			organizationRole: "Organization role",
			organizationRoleDescription:
				"Controls what this member can see and do across the organization.",
			organizationRolesHeader: "Organization roles",
			permission: "Permission",
			permissionsMatrix: "Permissions matrix",
			updateRole: "Update role",
			viewPermissions: "View permissions",
			sections: {
				information: {
					title: "Member information",
					description: "Personal details for this team member.",
					editMode: "Update this member's name and display color.",
					editModeColorOnly: "Update this member's display color.",
					firstName: "First name",
					firstNamePlaceholder: "Enter first name",
					lastName: "Last name",
					lastNamePlaceholder: "Enter last name",
					color: "Display color",
					colorDescription:
						"Used to identify this member across calendars and assignments.",
					cancel: "Cancel",
					saveChanges: "Save changes",
					updateSuccess: "Member information updated successfully",
				},
				organizationRole: {
					title: "Role & permissions",
					description:
						"Set this member's organization role to control their access.",
				},
			},
			roleDescriptions: {
				org_owner: "Full access to everything in the organization.",
				org_administrator:
					"Manages staff, settings, and most organization data.",
				org_manager: "Manages day-to-day operations and farm data.",
				org_medical_professional:
					"Access to medical records and health-related operations.",
				org_accountant: "Access to billing, finance, and reporting.",
				org_viewer: "Read-only access to organization data.",
			},
		},
	},
	establishments: {
		description: "Manage your organization's farms",
		createFarm: "Create farm",
		createTitle: "Add your farm",
		createDescription:
			"We need the name and the municipality. The rest can wait.",
		saveFarm: "Save farm",
		editFarm: "Edit farm",
		updateFarm: "Update farm",
		nameLabel: "Farm name",
		nameQuestion: "What is the farm called?",
		footerHint: "You can fill in the rest later, under Farm.",
		optionalDisclosure: "Add more details now",
		optionalDisclosureHelp:
			"Address, phone and description can be filled in later.",
		citySearch: {
			label: "Farm city",
			placeholder: "Start typing the municipality name",
			required: "Choose the farm's municipality",
			minLength: "Type at least 2 letters to search",
			searching: "Searching municipalities...",
			noResults: "No municipality found with that name",
			error: "Could not search municipalities right now. Please try again.",
			clear: "Remove city",
			resultsLabel: "Municipalities found",
			selected: "Selected city",
		},
		searchPlaceholder: "Search farms...",
		namePlaceholder: "Boa Vista Farm",
		addressPlaceholder: "Retiro road, km 12",
		phonePlaceholder: "(67) 99999-0000",
		emailPlaceholder: "contact@farm.com",
		descriptionPlaceholder: "Cow-calf and backgrounding, 1,200 ha",
		noEstablishments: "No farm yet",
		noEstablishmentsDescription:
			"Create your first farm to start managing locations.",
		confirmDelete: "Delete farm",
		confirmDeleteDescription:
			"Are you sure you want to delete this farm? This action cannot be undone.",
		confirmDeleteWarning:
			"All data associated with this farm will be permanently removed.",
		confirmDeletePhrase: "DELETE",
	},
	platformPlan: {
		checkout: {
			loading: "Creating checkout session...",
			success: "Redirecting to checkout...",
			error: "Failed to create checkout session",
		},
		portal: {
			loading: "Opening billing portal...",
			success: "Redirecting to billing portal...",
			error: "Failed to open billing portal",
		},
		success: {
			syncing: "Activating your subscription...",
			syncingDescription:
				"Please wait while we confirm your payment and activate your subscription.",
			syncError: "Unable to confirm subscription",
			syncErrorDescription:
				"There was an issue confirming your subscription. Please try again or contact support.",
			title: "Congratulations!",
			description:
				"Your subscription is now active. Thank you for subscribing!",
			redirectingIn: "Redirecting in {{seconds}} seconds...",
			cta: "Go to Dashboard",
		},
		update: {
			syncing: "Updating subscription...",
			syncingDescription:
				"Please wait while we sync your subscription changes.",
		},
		subscribeFree: {
			loading: "Activating free plan...",
			success: "Free plan activated successfully!",
			error: "Failed to activate free plan",
		},
		welcomeModal: {
			title: "Welcome to RuralOps!",
			description:
				"Your free plan has been activated. You can now start using all the features of RuralOps.",
			trialTitle: "Your free trial has started!",
			trialDescription:
				"You have 30 days of full access — no credit card required. Add a payment method any time before it ends to keep your subscription active.",
			goToDashboard: "Go to Dashboard",
			redirectingIn: "Redirecting in {{seconds}} seconds...",
		},
		trialBanner: {
			message_one:
				"Your free trial ends in {{count}} day. Add a payment method to keep access.",
			message_other:
				"Your free trial ends in {{count}} days. Add a payment method to keep access.",
			cta: "Add payment method",
		},
		usage: {
			title: "Plan Usage",
			description: "Your current usage against plan limits",
			customLimit: "Custom limit",
			expiresAt: "expires {{date}}",
		},
		entitlements: {
			max_establishments: "Establishments",
			max_staff_members: "Staff Members",
			max_animals: "Animals",
		},
		llmUsage: {
			title: "AI Usage",
			description: "Usage from {{periodStart}} to {{periodEnd}}",
			budgetUsed: "AI Budget Used",
			byokEstimate: "Your own key (estimate at platform rates)",
			fxAsOf: "rate as of {{date}}",
			summaryUnavailable:
				"AI spend summary is temporarily unavailable. Other plan details are unaffected.",
			topup: {
				planBudget: "Plan budget",
				credits: "Top-up credits",
				totalAvailable: "Total available this cycle",
				impact:
					"Top-ups add to your plan budget and raise this cycle's AI limit. They apply immediately and expire at the end of the cycle.",
				yourTopups: "Your top-ups",
				purchasedOn: "Bought {{date}}",
				expiresOn: "expires {{date}}",
				title: "Need more this cycle?",
				description:
					"Buy a one-time top-up. It applies immediately and lasts until your cycle ends.",
				amountLabel: "Amount ({{min}}–{{max}})",
				buyAmount: "Add {{amount}}",
				dunningBlocked:
					"Top-up purchases are paused while your subscription payment is past due.",
			},
			byTaskType: "By Task Type",
			byProvider: "By Provider",
			noUsage: "No AI usage in this period",
			taskTypes: {
				ANAMNESIS_SUMMARY: "Anamnesis Summary",
				SESSION_PLAN_SUGGESTION: "Session Plan Suggestion",
				CUSTOMER_WORKOUT_RECOMMENDATION: "Customer Workout Recommendation",
			},
			providers: {
				openai: "OpenAI",
				anthropic: "Anthropic",
				bedrock: "AWS Bedrock",
			},
		},
		recurringAddons: {
			title: "Recurring add-ons",
			description:
				"Raise your plan limits with monthly add-ons. Charges are prorated and billed in your subscription's currency.",
			perUnit: "{{price}} per +{{step}} / month",
			increase: "Increase",
			decrease: "Decrease",
			types: {
				animals: { name: "+{{step}} animals" },
				farms: { name: "+{{step}} farm" },
			},
			saved: "Add-on updated.",
			prorationCharge:
				"A prorated {{amount}} will be added to your next invoice on {{date}}.",
			prorationCredit:
				"A prorated credit of {{amount}} will be applied to your next invoice on {{date}}.",
		},
		planChange: {
			changed: "Plan updated.",
			noChange: "You're already on this plan.",
			prorationCharge:
				"A prorated {{amount}} will be added to your next invoice on {{date}}.",
			prorationCredit:
				"A prorated credit of {{amount}} will be applied to your next invoice on {{date}}.",
		},
		billingSummary: {
			title: "Billed monthly",
			basePlan: "Base plan",
			total: "Total",
			perMonth: "/ mo",
		},
		invoices: {
			title: "Invoices",
			nextInvoice: "Next invoice",
			nextInvoiceOn: "on {{date}}",
			nextInvoiceNote: "Updates automatically as you add or remove add-ons.",
			history: "Billing history",
			empty: "No invoices yet.",
			viewInvoice: "View",
			status: {
				paid: "Paid",
				open: "Open",
				void: "Void",
				uncollectible: "Uncollectible",
				draft: "Draft",
			},
		},
		partnerRedeem: {
			openCta: "Redeem partner code",
			title: "Redeem partner code",
			inputDescription:
				"Paste the code your partnership administrator gave you.",
			confirmDescription: "Confirm the plan you're about to redeem.",
			codeLabel: "Partner code",
			codePlaceholder: "Paste your code here",
			validating: "Validating…",
			redeeming: "Activating…",
			confirmCta: "Confirm and redeem",
			confirmPartnerLabel: "Partner:",
			confirmPlanLabel: "Plan:",
			confirmExpiresLabel: "Access until:",
			success: "Partner subscription activated.",
			errors: {
				invalid:
					"This code isn't valid. Double-check the code your partnership administrator gave you.",
				paidSub:
					"This organization already has an active paid plan. Cancel your current plan before redeeming a partner code.",
				raceLost:
					"This code is no longer valid — someone may have used the last seat while you were confirming.",
				disabled:
					"Partner redemption is temporarily unavailable. Try again shortly.",
				rateLimited:
					"Too many attempts — please wait a minute before trying again.",
				generic: "Something went wrong. Please try again.",
			},
			activeBanner: {
				title: "You're on the {{planName}} partner plan until {{date}}.",
				description:
					"Want more capacity? Upgrade to a paid plan below at any time.",
			},
		},
	},
	grass: {
		foragePreview: {
			cultivar: "Cultivar on the record date",
			definition:
				"Density represents total above-ground dry matter, including dead material, sampled from ground level, divided by uncompressed pasture height.",
			action: "Forage mass",
			onlineRequired: "An online connection is required for this preview.",
			close: "Close",
			record: "Field record",
			loadedRecords:
				"Choose from the loaded records. This list may not contain the full history.",
			noRecords: "No field records are loaded.",
			preview: "Preview forage mass",
			loading: "Loading…",
			error: "Could not load the preview. Check your connection and try again.",
			confirmConditions:
				"I verified the cultivar in this paddock on the record date, the uncompressed height measurement and all reference conditions.",
			confirmAndPreview: "Confirm conditions and preview",
			recordDate: "Record date",
			recorded: "Recorded forage mass",
			estimated: "Estimated from literature",
			massUnit: "kg DM/ha",
			densityUnit: "kg DM/ha/cm",
			methodUnspecified: "Method unspecified",
			referenceRange: "Supported height range",
			density: "Reference density",
			heightContext:
				"Height must be the uncompressed pasture height. Verify the cultivar and paddock conditions as they were on the record date.",
			limitations:
				"Total standing dry matter per hectare. This is not usable feed or a stocking recommendation. The preview is not saved.",
			sourceNotProvided: "Source not provided",
			conditions: "Conditions",
			managementSource: "Management reference",
			reasons: {
				INVALID_RECORDED_MASS: "The recorded mass is invalid.",
				MISSING_HEIGHT: "No height was recorded.",
				INVALID_HEIGHT: "The recorded height is invalid.",
				NO_REFERENCE: "No density reference is provided.",
				INVALID_REFERENCE: "The density reference is incomplete or invalid.",
				MIXED_PASTURE:
					"Mixed pasture or partial cultivar coverage is unsupported.",
				INCOMPATIBLE_MANAGEMENT:
					"The grazing method differs from the reference.",
				OUTSIDE_HEIGHT_RANGE: "The height is outside the supported range.",
				PLANTING_DATE_UNVERIFIED:
					"The cultivar planting date cannot be verified for this record.",
				CONDITIONS_UNCONFIRMED:
					"Verify and confirm the reference conditions before estimating.",
				REFERENCE_CHANGED:
					"The reference changed. Review and confirm the updated conditions.",
			},
		},
		registries: {
			tab: "Records",
			measurementType: "Field measurement",
			kind: {
				all: "All",
				operation: "Operations",
				measurement: "Measurements",
			},
			groups: {
				pasture: "Grazing",
				harvest: "Harvest",
				fertility: "Fertility",
				water: "Water",
				protection: "Protection",
				renewal: "Renewal",
			},
			register: {
				button: "Record",
				title: "New record",
				search: "Search type…",
				recents: "Recent",
				empty: "No type found",
			},
		},
		nav: {
			title: "Paddocks",
		},
		dashboard: {
			quickActions: {
				addMeasurement: "Add Measurement",
				logOperation: "Log Operation",
			},
		},
		farm: {
			title: "Farm",
			createSuccess: "Farm created successfully",
			createError: "Failed to create farm",
			updateSuccess: "Farm updated successfully",
			updateError: "Failed to update farm",
			selectFarm: "Select a Farm",
			noFarms: "No farms found",
			noFarmsDescription: "Create a farm to start managing grass production",
		},
		paddock: {
			selectPaddock: "Select a paddock",
			list: {
				title: "Paddocks",
				searchPlaceholder: "Search by name, cultivar or use type...",
				moreCultivars: "+{{count}} more",
				selectRow: "Select paddock {{name}}",
				resultLine: "{{shown}} of {{total}} paddocks",
				noResults: "No paddocks match your search",
				noResultsHint: "Try another name, code, cultivar or use type.",
				columns: {
					establishment: "Farm",
				},
				empty: {
					title: "No paddocks yet",
					description:
						"Create your first paddock to start tracking grass production",
				},
			},
			// Paddock-list multi-select bulk bar.
			bulk: {
				recordOperation: "Record operation",
				exportCsv: "Export CSV",
				csvExported: "CSV exported",
				csvAreaHeader: "Area (ha)",
				selectNeedsFarm:
					"Multi-select is only available with a farm selected. Pick a farm in the selector above.",
				capExceeded: "Bulk actions are limited to {{max}} paddocks per run.",
				mixedFarms: "Select paddocks from a single farm to run bulk actions.",
				partialResult:
					"{{success}} done, {{failed}} failed. Review the list and retry.",
				allFailed: "Nothing was processed. Check the errors and try again.",
				notFound: "Paddock not found — it may have been deleted or moved.",
				unknownError: "Unknown error",
				operationTitle_one: "Record operation on 1 paddock",
				operationTitle_other: "Record operation on {{count}} paddocks",
				operationBody:
					"The same operation is recorded on each paddock: {{names}}. Costs are not split across paddocks — add a cost on each paddock if needed.",
				operationCta_one: "Record on 1 paddock",
				operationCta_other: "Record on {{count}} paddocks",
				operationSuccess_one: "Operation recorded on {{count}} paddock",
				operationSuccess_other: "Operation recorded on {{count}} paddocks",
				delete: "Delete",
				deleteTitle_one: "Delete 1 paddock?",
				deleteTitle_other: "Delete {{count}} paddocks?",
				deleteBody:
					"These paddocks leave the paddock lists and the grass reports. This cannot be undone from the app.",
				// The word that arms the delete button — a verb, not a name.
				deleteWord: "Delete",
				deleteConfirm_one: "Delete paddock",
				deleteConfirm_other: "Delete {{count}} paddocks",
				deleteSuccess_one: "{{count}} paddock deleted",
				deleteSuccess_other: "{{count}} paddocks deleted",
			},
			create: {
				title: "Create Paddock",
			},
			edit: {
				title: "Edit Paddock",
			},
			form: {
				identity: "Identity",
				identityHint: "How this paddock is named and referenced",
				landAndUse: "Land & use",
				landAndUseHint:
					"Physical characteristics and how the paddock is grazed",
				areaFromMap: "Measured from boundary",
				areaManual: "Enter manually or draw the boundary",
				boundaryHint: "Tap the map or use GPS to trace the paddock edge",
				summary: "Paddock summary",
				notSet: "Not set",
				requiredHint: "Fields marked * are required",
				fromBoundary: "from boundary",
				enterManually: "enter manually",
				manual: "manual",
				soilTypeSuggestions: "Common types",
				codeAutoSuggested: "auto-suggested",
				moreDetails: "More details",
			},
			soilTypes: {
				clayLoam: "Clay loam",
				sandyLoam: "Sandy loam",
				clayey: "Clayey",
				sandy: "Sandy",
				silty: "Silty",
				loam: "Loam",
			},
			detail: {
				title: "Paddock Details",
				notFound: "Paddock not found",
				tabs: {
					paddocks: "Paddocks",
					measurements: "Measurements",
					operations: "Operations",
					soilAnalysis: "Soil Analysis",
				},
				sections: {
					soil: "Soil & fertility",
				},
				soilSection: {
					summary_one: "{{count}} analysis · latest sampled {{date}}",
					summary_other: "{{count}} analyses · latest sampled {{date}}",
				},
				overview: {
					basicInfo: "Basic Information",
					qualityTargets: "Quality Targets",
					yieldTarget: "Yield Target",
					cpMin: "Min. Crude Protein",
					ndfMax: "Max. NDF",
					utilizationTarget: "Utilization Target",
					yieldTargetHelp:
						"Expected annual dry matter production per hectare (t DM/ha/year).",
					cpMinHelp: "Minimum desired crude protein content in the forage.",
					ndfMaxHelp:
						"Maximum acceptable fibre (NDF) — higher fibre lowers digestibility.",
					utilizationTargetHelp:
						"Share of the available forage the herd actually consumes.",
					noTargets: "No quality targets defined",
					noCultivars: "No cultivars assigned",
					noBoundary: "No boundary defined",
					latestMeasurement: "Latest Measurement",
					noMeasurement: "No measurements recorded",
					biomass: "Biomass",
					height: "Height",
					measuredAt: "Measured",
				},
				measurementsList: {
					empty: "No measurements recorded for this paddock",
					addFirst:
						"Add your first measurement to start tracking grass production",
				},
				operationsList: {
					empty: "No operations recorded for this paddock",
					addFirst: "Log your first operation to track field activities",
				},
			},
			fields: {
				name: "Name",
				code: "Code",
				area: "Area",
				status: "Status",
				useType: "Use Type",
				cultivars: "Cultivars",
				// Short caption for the phone card's metrics line.
				cultivarsShort: "Cultivars",
				soilType: "Soil Type",
				slope: "Slope",
			},
			placeholders: {
				name: "e.g., North Paddock",
				code: "e.g., NP-01",
				soilType: "e.g., Clay loam",
			},
			actions: {
				create: "Create Paddock",
				// One word — the phone eyebrow row cannot fit "Create Paddock".
				createShort: "Paddock",
				record: "Record",
				more: "More actions",
				delete: "Delete paddock",
			},
			useTypes: {
				ROTATIONAL_GRAZING: "Rotational Grazing",
				CONTINUOUS_GRAZING: "Continuous Grazing",
				CUT_AND_CARRY: "Cut and Carry",
				HAY: "Hay Production",
				SILAGE: "Silage Production",
				DEFERRED_STOCKPILING: "Deferred/Stockpiling",
				MIXED: "Mixed Use",
			},
			statuses: {
				ESTABLISHED: "Established",
				RENOVATION: "Under Renovation",
				DEGRADED: "Degraded",
				REST: "Resting",
				DEFERRED: "Deferred",
				UNDER_RECOVERY: "Under Recovery",
			},
			boundary: {
				title: "Paddock Boundary",
				clear: "Clear",
				apply: "Apply",
				coordinatesLabel: "Enter coordinates (latitude, longitude)",
				coordinatesPlaceholder:
					"-23.5505, -46.6333\n-23.5510, -46.6320\n-23.5520, -46.6340",
				coordinatesHelp:
					"Enter one coordinate per line. Format: latitude, longitude (e.g., -23.5505, -46.6333)",
				pointsDetected: "{{count}} points detected",
				mapPreviewEmpty: "Enter coordinates to see preview",
				tapHint: "Tap the map to add boundary points",
				useGps: "Use my location",
				gpsError: "Couldn't get your location",
				gpsUnsupported: "Geolocation isn't supported on this device",
				undo: "Undo point",
				manualEntry: "Enter coordinates manually",
				tools: {
					draw: "Draw",
					gps: "Walk with GPS",
					coordinates: "Coordinates",
				},
				drawHint: "Tap the map to drop boundary points",
				pointsPerimeter: "{{count}} points · {{km}} km perimeter",
				perimeter: "{{km}} km perimeter",
				zoomIn: "Zoom in",
				zoomOut: "Zoom out",
				errors: {
					minPoints: "Minimum 3 points required to create a boundary",
				},
			},
			kpi: {
				latestBiomass: "Last recorded biomass",
				swardHeight: "Sward height",
				sinceLastCut: "Since last cut",
				sinceDate: "since {{date}}",
				measured: "measured {{date}}",
				cutOn: "cut {{date}}",
				noCut: "No cut recorded",
				title: "Pasture now",
				swardOverTime: "Sward height over time",
				chartEmpty: "Record sward height measurements to see the trend",
				daysUnit_one: "day",
				daysUnit_other: "days",
				stocking: "Current stocking",
				// 1 AU = 450 kg live weight.
				stockingUnit: "AU/ha",
				headPerHaUnit: "head/ha",
				stockingDetail: "{{head}} head · {{units}} AU",
				stockingNoWeight: "{{head}} head · no weighing",
				stockingNoHerd: "No herds on the paddock",
			},
			records: {
				title: "Records",
				hint: "The operations and measurements recorded on this paddock will show up here.",
				created: {
					badge: "Created",
					title: "Paddock created",
				},
				filterAll: "All",
				filterHarvest: "Harvest",
				filterFertility: "Fertility",
				showOlder: "Show older",
				empty: "No records yet",
			},
			images: {
				title: "Images",
				dropzone:
					"No images yet — drop field photos here to keep a visual record alongside measurements",
				upload: "Upload",
				empty: "No images yet",
			},
			aside: {
				boundary: "Boundary",
				editOnMap: "Edit on map",
				cultivars: "Cultivars",
				manageCultivars: "Manage",
				soilSummary: "Soil & fertility",
				sampled: "sampled {{date}}",
				openSoil: "View soil & fertility",
				qualityTargets: "Quality targets",
				setTargets: "Set targets",
			},
			grazing: {
				guide: {
					open: "How {{status}} and other pasture statuses are defined",
					title: "How pasture status is defined",
					intro:
						"We compare the latest recorded pasture height with the entry and exit heights of the cultivar with the greatest coverage. Check the measurement date: conditions may have changed since then.",
					measuredAt: "Height measured on",
					thisPaddock: "This paddock",
					definitions: {
						ready:
							"Height at or above entry. The pasture has reached the target for bringing animals in.",
						grazing:
							"Height between exit and entry, with animals present. Grazing can continue until exit height.",
						waiting:
							"Height between exit and entry, with no animals. Wait for growth to entry height.",
						remove:
							"Height at or below exit, with animals present. Move them out to preserve the pasture residual.",
						regrowing:
							"Height at or below exit, with no animals. Wait for regrowth.",
						belowExit:
							"Height at or below exit, but occupancy is unavailable, so no action is inferred.",
						unknown:
							"A recorded height, assigned cultivar, or entry/exit target is missing.",
					},
				},
				ready: "Ready for grazing",
				remove: "Move animals out",
				regrowing: "Wait for regrowth",
				belowExit: "Below exit height",
				grazing: "Grazing",
				waiting: "Wait for entry height",
				unknown: "Height not assessed",
				height: "Pasture {{height}} cm",
				basis:
					"{{cultivar}} ({{coverage}}%) · entry {{entry}} cm / exit {{exit}} cm",
				noCultivar: "Assign a cultivar to compare grazing heights.",
				noHeight: "Record pasture height to assess {{cultivar}}.",
				noTargets: "{{cultivar}} has no entry and exit heights recorded.",
				title: "Current Herds",
				daysOnPaddock_one: "{{count}} day on paddock",
				daysOnPaddock_other: "{{count}} days on paddock",
				plannedDays_one: "{{count}}-day plan",
				plannedDays_other: "{{count}}-day plan",
				since: "since {{date}}",
				rested_one: "Rested {{count}} day",
				rested_other: "Rested {{count}} days",
				neverGrazed: "Never grazed",
				neverGrazedHint:
					"No grazing rotation has been recorded on this paddock yet.",
				history: "Previous herds",
				unknownHerd: "Unknown herd",
				viewAll: "View all ({{count}})",
				viewLess: "View less",
			},
			createSuccess: "Paddock created successfully",
			createError: "Failed to create paddock",
			updateSuccess: "Paddock updated successfully",
			updateError: "Failed to update paddock",
			deleteSuccess: "Paddock deleted successfully",
			deleteError: "Failed to delete paddock",
			deleteConfirm: {
				title: "Delete paddock",
				description:
					'Delete "{{name}}"? It will no longer appear in grass management for this farm. This action cannot be undone.',
			},
			cultivarsAssignSuccess: "Cultivars assigned successfully",
			cultivarsAssignError: "Failed to assign cultivars",
			manageCultivars: {
				title: "Manage Cultivars",
				description:
					"Assign cultivars to this paddock and specify their coverage percentage",
				selectCultivar: "Select a cultivar",
				coverage: "Coverage %",
				addCultivar: "Add Cultivar",
				addAnother: "Add",
				removeCultivar: "Remove",
				totalCoverage: "Total Coverage",
				noCultivarsAvailable: "No cultivars available. Create cultivars first.",
				allCultivarsAssigned: "All available cultivars have been assigned.",
				cultivarNotFound: "Didn't find the cultivar you're looking for?",
				registerNewCultivar: "Register a new one",
				coverageRange: "Enter a coverage between 0 and 100%",
				coverageExceeds: "Total coverage cannot exceed 100%",
				save: "Save Changes",
				cancel: "Cancel",
			},
		},
		measurement: {
			list: {
				title: "Measurements",
				empty: {
					title: "No measurements yet",
					description: "Record your first measurement",
				},
			},
			create: {
				title: "Record Measurement",
			},
			edit: {
				title: "Edit Measurement",
			},
			delete: {
				title: "Delete measurement",
				confirm:
					"This measurement will be permanently deleted. This cannot be undone.",
			},
			fields: {
				measuredAt: "Date & Time",
				swardHeight: "Sward Height",
				biomass: "Biomass Estimate",
				swardHeightHelp:
					"Average grass height from the ground to the top of the canopy.",
				biomassHelp:
					"Estimated dry matter (kg) per hectare available for grazing.",
				biomassMethodHelp:
					"How the biomass was estimated (ruler, rising plate meter, visual estimate, etc.).",
				biomassMethod: "Estimation Method",
				method: "Method",
				groundCover: "Ground Cover",
				location: "GPS Location",
				notes: "Notes",
			},
			placeholders: {
				notes: "Any observations about pasture condition...",
			},
			actions: {
				save: "Save Measurement",
				getLocation: "Get Current Location",
			},
			methods: {
				HEIGHT_DENSITY_EQUATION: "Height-Density Equation",
				RISING_PLATE_METER: "Rising Plate Meter",
				VISUAL_ESTIMATE: "Visual Estimate",
				CUT_AND_WEIGH: "Cut and Weigh",
				NIRS_SENSOR: "NIRS Sensor",
				SATELLITE_NDVI: "Satellite NDVI",
			},
			createSuccess: "Measurement recorded successfully",
			createError: "Failed to record measurement",
			updateSuccess: "Measurement updated successfully",
			deleteSuccess: "Measurement deleted successfully",
		},
		operation: {
			list: {
				title: "Operations",
				empty: {
					title: "No operations logged",
					description: "Log your first operation",
				},
			},
			create: {
				title: "Log Operation",
			},
			edit: {
				title: "Edit Operation",
			},
			delete: {
				title: "Delete operation",
				confirm:
					"This operation will be permanently deleted, along with its cost in the financial records. This cannot be undone.",
			},
			fields: {
				type: "Operation Type",
				performedAt: "Date & Time",
				cost: "Total Cost",
				notes: "Notes",
			},
			placeholders: {
				notes: "Additional details about the operation...",
			},
			actions: {
				save: "Save Operation",
			},
			types: {
				GRAZING: "Grazing",
				CUTTING: "Cutting",
				MOWING: "Mowing",
				FERTILIZATION: "Fertilization",
				LIMING: "Liming",
				IRRIGATION: "Irrigation",
				WEED_CONTROL: "Weed Control",
				PEST_CONTROL: "Pest Control",
				RENOVATION: "Renovation",
				SOIL_PREPARATION: "Soil Preparation",
			},
			grazing: {
				title: "Grazing Details",
				animalCount: "Animal Count",
				animalType: "Animal Type",
				entryBiomass: "Entry Biomass",
				exitBiomass: "Exit Biomass",
			},
			fertilization: {
				title: "Fertilization Details",
				product: "Product",
				rate: "Application Rate",
				nitrogen: "Nitrogen Applied",
			},
			cutting: {
				title: "Cutting Details",
				height: "Cutting Height",
				yield: "Yield",
				destination: "Destination",
				destinations: {
					hay: "Hay",
					silage: "Silage",
					fresh: "Fresh Feed",
				},
			},
			createSuccess: "Operation logged successfully",
			createError: "Failed to log operation",
			updateSuccess: "Operation updated successfully",
			deleteSuccess: "Operation deleted successfully",
		},
		soilLabReport: {
			eyebrow: "Farm documents",
			pageTitle: "Import Soil Lab Report",
			pageSubtitle:
				"Upload a report, review the extracted values, and track previous imports.",
			importAction: "Import report",
			importing: "Importing analyses...",
			importSuccess: "Soil analyses imported",
			steps: {
				upload: "Upload",
				extract: "Extract",
				review: "Review",
			},
			disclaimer: {
				title: "Before we start",
				body: "This import is here to save you the typing: our computer vision reads the values straight from your report. It gets most reports right, but scan quality, handwriting or an unusual layout can make a reading come out different from the paper.",
				checkAdvice:
					"You will see every extracted value in the review step — please compare them with the original report before confirming. Nothing is saved until you do.",
				confirm: "Got it, start the import",
			},
			upload: {
				title: "Upload a soil lab report",
				helper:
					"Upload the report as a PDF or a photo. The AI extracts each sample's results for you to review before saving.",
				chooseFile: "Choose file",
				submit: "Upload and extract",
				unsupportedType:
					"Unsupported file type. Upload a PDF or image (JPEG, PNG, WebP).",
			},
			extracting: {
				title: "Extracting the report...",
				helper: "This may take a moment. The page updates automatically.",
				failedTitle: "Extraction failed",
				failedHelper: "Could not extract the report. Please try again.",
				retry: "Try again",
			},
			review: {
				title: "Review and map samples",
				paddock: "Paddock",
				selectPaddock: "Select paddock",
				paddocks: "Paddocks",
				selectPaddocks: "Select paddocks",
				selectAllPaddocks: "Select all paddocks",
				deselectAllPaddocks: "Clear selection",
				nPaddocksSelected_one: "{{count}} paddock selected",
				nPaddocksSelected_other: "{{count}} paddocks selected",
				crossCheck: "Cross-check (computed / printed)",
				mismatchHint:
					"A computed index disagrees with the printed value — check the readings (possible OCR error).",
				unrecognizedUnit:
					'The unit "{{unit}}" is not one we recognize — pick the matching unit so this reading is used.',
				mapAllError: "Map every sample to a paddock.",
				duplicatePaddockError: "Each sample must map to a different paddock.",
				collisionError:
					"Glebas {{glebaA}} and {{glebaB}} are both mapped to paddock {{paddock}} with the same sampling date and depth. Change one of the two mappings.",
				repeatedPaddockError:
					"Paddock {{paddock}} is selected more than once in Gleba {{gleba}}. Remove the repeat.",
				skipWarning:
					"Glebas with no paddock selected will not be imported: {{glebas}}",
				skipCannotReimport:
					"This report cannot be imported again — whatever is left unmapped is lost.",
				skipAcknowledge: "I understand these Glebas will not be imported",
				allSkippedError:
					"Select at least one paddock for at least one Gleba before importing.",
				sampleN: "Sample {{n}}",
				importing: "Importing...",
				confirm_one: "Confirm and create {{count}} analysis",
				confirm_other: "Confirm and create {{count}} analyses",
			},
			indices: {
				sumOfBases: "SB",
				effectiveCec: "t",
				totalCec: "T",
				baseSaturation: "V%",
				aluminumSaturation: "m%",
			},
		},
		soilAnalysis: {
			reference: {
				label: "Soil interpretation reference",
				source: "View scientific source",
				unclassified: "No applicable criterion in this reference",
				loadError: "Could not load soil references.",
				saveError: "Could not save the selected reference.",
			},
			empty: "No soil analyses recorded",
			addFirst: "Add your first soil analysis to track soil health",
			addButton: "Add Soil Analysis",
			addTitle: "Add Soil Analysis",
			resultsSection: "Lab Results",
			formIntro:
				"Transcribe the values from your lab report. Leave any field you don't have blank — only what you enter is interpreted.",
			valuesLabel: "values",
			optionalNote: "All fields optional",
			glebaBadgeUnshared: "{{name}}",
			glebaBadge_one: "{{name}} · shared with {{count}} other paddock",
			glebaBadge_other: "{{name}} · shared with {{count}} other paddocks",
			sections: {
				metadata: "Sample & Lab",
				acidity: "Acidity & Organic Matter",
				phosphorus: "Phosphorus (by method)",
				cations: "Exchangeable Cations & Sulfur",
				micronutrients: "Micronutrients",
				texture: "Texture",
			},
			sectionDesc: {
				metadata:
					"When and where the sample was taken, and the lab's unit format.",
				acidity:
					"Soil reaction and organic matter — the baseline for liming and fertility.",
				phosphorus: "Enter whichever extraction method your lab reported.",
				cations:
					"Exchangeable bases, acidity and sulfur that drive base saturation.",
				micronutrients: "Trace elements — fill only those your lab measured.",
				texture: "Particle-size fractions used to derive the textural class.",
			},
			systems: {
				IAC: "IAC (Boletim 100)",
				CFSEMG: "CFSEMG (5ª Aproximação)",
				EMBRAPA_CERRADO: "Embrapa Cerrado",
			},
			fields: {
				sampleDate: "Sample Date",
				sampleDateOptional: "Sample Date (optional)",
				labEntrada: "Lab Received Date",
				labSaida: "Lab Released Date",
				labName: "Laboratory",
				labReportNumber: "Report Number",
				interpretationSystem: "Interpretation System",
				labUnitPreset: "Lab unit format",
				depthTop: "Depth from (cm)",
				depthBottom: "Depth to (cm)",
				soilTypeCode: "Soil Type Code",
				clayActivity: "Clay Activity (T)",
				ph: "pH",
				phWater: "pH (H₂O)",
				phCaCl2: "pH (CaCl₂)",
				phKCl: "pH (KCl)",
				organicMatter: "Organic Matter",
				organicCarbon: "Organic Carbon (C.O.)",
				phosphorus: "Phosphorus (P)",
				phosphorusMehlich: "P (Mehlich-1)",
				phosphorusResin: "P (resin)",
				phosphorusMehlich3: "P (Mehlich-3)",
				phosphorusRemaining: "P remaining (P-rem)",
				phosphorusTotal: "P total",
				potassium: "Potassium (K)",
				calcium: "Calcium (Ca)",
				magnesium: "Magnesium (Mg)",
				sodium: "Sodium (Na)",
				aluminum: "Aluminum (Al)",
				hydrogenAluminum: "H + Al",
				sulfur: "Sulfur (S)",
				boron: "Boron (B)",
				copper: "Copper (Cu)",
				iron: "Iron (Fe)",
				manganese: "Manganese (Mn)",
				zinc: "Zinc (Zn)",
				clay: "Clay",
				silt: "Silt",
				sand: "Sand",
			},
			createSuccess: "Soil analysis added successfully",
			createError: "Failed to add soil analysis",
			updateSuccess: "Soil analysis updated successfully",
			updateError: "Failed to update soil analysis",
			deleteSuccess: "Soil analysis deleted successfully",
			deleteError: "Failed to delete soil analysis",
			editTitle: "Edit Soil Analysis",
			details: {
				open: "Details",
				title: "Soil analysis · {{date}}",
				notFound: "Soil analysis not found",
				sampleTitle: "Sample",
				imported: "Imported from lab report",
				manual: "Entered by hand",
				depth: "Depth",
				gleba: "Gleba",
				valuesTitle: "Measured values",
				noValues: "No values recorded for this analysis.",
				notRequested: "not requested",
				sourceFile: "Lab report",
				openInNewTab: "Open in new tab",
			},
			samplesLabel: "Samples",
			viewReportError: "Couldn't open the lab report",
			showDerivedIndices: "Show all derived indices",
			hideDerivedIndices: "Hide derived indices",
			analysisTitle: "Analysis",
			derivedLabel: "Derived:",
			deleteTitle: "Delete Soil Analysis",
			confirmDelete:
				"Are you sure you want to delete this soil analysis? This action cannot be undone.",
			trend: {
				title: "Fertility trend",
				needMore:
					"Add a second dated analysis to see how pH, P and V% move between samplings.",
			},
			interpretation: {
				title: "Interpretation",
				insufficient: "Not enough data to interpret this analysis.",
				noDate: "no date",
				cationUnit: "Cation unit",
				texturalClass: "Textural class",
				measured: "measured",
				derived: "derived",
				warnings: "Notes",
			},
			indices: {
				baseSaturation: "Base saturation (V%)",
				aluminumSaturation: "Aluminum saturation (m%)",
			},
			scale: {
				label: "{{level}} — {{zone}}",
				marker: "This sample",
				zones: {
					bad: "Problem",
					attention: "Attention",
					good: "Good",
				},
			},
			levels: {
				muito_baixo: "very low",
				baixo: "low",
				medio: "medium",
				adequado: "adequate",
				alto: "high",
				muito_alto: "very high",
			},
			classLabels: {
				phWater: "pH",
				phosphorusMehlich: "P",
				phosphorusMehlich3: "P (M3)",
				potassium: "K",
				calcium: "Ca",
				magnesium: "Mg",
				aluminum: "Al",
				organicMatter: "O.M.",
				baseSaturation: "V%",
				aluminumSaturation: "m%",
				effectiveCec: "t",
				totalCec: "T",
			},
			texturalClass: {
				arenosa: "Sandy",
				media: "Medium",
				argilosa: "Clayey",
				muito_argilosa: "Very clayey",
			},
		},
		cultivar: {
			list: {
				title: "Cultivar Library",
				description:
					"This list shows cultivars and their average indicators. System cultivars provide reference values; you can create your own cultivar with settings that fit your farm.",
				searchPlaceholder: "Search by name, species, or variety",
				sourceFilter: "Filter by source",
				filters: { all: "All", organization: "Custom", system: "System" },
				resultCount: "{{count}} of {{total}} cultivars",
				noResults: "No cultivars found",
				noResultsDescription: "Try another search or change the source filter.",
				clearFilters: "Clear filters",
				systemDialog: {
					title: "System cultivar",
					description:
						"System cultivars cannot be edited. Create your own cultivar with its name, grazing heights, and rest period prefilled. Density and citations remain system references.",
					createFromSystem: "Create from this cultivar",
				},
				columns: {
					cultivar: "Cultivar",
					grazing: "Grazing heights",
					rest: "Rest period",
					density: "Reference density",
				},
				minimum: "Minimum",
				maximum: "Maximum",
				densityUnit: "kg DM/ha/cm",
				empty: {
					title: "No cultivars found",
					description:
						"Add cultivars to your library to assign them to paddocks",
				},
			},
			create: {
				title: "Add Cultivar",
			},
			edit: {
				title: "Edit Cultivar",
			},
			fields: {
				species: "Species",
				variety: "Variety",
				commonName: "Common Name",
				source: "Source",
				entryHeight: "Entry Height (cm)",
				exitHeight: "Exit Height (cm)",
				residualHeight: "Residual Height (cm)",
				restDaysMin: "Min Rest Days",
				restDaysMax: "Max Rest Days",
			},
			placeholders: {
				species: "e.g., Urochloa brizantha",
				variety: "e.g., Marandu",
				commonName: "e.g., Brachiaria Marandu",
			},
			required: "required",
			sections: {
				heights: {
					title: "Grazing heights and rest",
					optional: "optional",
					body: "Add these and the paddock tells you when to move the herd in and out. Leave them empty and the cultivar still works — you just won't get those alerts.",
				},
			},
			help: {
				species: "The botanical name printed on the seed bag.",
				variety: "The cultivar name, right after the species.",
				commonName:
					"The name your team says out loud. This is what shows on paddocks and records.",
			},
			suggestion: {
				text: "Most farms would call this <b>{{name}}</b>.",
				useIt: "Use it",
			},
			measures: {
				remove: "remove",
				units: {
					cm: "cm",
					days: "days",
				},
				entry: {
					label: "Entry height",
					help: "How tall the grass is when the herd goes in.",
				},
				exit: {
					label: "Exit height",
					help: "When the herd should come out, before it grazes too low.",
				},
				residual: {
					label: "Residual height",
					help: "What stays standing after grazing. This is what regrows.",
				},
				minRest: {
					label: "Minimum rest",
					help: "Shortest gap before the paddock is grazed again.",
				},
				maxRest: {
					label: "Maximum rest",
					help: "Longest gap before the grass gets past its point.",
				},
			},
			diagram: {
				eyebrow: "WHAT THIS MEANS",
				meaning:
					"The herd goes in at {{entry}} cm and comes out at {{exit}} cm. Below that the plant spends its reserves to regrow.",
				prompt:
					"Fill entry and exit height to see the grazing window for this cultivar.",
				entry: "Entry",
				exit: "Exit",
				residual: "Residual",
			},
			status: {
				blocked: "Species, variety and common name are required",
				readyWithAlerts: "Ready to save with grazing alerts on",
				readyLater: "Ready to save — heights can come later",
			},
			validation: {
				exitBelowEntry:
					"Exit height should be lower than entry height ({{entry}} cm).",
				residualVsExit: "Residual is usually the same as exit height or lower.",
				maxRestVsMin:
					"Maximum rest should be longer than the minimum ({{minRest}} days).",
			},
			source: {
				system: "System",
				organization: "Custom",
			},
			actions: {
				create: "Add Cultivar",
				createShort: "Cultivar",
			},
			createSuccess: "Cultivar created successfully",
			createError: "Failed to create cultivar",
			updateSuccess: "Cultivar updated successfully",
			updateError: "Failed to update cultivar",
		},
		map: {
			title: "Paddock Map",
			legend: "Legend",
			basemap: {
				label: "Base map",
				street: "Map",
				satellite: "Satellite",
			},
			colorBy: "Color by",
			colorByStatus: "Status",
			colorByBiomass: "Biomass",
			selectPaddock: "Select a paddock to view details",
			viewDetails: "View Details",
			paddockList: "Paddocks",
			noBoundaries: "No paddocks have map boundaries defined",
			noBoundary: "No boundary defined",
			biomass: {
				low: "Low (<1500 kg/ha)",
				medium: "Medium (1500-2500 kg/ha)",
				good: "Good (2500-3500 kg/ha)",
				high: "High (>3500 kg/ha)",
			},
		},
		alert: {
			title: "Alerts",
			acknowledgeSuccess: "Alert acknowledged",
			acknowledgeError: "Failed to acknowledge alert",
			resolveSuccess: "Alert resolved",
			resolveError: "Failed to resolve alert",
			noAlerts: "No alerts found",
			filterByStatus: "Filter by status",
			filterBySeverity: "Filter by severity",
			severity: {
				CRITICAL: "Critical",
				HIGH: "High",
				MEDIUM: "Medium",
				LOW: "Low",
			},
			statuses: {
				ACTIVE: "Active",
				ACKNOWLEDGED: "Acknowledged",
				RESOLVED: "Resolved",
				DISMISSED: "Dismissed",
			},
		},
		recommendation: {
			title: "AI Recommendations",
			approveSuccess: "Recommendation approved",
			approveError: "Failed to approve recommendation",
			rejectSuccess: "Recommendation rejected",
			rejectError: "Failed to reject recommendation",
			rejectTitle: "Reject Recommendation",
			rejectReasonPlaceholder: "Please provide a reason for rejection...",
			confirmReject: "Confirm Rejection",
			noRecommendations: "No recommendations found",
			filterByStatus: "Filter by status",
			statuses: {
				PENDING: "Pending",
				APPROVED: "Approved",
				REJECTED: "Rejected",
				EXPIRED: "Expired",
			},
		},
		forecast: {
			title: "Growth Forecasts",
			noData: "No forecast data available",
			readyForGrazing: "Ready for Grazing",
			approachingOptimal: "Approaching Optimal",
			avgGrowthRate: "Avg. Growth Rate",
			needsRest: "Needs Rest",
			kgHaDay: "kg/ha/day",
			daysToOptimal: "days to optimal",
			paddockForecasts: "Paddock Forecasts",
			statuses: {
				ready: "Ready",
				approaching: "Approaching",
				resting: "Resting",
				overgrown: "Overgrown",
			},
		},
		planning: {
			dashboard: {
				title: "Planning Dashboard",
				subtitle: "Manage annual and tactical plans",
			},
			kpi: {
				activePlans: "Active Plans",
				pendingTasks: "Pending Tasks",
				completedThisMonth: "Completed This Month",
				totalTasks: "Total Tasks",
				totalPlans: "Total Plans",
			},
			status: {
				label: "Status",
				DRAFT: "Draft",
				APPROVED: "Approved",
				ACTIVE: "Active",
				COMPLETED: "Completed",
				CANCELLED: "Cancelled",
			},
			annual: {
				title: "Annual Plans",
				subtitle: "Manage yearly production plans",
				activePlans: "Active Plans",
				recentPlans: "Recent Plans",
				noActivePlans: "No active plans",
				noPlans: "No annual plans",
				noPlansDesc: "Create your first annual plan to start planning",
				create: "Create Annual Plan",
				createTitle: "Create Annual Plan",
				createSubtitle: "Set up a new annual production plan",
				createSuccess: "Annual plan created successfully",
				createError: "Failed to create annual plan",
				updateSuccess: "Annual plan updated successfully",
				updateError: "Failed to update annual plan",
				deleteSuccess: "Annual plan deleted successfully",
				deleteError: "Failed to delete annual plan",
				deleteConfirmTitle: "Delete Annual Plan",
				deleteConfirmDescription:
					"Are you sure you want to delete this annual plan? This action cannot be undone and will also delete all associated tactical plans.",
				editStatus: "Edit Plan Status",
				details: "Plan Details",
				name: "Plan Name",
				namePlaceholder: "e.g., 2024 Production Plan",
				year: "Year",
				description: "Description",
				descriptionPlaceholder: "Describe the plan objectives...",
				yieldTarget: "Yield Target",
				budget: "Budget",
				targets: "Targets",
				createdAt: "Created At",
			},
			tactical: {
				title: "Tactical Plans",
				noPlans: "No tactical plans for this annual plan",
				create: "Create Tactical Plan",
				createSuccess: "Tactical plan created successfully",
				createError: "Failed to create tactical plan",
				updateSuccess: "Tactical plan updated successfully",
				updateError: "Failed to update tactical plan",
				deleteSuccess: "Tactical plan deleted successfully",
				deleteError: "Failed to delete tactical plan",
				name: "Plan Name",
				namePlaceholder: "e.g., Spring Grazing Plan",
				startDate: "Start Date",
				endDate: "End Date",
				description: "Description",
				descriptionPlaceholder: "Describe the tactical plan...",
			},
			quickLinks: {
				tasks: "Tasks",
				tasksDesc: "Manage field operations",
				rotation: "Rotation Planner",
				rotationDesc: "Plan grazing rotations",
				analytics: "Analytics",
				analyticsDesc: "View performance metrics",
			},
			tasks: {
				status: {
					pending: "Pending",
					scheduled: "Scheduled",
					in_progress: "In Progress",
					completed: "Completed",
					cancelled: "Cancelled",
				},
				priority: {
					critical: "Critical",
					high: "High",
					medium: "Medium",
					low: "Low",
				},
				kanbanView: "Kanban View",
				kanbanDescription: "Organize tasks by status with drag and drop",
				listView: "List View",
				calendarView: "Calendar View",
				selectEstablishment: "Select Establishment",
				selectEstablishmentPrompt: "Select an establishment to view tasks",
				markComplete: "Mark as Complete",
				detail: {
					scheduledDate: "Scheduled",
					dueDate: "Due Date",
					priority: "Priority",
					duration: "Duration",
					establishment: "Establishment",
					assignedTo: "Assigned To",
					domain: "Module",
					objective: "Objective",
					completedAt: "Completed on {{date}}",
					completedBy: "by {{name}}",
				},
			},
			objectives: {
				annual: {
					createSuccess: "Annual objective created successfully",
					createError: "Failed to create annual objective",
					updateSuccess: "Annual objective updated successfully",
					updateError: "Failed to update annual objective",
					deleteSuccess: "Annual objective deleted successfully",
					deleteError: "Failed to delete annual objective",
				},
				tactical: {
					createSuccess: "Tactical objective created successfully",
					createError: "Failed to create tactical objective",
					updateSuccess: "Tactical objective updated successfully",
					updateError: "Failed to update tactical objective",
					deleteSuccess: "Tactical objective deleted successfully",
					deleteError: "Failed to delete tactical objective",
				},
			},
		},
		rotation: {
			title: "Rotation Planner",
			subtitle: "Plan and manage grazing rotations",
			kpi: {
				totalSlots: "Total Slots",
				activeSlots: "Active Slots",
				plannedSlots: "Planned Slots",
				conflicts: "Conflicts",
			},
			conflicts: {
				title: "Scheduling Conflicts",
			},
			calendar: {
				title: "Rotation Calendar",
				paddock: "Paddock",
			},
		},
		analytics: {
			title: "Analytics Dashboard",
			subtitle: "Performance metrics and insights",
			export: "Export",
			noData: "No data available for this period",
			timeRange: {
				"7d": "Last 7 days",
				"30d": "Last 30 days",
				"90d": "Last 90 days",
				"1y": "Last year",
				all: "All time",
			},
			tabs: {
				growth: "Growth",
				operations: "Operations",
				economic: "Economic",
			},
			growth: {
				avgGrowthRate: "Avg. Growth Rate",
				totalBiomass: "Total Biomass",
				paddocksAnalyzed: "Paddocks Analyzed",
				measurementCount: "Measurements",
			},
			operations: {
				totalOperations: "Total Operations",
				completedOperations: "Completed",
				avgDuration: "Avg. Duration",
				efficiency: "Efficiency",
				byType: "Operations by Type",
			},
			economic: {
				totalRevenue: "Total Revenue",
				totalCosts: "Total Costs",
				netProfit: "Net Profit",
				roi: "ROI",
				breakdown: "Cost Breakdown",
			},
		},
		assistant: {
			memories: "Memories",
			memoryAuthor: "Reported by {{name}}",
			myMemories: "Mine",
			farmMemories: "Farm",
			noMemories: "No saved memories",
			deleteMemory: "Delete memory",
			title: "AI Assistant",
			subtitle: "Get intelligent insights and recommendations",
			conversations: "Conversations",
			noConversations: "No conversations yet",
			newConversation: "New Conversation",
			welcomeTitle: "How can I help with the farm today?",
			welcomeSubtitle:
				"Ask about livestock, paddocks, or weather across all your farms or a specific one.",
			openChat: "Open assistant",
			openFullView: "Open full view",
			chattingAboutFarm: "Farm assistant",
			generalAssistant: "General assistant",
			welcome: {
				title: "How can I help you today?",
				description:
					"Ask me about grass production, paddock management, or get recommendations for your farm.",
			},
			thinking: "Thinking...",
			processing: "Processing...",
			emptyThread: "Ask a question to get started.",
			deleteConversation: "Delete conversation",
			agents: {
				livestock: "Consulting the livestock specialist...",
				pasture: "Consulting the pasture specialist...",
				weather: "Checking weather data...",
				planning: "Reviewing the planning...",
				equipment: "Checking the equipment...",
				economics: "Analyzing the finances...",
			},
			allFarms: "All farms",
			scopedTo: "Conversation will focus on",
			suggestions: "Suggested questions",
			suggestedPrompts: [
				"How many animals do I have across all my farms?",
				"What is the status of my paddocks across all farms?",
				"Which farms do I have registered?",
				"What are the best practices for cattle management?",
			],
			inputPlaceholder: "Ask a question about your farm...",
			sending: "Sending message...",
			sendMessage: "Send message",
			sendError: "Failed to send message",
			inputTooLong: "Message too long",
			proposalFlushError:
				"The changes discussed in this answer could not be queued for approval. Nothing was recorded — please ask again.",
			// Terminal failure of a chat turn (BUG-3) — the bubble shows one of
			// these plus a Retry action instead of staying permanently empty.
			turnFailed: "This reply could not be completed.",
			streamStalled:
				"The reply stopped arriving. Check your connection and try again.",
			streamDeadline:
				"The reply took longer than expected and was cancelled. Please try again.",
			// The stream closed cleanly but never said the turn ended — a dropped
			// connection, not a finished answer.
			streamIncomplete:
				"The connection closed before the reply finished. Please try again.",
			// Terminal server-side failure (INTERNAL_ERROR code) — the backend's raw
			// English message must never reach a chat bubble.
			internalError:
				"Something went wrong while processing your question. Please try again in a moment.",
			retry: "Try again",
			createSuccess: "Conversation created",
			createError: "Failed to create conversation",
			deleteSuccess: "Conversation deleted",
			deleteError: "Failed to delete conversation",
			voiceInput: {
				record: "Record voice message",
				stopRecording: "Stop recording",
				transcribing: "Transcribing...",
				autoSend: "Send automatically",
				transcriptionError: "Failed to transcribe audio",
				unavailable: "Voice input is currently unavailable",
				recording: "Recording…",
				cancelRecording: "Discard recording",
				confirmRecording: "Stop and transcribe",
				remaining: "{{seconds}} s left",
				sendingIn: "Sending in {{seconds}} s — tap to edit",
				cutShort:
					"Recording was interrupted. The captured text stays in the box for you to review before sending.",
				startFailed: "Recording didn't start. Tap the mic to try again.",
				alreadyTranscribed:
					"This recording was already transcribed. Record again to send the text.",
				transcriptionInProgress:
					"This recording is still being transcribed. Try again in a moment.",
				retry: "Try again",
				micDeniedIosApp:
					"Microphone blocked. Open Settings → the app → Microphone and allow access.",
				micDeniedAndroid:
					"Microphone blocked. Tap the lock in the address bar → Permissions → Microphone.",
				micDeniedDefault:
					"Microphone blocked. Allow microphone access in your browser settings.",
				micNotFound: "No microphone found on this device",
				micInUse: "The microphone is being used by another app",
				explainerTitle: "Talk to the assistant",
				explainerBody:
					"Your browser will ask for microphone access. Recording only happens while the recording bar is open, and the audio is not kept after transcription.",
				explainerContinue: "Continue",
				explainerCancel: "Not now",
			},
			voiceOutput: {
				play: "Play reply",
				stop: "Stop audio",
				autoPlay: "Play replies aloud",
				truncatedHint: "Audio shortened — the full reply is in the text",
			},
			voice: {
				title: "Voice Assistant",
				startVoice: "Start Voice Chat",
				stopVoice: "Stop Voice Chat",
				connecting: "Connecting...",
				connected: "Connected",
				listening: "Listening...",
				processing: "Processing...",
				speaking: "Speaking...",
				idle: "Ready",
				disconnected: "Disconnected",
				error: "Error",
				micPermissionDenied: "Microphone access denied",
				micPermissionPrompt: "Please allow microphone access to use voice chat",
				micNotSupported: "Microphone not supported in this browser",
				connectionFailed: "Failed to connect to voice server",
				transcription: "Transcription",
				tapToSpeak: "Tap to speak",
				releaseToSend: "Release to send",
				holdToSpeak: "Hold to speak",
				speakNow: "Speak now...",
				noAudioDetected: "No audio detected",
				audioLevel: "Audio level",
				retryConnection: "Retry Connection",
				endSession: "End Session",
				quality: {
					noInput: "No audio input detected. Check your microphone.",
					tooQuiet: "Audio too quiet. Speak louder or move closer.",
					tooLoud: "Audio too loud. Speak softer or move away.",
				},
			},
		},
	},
	feasibility: {
		nav: {
			title: "Feasibility Analysis",
		},
		study: {
			createSuccess: "Feasibility study created",
			createError: "Failed to create study",
			updateSuccess: "Study updated",
			updateError: "Failed to update study",
			deleteSuccess: "Study deleted",
			deleteError: "Failed to delete study",
			calculating: "Running analysis...",
			calculateSuccess: "Analysis complete",
			calculateError: "Analysis failed",
		},
		list: {
			title: "Feasibility Studies",
			newStudy: "New Study",
			noStudies: "No feasibility studies yet",
			createFirst:
				"Create your first feasibility study to analyze a cow-calf operation",
		},
		new: {
			lede: "Set up a viability study for your operation. You'll define the herd, forage, and economics step by step — then run the projection.",
			formTitle: "Study details",
			formSubtitle: "Name your study and choose where it runs.",
			typeSection: "Operation type",
			typeSectionHint: "Choose the kind of operation to model.",
			whatsNextTitle: "What comes next",
			whatsNextSubtitle:
				"After you create the study, you'll work through these sections:",
			whatsNextNote:
				"Nothing is final until you run the analysis — you can revisit every section.",
			descriptions: {
				COW_CALF:
					"Model a breeding herd: calves born, weaned, and sold against forage and costs.",
			},
		},
		types: {
			COW_CALF: "Cow-Calf Operation",
		},
		status: {
			DRAFT: "Draft",
			PENDING_CALC: "Calculating",
			CALCULATED: "Calculated",
			ERROR: "Error",
		},
		verdict: {
			GO: "Go",
			CONDITIONAL_GO: "Conditional Go",
			NO_GO: "No Go",
		},
		wizard: {
			steps: {
				farmForage: "Farm & Forage",
				herdDefinition: "Herd Definition",
				reproductionSurvival: "Reproduction & Survival",
				nutritionIntake: "Nutrition & Intake",
				commercialFinancial: "Commercial & Financial",
				review: "Review",
			},
			runAnalysis: "Run Analysis",
			recalculate: "Recalculate",
			review: {
				title: "Review Your Inputs",
				allComplete: "All sections are complete. You can now run the analysis.",
				incomplete:
					"Please complete all required sections before running the analysis.",
				stepIncomplete: "This section needs to be completed",
				requiredSections: "Complete all required sections to proceed",
				farmForage: {
					pastureCount: "{{count}} pasture type(s)",
					totalArea: "{{area}} ha total area",
					waterPoints: "{{count}} water point(s)",
				},
				herdDefinition: {
					breedingCows: "{{count}} breeding cows",
					bulls: "{{count}} bulls",
					target: "Target: {{count}} breeding females",
				},
				reproductionSurvival: {
					pregnancyRate: "{{rate}}% pregnancy rate",
					weaningWeight: "{{weight}} kg weaning weight",
				},
				nutritionIntake: {
					cowWeight: "{{weight}} kg mature cow weight",
					bullWeight: "{{weight}} kg bull weight",
				},
				commercialFinancial: {
					calfPrice: "{{price}} {{currency}}/kg calf price",
					startingCash: "{{amount}} {{currency}} starting cash",
				},
			},
		},
		cowcalf: {
			farmForage: {
				location: {
					title: "Location & Climate",
					description: "Define the wet season and drought risk for your region",
					wetSeasonMonths: "Wet Season Months",
					droughtFrequency: "Drought Frequency",
					droughtFrequencyHint: "e.g., 0.2 means 1-in-5 years",
				},
				waterAccess: {
					title: "Water Access",
					description: "Water infrastructure for livestock",
					waterPoints: "Number of Water Points",
					maxWalkingDistance: "Max Walking Distance (km)",
					reliabilityScore: "Water Reliability",
				},
				pasture: {
					title: "Pasture Inventory",
					description: "Define your pasture types and production capacity",
					addPasture: "Add Pasture Type",
					name: "Pasture Name",
					areaHa: "Area (ha)",
					condition: "Condition",
					utilizationPercent: "Utilization %",
				},
			},
			herdDefinition: {
				startingHerd: {
					title: "Starting Herd",
					description: "Current herd composition",
					breedingCows: "Breeding Cows",
					bulls: "Bulls",
					heifers12to24: "Heifers 12-24 months",
					heifers0to12: "Heifers 0-12 months",
				},
				targetHerd: {
					title: "Target Herd",
					description: "Growth objectives for your operation",
					targetFemales: "Target Breeding Females",
					rampUpStrategy: "Ramp-up Strategy",
					targetYear: "Target Year",
					strategies: {
						PURCHASE: "Purchase",
						RETAIN: "Retain Heifers",
						HYBRID: "Hybrid",
					},
				},
				breedingSystem: {
					title: "Breeding System",
					description: "Breeding season and bull management",
					breedingMonths: "Breeding Season Months",
					bullCowRatio: "Bull:Cow Ratio",
				},
			},
			reproductionSurvival: {
				reproduction: {
					title: "Reproduction Rates",
					description: "Expected reproduction performance",
				},
				mortality: {
					title: "Mortality & Culling",
					description: "Expected losses and culling rates",
				},
				weaning: {
					title: "Weaning Parameters",
					description: "Age and weight at weaning",
				},
				pregnancyRate: "Pregnancy Rate",
				abortionStillbirth: "Abortion/Stillbirth Rate",
				preWeaningMortality: "Pre-Weaning Mortality",
				cowCullRate: "Cow Cull Rate",
				cowMortality: "Cow Mortality",
				bullMortality: "Bull Mortality",
				heiferMortality: "Heifer Mortality",
				heiferConceptionRate: "Heifer Conception Rate",
				heiferSurvival: "Heifer Survival to 1st Calving",
				sexRatioMale: "Sex Ratio (% Male)",
				weaningAge: "Weaning Age",
				weaningWeight: "Weaning Weight",
			},
			nutritionIntake: {
				animalWeights: {
					title: "Animal Weights",
					description: "Mature weights for different animal classes",
				},
				intakeRates: {
					title: "Intake Rates",
					description: "Daily dry matter intake as percentage of body weight",
				},
				supplementation: {
					title: "Supplementation",
					description: "Mineral and hay supplementation costs",
					mineralCostPerCowYear: "Mineral Cost per Cow/Year",
					mineralCostPerBullYear: "Mineral Cost per Bull/Year",
					mineralCostPerHeiferYear: "Mineral Cost per Heifer/Year",
					hayDryMatterPercent: "Hay Dry Matter",
					hayFeedingWastePercent: "Hay Feeding Waste",
					hayCostPerKgAsFed: "Hay Cost per kg (as fed)",
				},
				cowMatureWeight: "Mature Cow Weight",
				bullWeight: "Bull Weight",
				heifer12to24Weight: "Heifer (12-24mo) Weight",
				heifer0to12Weight: "Heifer (0-12mo) Weight",
				lactatingCowIntake: "Lactating Cow Intake",
				dryCowIntake: "Dry Cow Intake",
				bullIntake: "Bull Intake",
				heifer12to24Intake: "Heifer (12-24mo) Intake",
				heifer0to12Intake: "Heifer (0-12mo) Intake",
			},
			commercialFinancial: {
				capitalFinancing: {
					title: "Capital Investment & Financing",
					description: "Infrastructure, equipment, and financing terms",
					infrastructureCapex: "Infrastructure Investment",
					equipmentCapex: "Equipment Investment",
					animalPurchaseCosts: "Animal Purchase Costs",
					debtPercent: "Debt Financing",
					interestRatePercent: "Interest Rate",
					tenorYears: "Loan Term",
				},
				workingCapital: {
					title: "Working Capital",
					description: "Starting cash and liquidity",
					startingCash: "Starting Cash",
				},
				calfSales: {
					title: "Calf Sales",
					description: "Calf sale prices and costs",
					saleMonth: "Sale Month",
					pricePerKg: "Price per kg Liveweight",
					shrinkPercent: "Shrink",
					commissionPercent: "Commission",
					freightPerHead: "Freight per Head",
				},
				cullSales: {
					title: "Cull Cow Sales",
					description: "Cull cow sale prices and costs",
					saleMonth: "Sale Month",
					pricePerKg: "Price per kg",
					saleWeightKg: "Sale Weight",
					shrinkPercent: "Shrink",
					commissionPercent: "Commission",
					freightPerHead: "Freight per Head",
				},
				operatingCosts: {
					title: "Operating Costs",
					description: "Annual operating expenses",
					vetPerCow: "Vet/Medicine per Cow",
					vetFixedCostYear: "Vet Fixed Cost/Year",
					bullUpkeepPerBullYear: "Bull Upkeep per Bull/Year",
					pastureMaintPerHaYear: "Pasture Maintenance/Ha/Year",
					laborMonthly: "Monthly Labor Cost",
					fuelRepairsYear: "Fuel & Repairs/Year",
					adminInsuranceTaxesYear: "Admin, Insurance & Taxes/Year",
				},
			},
		},
		results: {
			title: "Analysis Results",
			summary: "Summary",
			verdict: "Verdict",
			viewDetails: "View Details",
			editInputs: "Edit Inputs",
			recalculate: "Recalculate",
			downloadPdf: "Download PDF",
			keyMetrics: "Key Metrics",
			npv: "Net Present Value",
			irr: "Internal Rate of Return",
			payback: "Payback Period",
			dscr: "Debt Service Coverage Ratio",
			years: "years",
			herdProjection: "Herd Projection",
			feedBalance: "Feed Balance",
			financials: "Financial Projections",
			cashFlow: "Cash Flow",
			profitLoss: "Profit & Loss",
			sensitivity: "Sensitivity Analysis",
			scenarios: "Scenarios",
			constraints: "Primary Constraints",
			conditions: "Conditions to Proceed",
			noConditions: "No conditions - project is viable as designed",
			// Constraint types
			constraintType: {
				CAPITAL: "Capital",
				RETURNS: "Returns",
				LIQUIDITY: "Liquidity",
				FORAGE: "Forage",
				REPRODUCTION: "Reproduction",
			},
			// Condition messages
			conditionMessage: {
				reduceInvestment:
					"Reduce initial investment or secure lower-cost financing",
				improveReturns: "Improve returns to achieve minimum {{irr}}% IRR",
				reduceDept:
					"Reduce debt or improve cash flow to meet DSCR requirements",
				reducePayback: "Reduce payback period to under {{years}} years",
				reduceStockingRate:
					"Reduce stocking rate or secure supplementary feed sources",
				secureWorkingCapital:
					"Secure working capital line of credit or increase starting cash",
				improveReproduction:
					"Improve reproductive performance to achieve minimum 75% weaning rate",
			},
			// Verdict summaries
			verdictSummary: {
				go: "The project is financially viable with an NPV of {{npv}}, {{irr}}% IRR, and {{payback}}-year payback period. All key metrics meet or exceed thresholds.",
				conditionalGo:
					"The project shows potential but has constraints in: {{constraints}}. NPV: {{npv}}, IRR: {{irr}}%, DSCR: {{dscr}}. Address the listed conditions before proceeding.",
				noGo: "The project is not viable in its current form. Key constraints: {{constraints}}. NPV: {{npv}}, IRR: {{irr}}%. Significant restructuring is required.",
			},
			riskFactors: "Risk Factors",
			riskCategory: {
				DROUGHT: "Drought",
				DISEASE: "Disease Outbreak",
				PRICE: "Calf Price Volatility",
				FERTILITY: "Reproductive Performance",
				INPUT_COST: "Input Cost Inflation",
			},
			probability: {
				LOW: "Low",
				MEDIUM: "Medium",
				HIGH: "High",
			},
			impact: {
				LOW: "Low",
				MEDIUM: "Medium",
				HIGH: "High",
				CRITICAL: "Critical",
			},
			mitigation: {
				DROUGHT:
					"Maintain hay reserves, establish destocking triggers, drought insurance",
				DISEASE:
					"Vaccination program, biosecurity protocols, quarantine for new animals",
				PRICE:
					"Forward contracts, price risk management, diversify sales timing",
				FERTILITY:
					"Improve nutrition, bull fertility testing, veterinary support",
				INPUT_COST:
					"Lock in feed prices, optimize pasture utilization, reduce waste",
			},
			unitEconomics: "Unit Economics",
			costPerCow: "Cost per Exposed Female",
			costPerCalf: "Cost per Weaned Calf",
			marginPerCalf: "Net Margin per Calf",
			marginPerHa: "Net Margin per Hectare",
			breakEven: "Break-Even Analysis",
			breakEvenCalfPrice: "Break-Even Calf Price",
			breakEvenWeaningRate: "Break-Even Weaning Rate",
			year: "Year",
			breedingCows: "Breeding Cows",
			bulls: "Bulls",
			calvesWeaned: "Calves Weaned",
			weaningRate: "Weaning Rate",
			supply: "Supply",
			demand: "Demand",
			balance: "Balance",
			deficit: "Deficit",
			surplus: "Surplus",
			revenue: "Revenue",
			costs: "Costs",
			netProfit: "Net Profit",
			grossMargin: "Gross Margin",
			deficitMonths: "Deficit Months",
			totalHayCost: "Total Hay Cost",
			criticalPeriods: "Critical Periods",
			inflows: "Inflows",
			outflows: "Outflows",
			endingCash: "Ending Cash",
		},
		tooltips: {
			npv: "Net Present Value (NPV) represents the total value of future cash flows in today's dollars.\n\nFormula: Sum of (Cash Flow / (1 + Discount Rate)^Year)\n\nPositive NPV indicates the project creates value.",
			irr: "Internal Rate of Return (IRR) is the discount rate that makes NPV equal to zero.\n\nIt represents the expected annual return on investment.\n\nHigher IRR = more attractive investment.",
			payback:
				"Payback Period is the time required to recover the initial investment.\n\nCalculated by finding when cumulative cash flows turn positive.\n\nShorter payback = lower risk.",
			dscr: "Debt Service Coverage Ratio measures the ability to pay debt obligations.\n\nFormula: Net Operating Income / Total Debt Service\n\nDSCR > 1.2 is typically required by lenders.",
			herdProjection:
				"Herd projection shows the evolution of your cattle inventory over time.\n\nLines show: Breeding Cows, Bulls, and Calves Weaned per year.\n\nBased on pregnancy rates, weaning rates, culling, and heifer retention.",
			weaningRate:
				"Weaning rate is the percentage of calves successfully weaned from breeding cows.\n\nFormula: (Calves Weaned / Breeding Cows) × 100\n\nAffected by pregnancy rate, calf mortality, and cow losses.",
			deficitMonths:
				"Number of months where forage demand exceeds pasture supply.\n\nThese months require supplemental hay feeding.\n\nBased on monthly pasture production vs. herd consumption.",
			totalHayCost:
				"Total cost of supplemental hay purchases for the year.\n\nFormula: Sum of (Monthly Deficit kg × Hay Price/kg)\n\nDeficit = max(0, Demand - Supply) for each month.",
			criticalPeriods:
				"Months identified as having feed deficits.\n\nDuring critical periods, pasture cannot meet herd demand.\n\nPlan hay reserves or reduce stocking for these periods.",
			feedBalance:
				"Feed balance compares monthly forage supply to herd demand.\n\nSupply: Pasture production in kg DM (dry matter)\nDemand: Herd consumption based on animal weights and intake rates\n\nGreen = surplus, Red = deficit requiring hay.",
			unitEconomics:
				"Unit economics shows profitability per animal.\n\nCost per Cow: Total costs / Breeding cows\nCost per Calf: Total costs / Calves weaned\nMargin per Calf: (Revenue - Costs) / Calves weaned\nMargin per Ha: Net profit / Total hectares",
			breakEven:
				"Break-even analysis shows the threshold values where profit = 0.\n\nCalf Price: Minimum sale price to cover costs\nWeaning Rate: Minimum rate needed to break even\nHay Price: Maximum hay cost before losing money",
			profitLoss:
				"Profit & Loss chart shows annual financial performance.\n\nRevenue: Calf sales + Cull cow sales\nCosts: Variable + Fixed operating costs\nNet Profit: Revenue - All Costs (including interest & depreciation)",
			cashFlow:
				"Cash flow chart shows money movement through the operation.\n\nInflows: Sales revenue + Loans received\nOutflows: Operating costs + Loan payments + CAPEX\nEnding Cash: Cumulative cash position",
		},
		calculations: {
			showCalculations: "Calculations",
			title: "Calculation Details",
			herdDynamics: "Herd",
			feedBalance: "Feed",
			financials: "Financials",
			unitEconomics: "Unit Econ",
			investmentMetrics: "Investment",
			cashFlow: "Cash Flow",
			// Herd Structure
			herdStructure: "Herd Structure",
			breedingCows: "Breeding Cows",
			bulls: "Bulls",
			heifers12to24: "Replacement Heifers (12-24 mo)",
			heifers0to12: "Replacement Heifers (0-12 mo)",
			// Weaned Calves
			weanedCalves: "Weaned Calves Calculation",
			pregnancyRate: "Pregnancy Rate",
			weaningRate: "Weaning Rate",
			calvesWeanedResult: "Calves Weaned",
			replacements: "Replacement Requirements",
			cowsCulled: "Cows Culled",
			cowDeaths: "Cow Deaths",
			heifersRetained: "Heifers Retained",
			salesOutput: "Sales Output",
			calvesSold: "Calves Sold",
			// Feed Balance
			feedBalanceFormula: "Feed Balance Formula",
			feedBalanceDescription:
				"Monthly forage supply is compared to herd demand. Negative gaps require supplemental hay.",
			monthlyDeficits: "Monthly Deficits (Year 1)",
			noDeficits: "No feed deficits in any month.",
			hayRequirement: "Hay Requirement",
			totalHayTonnes: "Total Hay Required",
			totalHayCost: "Total Annual Hay Cost",
			// Revenue
			revenueCalc: "Revenue Calculations",
			calfRevenue: "Net Calf Revenue",
			cullRevenue: "Net Cull Revenue",
			grossRevenue: "Gross Revenue",
			// Operating Costs
			operatingCosts: "Operating Costs",
			hayCosts: "Hay Costs",
			mineralCosts: "Mineral Costs",
			vetCosts: "Vet & Medicine",
			laborCosts: "Labor Costs",
			pastureMaint: "Pasture Maintenance",
			totalVariableCosts: "Total Variable Costs",
			totalFixedCosts: "Total Fixed Costs",
			// Profitability
			profitability: "Profitability",
			grossMargin: "Gross Margin",
			ebitda: "EBITDA",
			ebitdaPerCow: "EBITDA per Cow",
			ebitdaPerCalf: "EBITDA per Calf Sold",
			interestExpense: "Interest Expense",
			depreciation: "Depreciation",
			netProfit: "Net Profit",
			// Unit Economics
			costMetrics: "Cost Metrics",
			costPerCow: "Cost per Exposed Female",
			costPerCalf: "Cost per Weaned Calf",
			marginMetrics: "Margin Metrics",
			marginPerCalf: "Net Margin per Calf",
			marginPerHa: "Net Margin per Hectare",
			breakEvenAnalysis: "Break-Even Analysis",
			breakEvenDescription:
				"Break-even values show the threshold at which the operation generates zero profit.",
			breakEvenCalfPrice: "Break-Even Calf Price",
			breakEvenCalfPriceFormula: "Total Costs / (Calves Sold × Weaning Weight)",
			breakEvenWeaningRate: "Break-Even Weaning Rate",
			breakEvenWeaningRateFormula:
				"Total Costs / (Calf Revenue / Current Weaning Rate)",
			breakEvenHayPrice: "Break-Even Hay Price",
			breakEvenHayPriceFormula:
				"Estimated maximum hay price before operation becomes unprofitable",
			// Investment Metrics
			investmentReturns: "Investment Returns",
			npv: "Net Present Value (NPV)",
			irr: "Internal Rate of Return (IRR)",
			paybackPeriod: "Payback Period",
			years: "years",
			roic: "Return on Invested Capital (ROIC)",
			debtCoverage: "Debt Coverage",
			dscr: "Debt Service Coverage Ratio (DSCR)",
			dscrDescription:
				"DSCR > 1.0 means cash flow covers debt payments. Higher is better.",
			// Cash Flow
			cashFlowSummary: "Cash Flow Summary (Year 1)",
			totalCashInflows: "Total Cash Inflows",
			totalCashOutflows: "Total Cash Outflows",
			netCashFlow: "Net Cash Flow",
			liquidity: "Liquidity",
			peakNegativeCash: "Peak Negative Cash (WC Requirement)",
			endingCashBalance: "Ending Cash Balance",
			// Debt Service
			debtService: "Debt Service (Year 1)",
			openingDebt: "Opening Debt Balance",
			principalPayment: "Principal Payment",
			interestPayment: "Interest Payment",
			totalDebtService: "Total Debt Service",
			closingDebt: "Closing Debt Balance",
		},
	},
	livestock: {
		waterIntake: {
			herdTotal: "Average daily water intake for the herd",
			animal: "Estimated average water intake",
			unit: "L/day",
		},
		dryMatterIntake: {
			herdTotal: "Daily DM intake for the herd",
			herdWeightWarning:
				"The calculation only includes the {{weighed}} of {{total}} animals with a recorded weight. Animals without weights were excluded.",
			animal: "Estimated daily DM intake",
			unit: "kg DM/day",
		},
		nav: {
			title: "Livestock",
		},
		attention: {
			title: "Needs attention",
			columnHeader: "Attention",
			forEntity: { title: "Attention items" },
			forEntityCount_one: "{{count}} attention item",
			forEntityCount_other: "{{count}} attention items",
			allClear: "All clear",
			allClearHint:
				"Overdue weighings, vaccinations and rotations, paddocks over capacity and expected calvings will show up here as you keep records.",
			// With nothing recorded nothing has been assessed — the line promises
			// nothing about what triggers an alert (§2.7).
			nothingYet:
				"Nothing pending so far. When something needs attention, it shows up here.",
			needAttention_one: "{{count}} need attention",
			needAttention_other: "{{count}} need attention",
			viewDetails_one: "View {{count}} attention item",
			viewDetails_other: "View {{count}} attention items",
			badgeLabel_one: "{{count}} attention item: {{breakdown}}",
			badgeLabel_other: "{{count}} attention items: {{breakdown}}",
			categories: {
				VACCINATION_DUE: "Vaccination due",
				WEIGHING_OVERDUE: "Weighing overdue",
				PADDOCK_OVERSTAY: "Rotation overdue",
				OVER_CAPACITY: "Over capacity",
				CALVING_WINDOW: "Calving expected",
			},
			affectedAnimals_one: "{{overdue}} of {{count}} animal",
			affectedAnimals_other: "{{overdue}} of {{count}} animals",
			wholeHerd: "Entire herd",
			overdueBy_one: "{{count}} day overdue",
			overdueBy_other: "{{count}} days overdue",
			overdueYears_one: "{{count}}+ year overdue",
			overdueYears_other: "{{count}}+ years overdue",
			yearsShort_one: "{{count}}+ year",
			yearsShort_other: "{{count}}+ years",
			daysCount_one: "{{count}} day",
			daysCount_other: "{{count}} days",
			neverVaccinated: "Never vaccinated",
			overBy_one: "{{count}} over",
			overBy_other: "{{count}} over",
			inDays_one: "in {{count}} day",
			inDays_other: "in {{count}} days",
			today: "today",
			loadError: "Couldn't load alerts",
			moreCount: "+{{count}} more",
			asOf: "updated {{when}}",
			retry: "Try again",
			vaccinationDescription: 'The "{{rule}}" vaccine is past its schedule.',
			vaccinationLastDose: 'Last "{{rule}}" dose on {{date}}.',
			vaccinationNeverDosed: 'No "{{rule}}" dose ever recorded.',
			neverVaccinatedCount_one: "{{count}} never vaccinated",
			neverVaccinatedCount_other: "{{count}} never vaccinated",
			weighingDescription: "No weight recorded for over {{days}} days.",
			weighingLastDate: "Last weighed on {{date}}.",
			neverWeighed: "No weight ever recorded.",
			neverWeighedCount_one: "{{count}} never weighed",
			neverWeighedCount_other: "{{count}} never weighed",
			overstayDescription:
				'On paddock "{{paddock}}" for {{duration}} (target: {{target}} days).',
			overstayShort: "{{duration}} on this paddock (target: {{target}} days)",
			capacityDescription:
				"The herd has {{head}} animals; its capacity is {{max}}.",
			calvingDescription:
				"Expected around {{date}}, based on the diagnosis on {{diagDate}}.",
			actions: {
				recordVaccination: "Record vaccination",
				recordVaccinationBulk: "Vaccinate overdue",
				recordWeight: "Weigh",
				rotate: "Rotate",
				viewHerd: "View herd",
				viewAnimal: "View animal",
			},
		},
		// The animal's vaccination panel. Unlike `attention`, it also carries the
		// states that are NOT a debt — a calf too young, a dose already given, a
		// rule that does not apply. Nothing here becomes an alert or an email.
		vaccinationStatus: {
			title: "Vaccination",
			empty: {
				title: "No vaccination rules set up",
				description:
					"With no rules in the sanitary calendar there is nothing to assess for this animal.",
				action: "Set up the sanitary calendar",
			},
			statuses: {
				DUE: {
					label: "Overdue",
					detail_one: "Due since {{date}} — {{count}} day.",
					detail_other: "Due since {{date}} — {{count}} days.",
				},
				WINDOW_MISSED: {
					label: "Window closed",
					detail:
						"The window for this dose closed on {{date}} and no dose was recorded.",
				},
				UNKNOWN_AGE: {
					label: "Age unknown",
					detail:
						"Without a birth date this animal's age cannot be judged against this rule.",
				},
				// The newborn case: reassurance, not a warning.
				TOO_YOUNG: {
					label: "Not yet",
					detail: "Not time yet — first dose expected around {{date}}.",
				},
				UPCOMING: {
					label: "Scheduled",
					detail: "Next dose expected around {{date}}.",
				},
				DONE: {
					label: "Up to date",
					detail: "Dose recorded on {{date}}.",
				},
				NOT_APPLICABLE: {
					label: "Not applicable",
					detail: "This rule does not apply to this animal.",
				},
			},
		},
		// Care card on the animal page (2026-09-04 enrichment, phase 9).
		care: {
			title: "Health",
			subtitle:
				"Withdrawal, deworming, diseases and treatments for this animal",
			withdrawal: {
				banner:
					"In withdrawal until {{date}} ({{medication}}) — cannot be sold or slaughtered",
				bannerNoMedication:
					"In withdrawal until {{date}} — cannot be sold or slaughtered",
			},
			lastDeworming: "Last deworming",
			lastEctoparasiteControl: "Last ectoparasite control",
			neverRecorded: "Never recorded",
			openDiseases: {
				title: "Open diseases",
				noDiagnosis: "No diagnosis",
				quarantined: "Quarantined",
			},
			treatments: {
				title: "Recent treatments",
				noMedication: "Medication not recorded",
				durationDays_one: "{{count}} day",
				durationDays_other: "{{count}} days",
				withdrawalUntil: "Withdrawal until {{date}}",
				performedBy: "by {{name}}",
			},
			upcoming: {
				title: "Upcoming",
				kinds: {
					weighing: "Weighing",
					vaccination: "Vaccination",
					pregnancy_check: "Pregnancy check",
				},
			},
			nutrition: {
				title: "Nutrition",
			},
		},
		reproduction: {
			notRecorded: "Not available",
			statuses: {
				PREGNANT: "Pregnant",
				EMPTY: "Open",
				DOUBTFUL: "Inconclusive",
				UNKNOWN: "Unknown",
			},
			values: {
				days_one: "{{count}} day",
				days_other: "{{count}} days",
				months_one: "{{count}} month",
				months_other: "{{count}} months",
				kg: "{{value}} kg",
				measurementWithDate: "{{value}} {{unit}} · {{date}}",
			},
			cow: {
				title: "Reproduction",
				subtitle: "Cycle standing and lifetime reproductive productivity",
				cycleStatus: "Cycle status",
				calvings: "Calvings",
				averageCalvingInterval: "Average calving interval",
				ageAtFirstCalving: "Age at first calving",
				daysOpen: "Days open",
				lastDiagnosis: "Last pregnancy diagnosis",
				weanedKgPerCalving: "Weaned kg per calving",
				currentPregnancySire: "Current-pregnancy sire",
			},
			bull: {
				title: "Breeding performance",
				subtitle: "Soundness, exposure and progeny performance",
				scrotalCircumference: "Scrotal circumference",
				lastExam: "Last breeding soundness exam",
				cowsExposed: "Cows exposed · last 12 months",
				progenyCount: "Progeny",
				progenyAverageGmd90: "Average progeny ADG · 90 days",
				viewOffspring: "View offspring",
			},
		},
		settings: {
			title: "Livestock",
			tabLabel: "Livestock",
			updateSuccess: "Settings saved",
			thresholds: {
				title: "Alerts & parameters",
				weighingOverdueDays: {
					label: "Weighing overdue (days)",
					help: "Fattening herds with no weighing for more than N days need attention.",
				},
				paddockOverstayDefaultDays: {
					label: "Default paddock-days target",
					help: "Used when a rotation has no planned days. Empty = no default alert.",
					placeholder: "No default",
				},
				calvingWindowDays: {
					label: "Calving window (days)",
					help: "How far ahead to flag expected calvings.",
				},
				waterIntakePercent: {
					label: "Average water intake (% of weight)",
					help: "Percentage of the latest live weight used to estimate daily intake in liters. Default: 10%.",
					invalid: "Enter a percentage greater than 0 and at most 100",
				},
				dryMatterIntakePercent: {
					label: "Daily dry matter intake (DM) (% of weight)",
					help: "Percentage of the latest live weight used to estimate intake in kg DM/day. Default: 2.5%. Leave blank to use the default.",
					placeholder: "2.5% (default)",
					invalid: "Enter a percentage greater than 0 and at most 100",
				},
				carcassYieldPercent: {
					label: "Carcass yield (%)",
					help: "Used by the arrobas-produced math and margin projections.",
				},
			},
			rules: {
				title: "Sanitary calendar (vaccinations)",
				add: "New rule",
				addTitle: "New vaccination rule",
				editTitle: "Edit vaccination rule",
				empty: "No rules configured.",
				createSuccess: "Rule created",
				updateSuccess: "Rule updated",
				deleteSuccess: "Rule deleted",
				summaryRecurring: "Every {{days}} days",
				summaryAgeWindow: "Single dose between {{min}} and {{max}} days of age",
				matchLabel: 'match: "{{match}}"',
				kinds: {
					RECURRING: "Recurring",
					SINGLE_DOSE_AGE_WINDOW: "Single dose by age",
				},
				sex: {
					ANY: "Any sex",
					FEMALE: "Females",
					MALE: "Males",
				},
				fields: {
					name: "Name",
					namePlaceholder: "Foot-and-mouth",
					vaccineMatch: "Match text",
					vaccineMatchPlaceholder: "aftosa",
					vaccineMatchHelp:
						"Fragment compared against vaccination records' description/medication.",
					ruleKind: "Kind",
					appliesToSex: "Sex",
					intervalDays: "Interval (days)",
					minAgeDays: "Minimum age (days)",
					maxAgeDays: "Maximum age (days)",
				},
				validation: {
					maxAgeAfterMin: "Maximum age must be greater than the minimum",
				},
				deleteConfirm: {
					title: 'Delete rule "{{name}}"?',
					body: "The rule stops generating alerts immediately.",
				},
			},
		},
		margin: {
			title: "Margin",
			subtitle: "Sale result, computed from livestock records",
			presaleSubtitle: "Accumulated cost and margin projection before sale",
			notSold: "This animal has not been sold yet.",
			revenue: "Revenue",
			acquisitionCost: "Acquisition cost",
			animalOperationCost: "Animal operation costs",
			herdOperationCostShare: "Herd cost share",
			deductions: "Sale deductions",
			netMargin: "Net margin",
			soldOn: "Sold on {{date}}",
			accumulatedCost: "Accumulated cost",
			referencePriceLabel: "Reference price (R$/@)",
			referencePriceInUse: "Reference price: {{price}}/@",
			simulateAnotherPriceLabel: "Simulate another price (R$/@)",
			staleQuote: "outdated",
			referencePricePlaceholder: "e.g. 320.00",
			simulate: "Simulate",
			useOrgPrice: "Back to the organization's quote",
			noProjection:
				"Set a reference price (above or in Financial settings) and record a weighing to project the margin.",
			noProjectionNeedsWeighing:
				"Record a weighing to project the margin at {{price}}/@.",
			projectionTitle: "Projected margin",
			simulationBadge: "simulation",
			projectionBasis:
				"Basis: {{weight}} kg (~{{arrobas}} @) at {{price}}/@ · weighed on {{date}}",
			projectedRevenue: "Projected revenue",
			projectedMargin: "Projected margin",
			// Unit economics (animal enrichment, phase 8): server-computed values.
			costPerKgGained: "Cost per kg gained",
			costPerDay: "Cost per day",
			breakEven: "Break-even price (@)",
			perDay: "{{value}}/day",
			nutrition: {
				feedingPlan: "Feeding plan: {{name}} since {{date}} ({{source}})",
				supplementation:
					"Supplementation: {{name}} since {{date}} ({{source}})",
				unnamed: "unnamed",
				source: {
					animal: "animal",
					herd: "herd",
				},
			},
			// Before any input exists (§2.6): states what is missing, not a zero.
			prerequisites: {
				title: "To project the margin, RuralOps needs:",
				weighing: "a weighing",
				recordWeighing: "Record weighing",
				referencePrice: "a reference price per arroba",
				simulateHint:
					"Already in place, from Financial settings. Simulate another price if you like.",
				showBreakdown: "Show breakdown",
				hideBreakdown: "Hide breakdown",
			},
			// Honesty facts — the backend decides WHICH ones appear (by code); the
			// wording lives here. See KeyDecisions/2026-09-03-margin-honesty-labels-as-codes.
			honesty: {
				PASTURE_COSTS_EXCLUDED: "pasture costs not included",
				LOT_COSTS_PER_HEAD: "herd costs split per head",
				CARCASS_YIELD:
					"arrobas produced estimated at {{percent}}% carcass yield",
				WEIGHED_COVERAGE:
					"weight gain computed over {{withData}}/{{total}} animals with weighings",
			},
		},
		growth: {
			tab: "Growth",
			title: "Growth",
			subtitle_one: "{{count}} weighing recorded",
			subtitle_other: "{{count}} weighings recorded",
			empty: {
				title: "No weighing yet",
				body: "The chart, daily gain, arrobas and time-to-target appear after the first weighing.",
				recordWeighing: "Record weighing",
			},
			latestWeight: "Latest weight",
			arrobas: "Arrobas ({{percent}}% yield)",
			estimatedCarcass: "Estimated carcass",
			chartTitle: "Weight over time",
			weightSeries: "Weight",
			targetLine: "Target {{value}} kg",
			windows: {
				window: "Window",
				gmd: "ADG",
				weights: "Weights",
				period: "Dates",
				lastTwoWeighings: "Last two weighings",
				rolling30: "Last 30 days",
				rolling90: "Last 90 days",
				lifetime: "Since first weighing",
				needTwo: "Daily gain appears after the second weighing.",
			},
			target: {
				title: "Herd target weight: {{value}} kg",
				reaches_one:
					"Reaches the target in {{count}} day, at {{basis}} kg/day, on",
				reaches_other:
					"Reaches the target in {{count}} days, at {{basis}} kg/day, on",
				noProjection:
					"No projection: the animal is already at target or has no recent weight gain to project from.",
				noTarget:
					"Set a target weight on the herd to see when this animal reaches it.",
			},
			lotRank: {
				title: "Position in the lot",
				byWeight: "By weight",
				byGmd: "By ADG",
				position: "Rank {{rank}} of {{size}}",
				percentile: "percentile {{value}}",
				cullTitle: "Cull candidate",
				cullBody:
					"Among the lightest 10% of the lot. Review health, nutrition or an early sale.",
			},
			weaning: {
				title: "Weaning",
				date: "Date",
				weight: "Weaning weight",
				age: "Age at weaning",
				ageValue_one: "{{count}} day",
				ageValue_other: "{{count}} days",
				adjusted205: "205-day adjusted weight",
			},
		},
		lotResult: {
			title: "Lot result",
			subtitle_one:
				"{{count}} head in this cycle, net of mortality (closed cycle)",
			subtitle_other:
				"{{count}} head in this cycle, net of mortality (closed cycle)",
			empty: "No animal has passed through this lot yet.",
			totalHead: "Total",
			soldHead: "Sold",
			deadHead: "Died",
			activeHead: "Active",
			proceeds: "Sale proceeds",
			acquisitionCost: "Acquisition cost",
			operationCost: "Operation costs",
			deductions: "Sale deductions",
			netResult: "Net result",
			kpis: {
				title: "Strategic indicators",
				kgGained: "Weight gained",
				arrobasProduced: "@ produced",
				revenuePerArroba: "Revenue per @ produced",
				costPerKgGained: "Cost per kg gained",
				breakEven: "Break-even price (@)",
				marginPercent: "Margin",
			},
		},
		kpi: {
			gmdShort: "ADG",
			gmd90: "ADG (90d)",
			gmdValue: "{{value}} kg/day",
			gmdUnit: "kg/day",
			coverage: "{{weighed}}/{{total}} weighed",
			onPaddockDays_one: "{{count}} day on paddock",
			onPaddockDays_other: "{{count}} days on paddock",
			kgValue: "{{value}} kg",
			arrobaValue: "{{value}} @",
			perArroba: "{{value}}/@",
			perKg: "{{value}}/kg",
			percentValue: "{{value}}%",
		},
		sales: {
			sellCta: "Sell",
			sellAnimalsCta: "Sell animals",
			step1Title: "Select animals",
			step1Description: "Choose the animals to include in this sale.",
			step2Title: "Sale details",
			step2Description:
				"Buyer, settlement type (on the hook, live weight, per head or lot) and amount.",
			step3Title: "Confirmation",
			step3Description: "Review before recording the sale.",
			completion: {
				title: "Sale recorded",
				description:
					"Flag the sale as credit if payment is due later, or finish.",
				done: "Done",
			},
			emptyHerd: "No active animals in this herd.",
			totalWeightEstimateHint:
				"Estimated from the last weighing — adjust if needed.",
			sections: {
				buyer: "Buyer",
				conditions: "Sale conditions",
				pricing: "Weight and price",
			},
			buyerLabel: "Buyer",
			saleDateLabel: "Sale date",
			channelLabel: "Channel",
			pricingModelLabel: "Pricing model",
			dealKind: {
				label: "How was the deal closed?",
				hint: "In Brazil the settlement comes before the price: on the hook at the packer, live weight between farms, per head, or a closed lot.",
				ON_HOOK: "On the hook (carcass)",
				ON_HOOKHint:
					"Packer sale. Price per carcass @, with yield or slaughter statement.",
				LIVE_WEIGHT: "Live weight",
				LIVE_WEIGHTHint:
					"Calves, backgrounding, or farm-to-farm. Price per live @ or per kg.",
				PER_HEAD: "Per head",
				PER_HEADHint: "The same amount per animal, without weighing the lot.",
				LOT_TOTAL: "Closed lot",
				LOT_TOTALHint: "One negotiated amount for the whole lot.",
			},
			livePriceUnit: {
				ARROBA: "R$ per live-weight @",
				KG: "R$ per live kg",
			},
			totalWeightLabel: "Total weight (kg)",
			liveWeightLabel: "Total live weight",
			carcassWeightLabel: "Slaughter-statement carcass weight",
			totalWeightPlaceholder: "e.g. 1500",
			liveWeightHint:
				"Latest recorded weights total {{weight}} kg. Adjust to the live weight actually negotiated.",
			actualCarcassHint:
				"Enter the net carcass weight reported by the slaughterhouse.",
			estimatedCarcassHint:
				"{{liveWeight}} kg live weight × {{yield}}% = {{carcassWeight}} kg estimated carcass.",
			carcassYieldLabel: "Carcass yield",
			carcassYieldHint:
				"Conversion from live weight to carcass (hide, viscera, head). This is not a commercial discount — 50–54% is typical in Brazil.",
			shrinkLabel: "Weight shrink",
			shrinkHint:
				"Transport or yard shrink on live weight. 2% is the usual figure; leave 0 if none.",
			carcassDiscountLabel: "Commercial carcass discount",
			carcassDiscountHint:
				"Extra percent some plants take after yield (1–2%). Condemned or graded weight belongs on the slaughter statement or as a later cost.",
			weightBasis: {
				label: "How was carcass weight obtained?",
				arrobaExplanation:
					"For fat cattle, 1 @ = 15 kg of carcass. A live-weight @ is a different deal, used between farms.",
				live: "Live weight",
				actual: "Actual slaughter-statement carcass",
				actualHint:
					"Use after slaughter, with weight reported by the processor.",
				estimated: "Carcass estimated from yield",
				estimatedHint:
					"Use when the agreement settles by estimate; live weight is converted by yield.",
			},
			settlement: {
				title: "Sale settlement",
				liveWeight: "Live weight",
				shrink: "Shrink {{percent}}%",
				yield: "Yield {{percent}}%",
				romaneio: "Slaughter-statement carcass",
				carcassDiscount: "Carcass discount {{percent}}%",
				billedLive: "Billed live weight",
				billedCarcass: "Billed carcass",
				liveArrobas: "Live-weight arrobas",
				carcassArrobas: "Carcass arrobas",
				pricePerKg: "Price per kg",
				pricePerLiveArroba: "Price per live @",
				pricePerCarcassArroba: "Price per carcass @",
				note: "Settlement: {{shrink}}% shrink; {{discount}}% carcass discount.",
			},
			notesLabel: "Notes",
			notesPlaceholder: "Optional notes",
			perAnimalWeightsLabel: "Per-animal weights (optional)",
			perAnimalLiveWeightsLabel: "Per-animal live weights (optional)",
			liveTotalLabel: "Total",
			unitLabels: {
				PER_HEAD: "Price per head (R$)",
				PER_KG: "Price per live kg (R$)",
				PER_ARROBA: "Price per @ (R$)",
				LOT_TOTAL: "Lot total (R$)",
				LIVE_ARROBA: "Price per live-weight @ (R$)",
				CARCASS_ARROBA: "Price per carcass @ (R$)",
			},
			pricingModels: {
				PER_HEAD: "Per head",
				PER_KG: "Per kg",
				PER_ARROBA: "Per arroba",
				LOT_TOTAL: "Lot total",
			},
			channels: {
				DIRECT: "Direct",
				AUCTION: "Auction",
				MARKETPLACE: "Marketplace",
				OTHER: "Other",
			},
			statuses: {
				COMPLETED: "Completed",
				CANCELED: "Canceled",
			},
			confirmSubmit: "Confirm sale",
			validation: {
				selectAnimals: "Select at least one animal",
				buyerRequired: "Enter the buyer",
				priceRequired: "Enter a price greater than 0",
				weightRequired: "Enter a total weight greater than 0",
				carcassYieldRequired: "Enter a carcass yield between 0 and 100%",
				percentRange: "Enter a percentage between 0 and 100",
			},
			createSuccess: "Sale recorded.",
			toastSuccess_one: "Sale recorded — {{count}} animal.",
			toastSuccess_other: "Sale recorded — {{count}} animals.",
			toastError: "Could not record the sale.",
			undoLabel: "Undo",
			reverseSuccess: "Sale reversed.",
			reverseError: "Could not reverse the sale.",
			reverseConfirmTitle: "Reverse sale?",
			reverseConfirmDescription:
				"Animals will return to ACTIVE status and the sale records will be removed. Allowed only within 24 hours of the sale.",
			reverseConfirmAction: "Reverse",
			summary: {
				headerTitle: "Sale summary",
				buyer: "Buyer",
				headCount: "Head count",
				pricingModel: "Pricing model",
				dealKind: "Settlement type",
				unitPrice: "Unit price",
				totalWeight: "Total weight",
				liveWeight: "Live weight",
				carcassWeight: "Carcass weight",
				weightBasis: "Weight basis",
				carcassYield: "Carcass yield",
				arrobas: "Carcass arrobas",
				arrobasLive: "Live-weight arrobas",
				saleDate: "Date",
				channel: "Channel",
				totalAmount: "Total",
				moneyDeductionsHint:
					"FUNRURAL, freight and commission are recorded next — they are cash costs, not a carcass discount.",
				willEmptyHerdTitle: "This will empty the herd",
				willEmptyHerdBody:
					"All active animals are being sold. The herd will be marked inactive and any paddock it currently grazes will be freed for other herds.",
			},
			// Withdrawal guard (2026-09-04 enrichment, phase 9).
			withdrawal: {
				title: "Animal in withdrawal",
				body: "This animal is in withdrawal for {{medication}} until {{date}}. Selling or slaughtering before then breaks the drug withdrawal period.",
				bodyNoMedication:
					"This animal is in withdrawal until {{date}}. Selling or slaughtering before then breaks the drug withdrawal period.",
				acknowledge: "I understand and want to record the sale anyway",
				rejected: "Sale rejected — animal in withdrawal: {{detail}}",
				rejectedItem: "{{tagId}} until {{date}}",
			},
			deductions: {
				title: "Commercialization costs",
				subtitle:
					"FUNRURAL, freight, commission and other sale costs. Gross revenue is kept intact; margin is computed net.",
				empty: "No deductions recorded.",
				amountPlaceholder: "Amount (R$)",
				descriptionPlaceholder: "Description (optional)",
				types: {
					FUNRURAL: "FUNRURAL",
					FREIGHT: "Freight",
					COMMISSION: "Commission",
					OTHER: "Other",
				},
			},
		},
		buyers: {
			placeholder: "Select buyer",
			searchPlaceholder: "Search by name or document",
			noResults: "No buyers found.",
			createNew: "+ Create new buyer",
			createNamed: '+ Create "{{name}}"',
			nameLabel: "Name",
			namePlaceholder: "e.g. John Smith",
			documentLabel: "Tax document",
			documentPlaceholder: "Optional",
			createSubmit: "Create",
			createSuccess: "Buyer created.",
		},
		tabs: {
			overview: "Overview",
			herds: "Herds",
			animals: "Animals",
			health: "Health",
			activity: "Activity",
			measurements: "Measurements",
			operations: "Operations",
		},
		farm: {
			selectFarm: "Select Farm",
			noFarms: "No Farms Available",
			noFarmsDescription:
				"You need to create a farm before managing livestock.",
		},
		dashboard: {
			title: "Livestock Dashboard",
			herdsOverview: "Herds Overview",
			kpi: {
				totalAnimals: "Total Animals",
				herds: "Herds",
				recentActivity: "Recent Activity",
				recentOperations: "Recent Operations",
				recentMeasurements: "Recent Measurements",
			},
			recentActivity: {
				title: "Recent Activity",
				viewAll: "View All",
				thisMonth: "this month",
				noActivity: "No recent activity",
			},
			herds: {
				title: "Herds",
				viewAll: "View All Herds",
				createHerd: "Create Herd",
				empty: "No herds yet. Create a herd to organize your animals.",
			},
			quickActions: {
				title: "Quick Actions",
				addHerd: "Add Herd",
				// One word — the phone toolbar has no room for "Add Herd".
				addHerdShort: "Herd",
				animals: "Animals",
				health: "Health",
			},
			sections: {
				herds: "Herds",
				herdsDesc: "Manage your livestock herds",
				animals: "Animals",
				animalsDesc: "View and manage individual animals",
				health: "Health Records",
				healthDesc: "Track health events and treatments",
				grazing: "Grazing",
				grazingDesc: "Manage grazing allocations",
				feeding: "Feeding",
				feedingDesc: "Manage feeding plans",
				reproduction: "Reproduction",
				reproductionDesc: "Track breeding and calving events",
			},
		},
		herd: {
			singular: "Herd",
			select: "Select herd",
			name: "Name",
			animals: "Animals",
			activeCount_one: "{{count}} active",
			activeCount_other: "{{count}} active",
			otherCount_one: "{{count}} other",
			otherCount_other: "{{count}} other",
			purpose: "Purpose",
			description: "Description",
			primaryBreed: "Primary Breed",
			managementSystem: "Management System",
			targetWeightKg: "Target Weight (kg)",
			maxCapacity: "Max Capacity",
			formationDate: "Formation Date",
			currentPaddock: "Current Paddock",
			unassigned: "Unassigned",
			rotateToPaddock: "Rotate",
			assignPaddock: "Assign paddock",
			rotateToPaddockShort: "Rotate",
			assignPaddockShort: "Assign",
			rotate: {
				dialog: {
					title: "Rotate to paddock",
					description:
						"Move this herd to a new paddock. This creates a grazing rotation operation.",
					descriptionFrom: "Currently grazing {{name}}. Pick the next paddock.",
				},
				fields: {
					paddock: "Target paddock",
					paddockPlaceholder: "Select a paddock",
					occupiedBy: "occupied by {{name}}",
					restedDays_one: "rested {{count}} day",
					restedDays_other: "rested {{count}} days",
				},
				samePaddockWarning: "Herd is already in this paddock",
				noPaddocks: "No paddocks in this farm yet",
				createPaddockLink: "Create one",
				detailsToggle: "+ Add planning details",
				endGrazing: "End grazing",
				endGrazingConfirm:
					"Mark this herd as off-pasture. The current rotation will be closed.",
				endGrazingConfirmButton: "Confirm end",
			},
			healthAlerts: "Health Alerts",
			details: "Details",
			totalAnimals: "Total Animals",
			healthEvents: "Health Events",
			feedingPlans: "Feeding Plans",
			tabs: {
				operations: "Operations",
				measurements: "Measurements",
				lotResult: "Lot result",
			},
			sections: {
				basicInfo: {
					title: "Basic Information",
					description: "Herd name and production purpose",
				},
				herdProfile: {
					title: "Herd Profile",
					description: "Breed composition and management approach",
				},
				managementGoals: {
					title: "Management Goals",
					description: "Production targets and capacity planning",
				},
				additionalDetails: {
					title: "Additional Details",
					description: "Notes and extra information",
				},
			},
			list: {
				title: "Herds",
				empty: {
					title: "No Herds",
					// Says what a herd is for and that a farm can start without one — it
					// does not promise any animal will be put in it (§2.7).
					description:
						"Herds group animals to handle together — weigh, vaccinate or move at once. You can start without them.",
					createButton: "Create herd",
				},
			},
			create: {
				title: "Create Herd",
			},
			edit: {
				title: "Edit Herd",
			},
			placeholders: {
				name: "Enter herd name",
				purpose: "Select purpose",
				description: "Enter herd description (optional)",
				primaryBreed: "Enter primary breed",
				targetWeightKg: "e.g. 480",
				maxCapacity: "e.g. 100",
			},
			purposes: {
				BREEDING: "Breeding",
				FATTENING: "Fattening",
				DAIRY: "Dairy",
				MIXED: "Mixed",
				REPLACEMENT: "Replacement",
			},
			purposeDescriptions: {
				BREEDING: "Reproduction and genetic improvement",
				FATTENING: "Weight gain for sale/slaughter",
				DAIRY: "Milk production",
				MIXED: "Multiple production goals",
				REPLACEMENT: "Young stock for future breeding",
			},
			managementSystems: {
				EXTENSIVE: "Extensive",
				SEMI_INTENSIVE: "Semi-intensive",
				INTENSIVE: "Intensive",
				CONFINEMENT: "Confinement",
			},
			managementSystemDescriptions: {
				EXTENSIVE: "Free-range grazing on large pastures",
				SEMI_INTENSIVE: "Grazing with supplemental feeding",
				INTENSIVE: "High-density managed grazing",
				CONFINEMENT: "Feedlot or barn-based system",
			},
			tooltips: {
				primaryBreed:
					"The predominant breed in this herd. Determines genetics, expected performance, and health protocols.",
				managementSystem:
					"How animals are raised and fed. Drives feeding, space, and cost planning.",
				targetWeightKg:
					"The target exit/slaughter weight for animals in this herd. Especially relevant for fattening herds.",
				maxCapacity:
					"Maximum number of animals planned for this herd. Helps prevent overcrowding and aids stocking rate calculations.",
				formationDate:
					"When this herd was assembled. Useful for tracking herd age and performance over time.",
			},
			quickBreeds: [
				"Nelore",
				"Angus",
				"Brahman",
				"Hereford",
				"Senepol",
				"Tabapuã",
				"Girolando",
				"Guzerá",
			],
			form: {
				nameLabel: "Herd name",
				required: "required",
				namePlaceholder: "Cows — Retiro Alto",
				nameError:
					"A herd needs a name — that is the only thing required here.",
				purposeTitle: "What this herd is for",
				purposeSub:
					"Sets which numbers RuralOps tracks for it. You can change it later.",
				purposeDesc: {
					BREEDING:
						"Reproduction and genetic improvement. RuralOps will track calving, services and pregnancy checks.",
					FATTENING:
						"Weight gain for sale or slaughter. RuralOps will track daily gain and days to target weight.",
					DAIRY:
						"Milk production. RuralOps will track yield per cow and lactation stage.",
					MIXED:
						"More than one goal in the same herd. RuralOps tracks both breeding and weight numbers.",
					REPLACEMENT:
						"Young stock kept for future breeding. RuralOps will track growth against entry weight for service.",
				},
				breedTitle: "Predominant breed",
				breedSub: "Tap one, or type a breed that is not listed.",
				otherBreed: "+ Other breed",
				otherBreedPlaceholder: "Breed name",
				systemTitle: "How it is raised",
				systemSub: "Drives feed, space and cost planning.",
				systemDesc: {
					EXTENSIVE: "Free-range grazing on large pastures.",
					SEMI_INTENSIVE: "Grazing with supplemental feeding.",
					INTENSIVE: "High-density managed grazing.",
					CONFINEMENT: "Feedlot or barn-based system.",
				},
				systemNone:
					"Not set. The herd works without it — feed and cost planning stay off until you pick one.",
				planningTitle: "Planning details",
				planningOptional: "optional",
				planningSub:
					"Add what you already know. Each one is used for a specific calculation, named below the field.",
				remove: "remove",
				units: {
					head: "head",
					kg: "kg",
				},
				details: {
					capacity: {
						label: "Maximum capacity",
						help: "Used to warn you before the herd outgrows its paddocks.",
					},
					formed: {
						label: "Formation date",
						help: "Used to report herd age and performance over time.",
					},
					notes: {
						label: "Notes",
						help: "Free text. Shows on the herd record, not in reports.",
						placeholder: "Anything the next person should know about this herd",
					},
					targetWeight: {
						label: "Target weight",
						help: "The exit weight RuralOps counts down to — days to target weight for a fattening herd.",
					},
				},
				statusReady: "Ready to create",
				upToHead: "up to {{count}} head",
				create: "Create herd",
			},
			saved: {
				title: "{{name}} created",
				body: "The herd is empty until animals are in it. Move animals you already registered, or register new ones straight into this herd.",
				addAnimals: "Add animals to this herd",
				createAnother: "Create another herd",
				undo: "Undo",
				missingTitle: "Fill in when you know",
				missingCaption_one:
					"{{count}} detail left out. None of them block anything today.",
				missingCaption_other:
					"{{count}} details left out. None of them block anything today.",
			},
			animalsSection: {
				title: "Herd Animals",
				description: "Manage animals assigned to this herd",
				searchPlaceholder: "Search animal to add...",
				addAnimal: "Add",
				empty:
					"No animals in this herd yet. Use the search above to add animals.",
				removeConfirm: "Remove this animal from the herd?",
				count: "{{count}} animal(s) in this herd",
				noUnassigned: "No unassigned animals available",
			},
			animalsList: {
				toggleShow: "Show animals",
				toggleHide: "Hide animals",
				empty: "No animals in this herd yet.",
			},
			/**
			 * Herd detail follows the animal-detail arrangement: identity rail
			 * + stacked cards on desktop, identity bar + numbers-first on a phone.
			 */
			detail: {
				weight: {
					title: "Weight",
					weigh: "Weigh",
					average: "Lot average",
					noWeighing: "No weighing yet",
					emptyHint:
						"Average weight and daily gain appear after the first weighings.",
					gmd90: "ADG (90 days)",
					gmd30: "ADG (30 days)",
					gmdTitle: "Average daily gain",
					target: "Target",
					targetUnset: "No target set",
					toTarget: "To target",
					percentOfTarget: "{{percent}}% of target",
					thinCoverage: "Thin coverage — {{weighed}} of {{total}} weighed",
					head: "Head",
				},
				attention: {
					title: "Attention",
					nothingPending: "Nothing pending",
					openItems_one: "{{count}} open item",
					openItems_other: "{{count}} open items",
				},
				animals: {
					title: "Animals",
					empty: "No animals in this herd yet.",
					caption_one: "{{count}} animal in this lot",
					caption_other: "{{count}} animals in this lot",
				},
				records: {
					title: "Records",
					hint: "This herd's weighings, vaccinations, treatments and paddock moves will show up here.",
				},
				details: {
					title: "Details",
					add: "Add",
					purpose: "Purpose",
					paddock: "Paddock",
					animals: "Animals",
					breed: "Breed",
					system: "System",
					target: "Target",
					capacity: "Capacity",
					formed: "Formed",
					registered: "Registered",
					paddockNotRecorded: "Unassigned",
					notRecorded: "Not recorded",
					capacityValue: "{{count}} head",
					fieldsFilled: "{{filled}} of {{total}} fields filled.",
					fieldsFilledShort: "{{filled}} of {{total}} filled",
					completeDetails: "Complete details",
				},
				lotResult: {
					open: "Open lot result",
					empty: "No result yet",
				},
				more: "More actions",
				delete: "Delete herd",
			},
			confirmDelete: "Are you sure you want to delete this herd?",
			notFound: "Herd not found",
			deleteConfirm: {
				title: "Delete lot {{name}}?",
				body: "This can't be undone.",
				blocked:
					"This lot has {{count}} animals. Remove or transfer them before deleting.",
			},
			actionsComingSoon: "More actions coming soon...",
			createSuccess: "Herd created successfully",
			createError: "Failed to create herd",
			updateSuccess: "Herd updated successfully",
			updateError: "Failed to update herd",
			deleteSuccess: "Herd deleted successfully",
			deleteError: "Failed to delete herd",
		},
		animal: {
			singular: "Animal",
			select: "Select animal",
			tagId: "Tag ID",
			tagIdFormLabel: "Ear Tag Number",
			electronicId: "Electronic ID",
			registrationNumber: "Registration Number",
			sex: "Sex",
			breed: "Breed",
			coatColor: "Coat Color",
			herd: "Herd",
			status: "Status",
			birthDate: "Birth Date",
			birthWeight: "Birth Weight",
			originType: "Origin",
			acquisitionDate: "Acquisition Date",
			acquisitionCost: "Acquisition Cost",
			supplier: "Supplier",
			sire: "Sire (Father)",
			dam: "Dam (Mother)",
			sireTagId: "External Sire Tag",
			damTagId: "External Dam Tag",
			hornStatus: "Horn Status",
			isCastrated: "Castrated",
			notes: "Handling notes",
			temperament: "Temperament",
			temperaments: {
				DOCILE: "Docile",
				NORMAL: "Normal",
				NERVOUS: "Nervous",
				AGGRESSIVE: "Aggressive",
			},
			gtaNumber: "GTA number",
			invoiceNumber: "Invoice number",
			originFarmName: "Farm of origin",
			// Animal profile (2026-09-04 plan, phase 7): category, age,
			// identification, provenance, paddock, death.
			categories: {
				BEZERRO: "Bull calf",
				BEZERRA: "Heifer calf",
				GARROTE: "Young steer",
				NOVILHA: "Heifer",
				VACA: "Cow",
				TOURO: "Bull",
				BOI_MAGRO: "Feeder steer",
				BOI_GORDO: "Finished steer",
			},
			castration: {
				yes: "Yes",
				no: "No (intact)",
			},
			daysOnFarm: "Time on farm",
			daysOnFarmValue_one: "on the farm for {{count}} day",
			daysOnFarmValue_other: "on the farm for {{count}} days",
			currentPaddock: "Current paddock",
			paddockDays_one: "for {{count}} day",
			paddockDays_other: "for {{count}} days",
			noPaddock: "No paddock recorded",
			death: {
				title: "Death",
				date: "Date of death",
				cause: "Cause",
				causeUnknown: "Cause not recorded",
			},
			latestWeight: "Latest Weight",
			notWeighedYet: "Not weighed yet",
			noHerd: "No herd",
			moveHerd: "Move",
			adg: "Average daily gain",
			estimatedCarcassBasis: "{{percent}}% of {{weight}} kg",
			targetWeight: "Target weight",
			targetWeightUnset: "Not set",
			targetMet: "Met",
			targetNotMet: "Not met",
			setTarget: "Set",
			setTargetTitle: "Herd target weight",
			setTargetDescription: "Applies to every animal in {{herd}}.",
			setTargetSave: "Save target",
			acquisitionCostNotApplicable: "Not applicable — born on the farm",
			acquisitionCostUnset: "Not recorded",
			// Day one (§2.4, revised 2026-09-04): the empty profile stated the same
			// missing fact in six places and attached the remedy to none of them.
			// Everything absent is downstream of ONE weighing — say it once.
			dayOne: {
				unlockTitle: "Weigh {{tagId}} to unlock gain, margin and alerts",
				unlockBody:
					"It's the one record missing — the rest of the profile builds from it.",
				addPhoto: "Add photo",
				assignHerd: "Put in a herd",
				createHerd: "Create the first herd",
				profileIncomplete: "Birth date, origin and parentage not recorded yet.",
				completeProfile: "Complete profile",
			},
			/**
			 * Animal detail redesign (Claude Design handoff 1b/2a/3a/3b): identity
			 * rail + stacked cards, no tabs.
			 */
			detail: {
				weight: {
					title: "Weight",
					add: "Weight",
					overTime: "Weight over time",
					lastOn: "Last {{date}}",
					herdTarget: "Herd target",
					herdTargetUnset: "Herd target · set by herd",
					toTarget: "To target",
					estimatedCarcass: "Est. carcass ({{percent}}%)",
					percentOfTarget: "{{percent}}% of target",
					yieldNote:
						"@ at {{percent}}% carcass yield (1 @ = {{kgPerArroba}} kg carcass)",
					rankInLot: "Rank {{rank}} of {{size}} in lot",
					chartTarget: "Target {{kg}} kg · {{arrobas}} @",
					noWeighing: "No weighing yet",
					emptyHint:
						"Arrobas, target and daily gain appear after the first weighings.",
					chartEmpty: "The curve starts with the first weighing.",
					gmdTitle: "Average daily gain",
					gmdLifetime: "Since first weighing",
					gmdLastTwo: "Last two weighings",
				},
				attention: {
					title: "Attention",
					nothingPending: "Nothing pending",
					next: "next: {{label}}, {{date}}",
					scheduleFirstWeighing: "Schedule first weighing",
					openItems_one: "{{count}} open item",
					openItems_other: "{{count}} open items",
				},
				health: {
					title: "Health",
					noWithdrawal: "No withdrawal",
					noTreatment: "no active treatment",
					noVaccines: "no vaccines recorded",
					withdrawalUntil: "Withdrawal until {{date}}",
					treatments_one: "{{count}} recent treatment",
					treatments_other: "{{count}} recent treatments",
					openDiseases_one: "{{count}} open condition",
					openDiseases_other: "{{count}} open conditions",
					vaccinesOverdue_one: "{{count}} vaccine overdue",
					vaccinesOverdue_other: "{{count}} vaccines overdue",
					vaccinesUpToDate: "vaccines up to date",
					vaccinesWindowMissed_one: "{{count}} vaccine window closed",
					vaccinesWindowMissed_other: "{{count}} vaccine windows closed",
					showDetail: "Sanitary detail",
					hideDetail: "Hide sanitary detail",
				},
				vaccination: {
					record: "Vaccine",
					recordShort: "Vaccine",
					columns: {
						vaccine: "Vaccine",
						status: "Status",
						lastApplication: "Last application",
						nextDue: "Next due",
					},
					lastAndDue: "Last {{last}} · due {{due}}",
					lastOnly: "Last {{last}}",
					dueOnly: "Due {{due}}",
					noApplication: "No application recorded",
					footerNote:
						"Mandatory vaccines for this region are listed. Record the last application to start tracking.",
				},
				records: {
					title: "Records",
					hint: "This animal's weighings, vaccinations, treatments and herd moves will show up here.",
				},
				details: {
					title: "Details",
					add: "Add",
					age: "Age",
					paddock: "Paddock",
					birth: "Birth",
					coat: "Coat",
					horns: "Horns",
					acquisition: "Acquisition",
					acquisitionNotApplicable: "Not applicable",
					onFarmValue_one: "{{count}} day",
					onFarmValue_other: "{{count}} days",
					registered: "Registered",
					onFarm: "On farm",
					paddockNotRecorded: "Not recorded",
					sexCastrated: "{{sex}}, castrated",
					fieldsFilled: "{{filled}} of {{total}} fields filled.",
					fieldsFilledShort: "{{filled}} of {{total}} filled",
					completeDetails: "Complete details",
				},
				margin: {
					noCost: "No acquisition cost. Add costs to see margin.",
					addCost: "Add acquisition cost to track margin.",
					open: "Open margin",
					projected: "Projected {{value}} at {{price}}/@",
					realized: "Net margin {{value}}",
					needsWeighing: "Record a weighing to project the margin.",
				},
				images: {
					title: "Images · {{count}}",
					none: "None yet.",
				},
				suggestion: {
					source: "RuralOps assistant",
					title: "Complete {{tagId}} with a weighing and a herd",
					body: "With a first weight and a herd, the page shows target, arrobas and position in the lot. Breed and birth date can wait.",
					evidence: "Only ID and sex recorded",
					approve: "Record weighing",
					dismiss: "Not now",
				},
				quickAdd: {
					weighing: "Weighing",
					moveToHerd: "Move to herd",
					vaccine: "Vaccine",
					photo: "Photo",
				},
				badges: {
					registeredToday: "Registered today",
				},
				categoryGuide: {
					title: "How the category is defined",
					intro:
						"RuralOps assigns this from the animal's sex, age, castration and latest body condition. It is not a field you set.",
					open: "How {{category}} and other categories are defined",
					thisAnimal: "This animal",
					bandCalf: "Under 12 months",
					bandYoung: "12 to 23 months",
					bandAdult: "24 months or older",
					definitions: {
						BEZERRO: "Male.",
						BEZERRA: "Female.",
						GARROTE: "Male.",
						NOVILHA: "Female.",
						VACA: "Female.",
						TOURO: "Intact male.",
						BOI_MAGRO:
							"Castrated male, still lean (typically BCS 5 or below). Also used when castration is unknown.",
						BOI_GORDO:
							"Castrated male with slaughter fat cover (BCS 7 or above).",
					},
					finishingTitle: "Feeder steer vs finished steer",
					finishingBody:
						"The difference is body condition on the 1–9 scale RuralOps already records (1 = emaciated, 5 = moderate, 9 = obese). A feeder steer is still lean — typically 5 or below. A finished steer has slaughter fat cover — typically 7 or above. Recording a score of 7 or more upgrades this badge to Finished steer.",
					noBirthDate: "Without a birth date, no category is shown.",
				},
				more: "More actions",
				delete: "Delete animal",
			},
			ageMonths: "Age (months)",
			details: "Details",
			weightRecords: "Weight Records",
			healthEvents: "Health Events",
			reproductionEvents: "Reproduction Events",
			plural: "animals",
			sections: {
				identification: {
					title: "Identification",
					description: "Primary identification and registration data",
				},
				classification: {
					title: "Classification",
					description: "Sex, breed, and physical appearance",
				},
				birthAndOrigin: {
					title: "Birth & Origin",
					description: "Birth details and acquisition information",
				},
				parentage: {
					title: "Parentage",
					description: "Sire and dam information",
				},
				physicalCharacteristics: {
					title: "Physical Characteristics",
					description: "Horn status and other physical traits",
				},
				assignment: {
					title: "Assignment",
					description: "Herd assignment and status",
				},
			},
			tooltips: {
				electronicId:
					"RFID/EID transponder number used for electronic identification",
				registrationNumber: "Official breed registry or pedigree number",
				birthWeight: "Weight at birth in kilograms",
				hornStatus: "Current horn condition of the animal",
				isCastrated: "Whether the male animal has been castrated",
			},
			parentageMode: {
				systemAnimal: "Select from system",
				externalTag: "Enter external tag",
			},
			calvingPrompt: {
				title: "Also record this calving?",
				description:
					"The dam is on this farm, so we can log a calving on her and register this animal as her calf — parentage and a birth record are filled in automatically.",
				birthDateRequired: "Set the birth date — it's the calving date.",
			},
			// Quick registration (design handoff "Cadastro de animal"): tag, sex
			// and birth date are required; every other detail is opt-in via a pill.
			quick: {
				required: "required",
				nextFree: "next free: {{tag}}",
				tagFree: "Free — no animal with this tag.",
				tagTaken: "Tag {{tag}} is already in the herd.",
				tagTakenInHerd: "Tag {{tag}} is already in the herd, in {{herd}}.",
				seeAnimal: "See that animal",
				detailsTitle: "Details (optional)",
				detailsHint: "Tap what you have now. The rest can wait.",
				remove: "remove",
				missing: "Missing {{fields}}",
				missingJoin: " and ",
				missingTag: "tag number",
				missingSex: "sex",
				missingBirthDate: "birth date",
				fixTag: "Fix the tag to continue",
				ready: "Ready to create",
				editNote: "Changes are added to the animal's history",
				createAndNext: "Create and next",
				createAnimal: "Create animal",
				saveChanges: "Save changes",
			},
			created: {
				title: "Animal {{tag}} created",
				noDetails: "No details yet",
				next: "Register next",
				viewRecord: "View record",
				undo: "Undo",
				repeat: "Repeat breed, herd and origin on the next one",
				sessionTitle: "Registered now",
				count_one: "{{count}} animal",
				count_other: "{{count}} animals",
				queued: "No signal — it will upload on its own when you reconnect.",
			},
			quickBreeds: [
				"Nelore",
				"Angus",
				"Brahman",
				"Girolando",
				"Gir",
				"Guzerá",
				"Tabapuã",
				"Senepol",
				"Hereford",
				"Charolais",
			],
			quickCoatColors: [
				"White",
				"Black",
				"Red",
				"Yellow",
				"Bay",
				"Gray",
				"Spotted",
				"Brindle",
			],
			tabs: {
				operations: "Operations",
				measurements: "Measurements",
			},
			list: {
				title: "Animals",
				empty: {
					title: "No Animals",
					description:
						"Create your first animal to start tracking your livestock.",
				},
				// Category derived from age and sex — the domain stores no such
				// field, it is read from what the row already carries.
				category: {
					calf: "Calf",
					breeder: "Breeder",
					fattening: "Fattening",
				},
				// Short attention labels for the phone card; the full category name
				// stays the accessible label.
				flags: {
					vaccination: "Vaccine",
					weighing: "No weighing",
					calving: "Calving soon",
				},
				noWeighing: "no weighing",
				weighedDaysAgo_one: "{{count}} day ago",
				weighedDaysAgo_other: "{{count}} days ago",
				lotOverCapacity: "Herd over capacity",
				selectRow: "Select animal {{tag}}",
				resultLine: "{{shown}} of {{total}} animals",
				showMore_one: "Show {{count}} more animal",
				showMore_other: "Show {{count}} more animals",
				showAll: "Show all {{count}} animals",
				// Short labels for the table columns and the sort sheet — in a column
				// header, "Tag number" and "Age (months)" only spend width.
				columns: {
					tagId: "Tag",
					sex: "Sex",
					breed: "Breed",
					herd: "Herd",
					latestWeight: "Latest weight",
					age: "Age",
					establishment: "Farm",
					status: "Status",
				},
			},
			filters: {
				searchPlaceholder: "Search by tag or breed...",
				allStatuses: "All",
				count: "{{count}} of {{total}}",
				noResults: "No animals match your filters",
				noResultsHint: "Try adjusting or clearing your filters.",
				clear: "Clear filters",
				advanced: {
					button: "Filters",
					title: "Advanced filters",
					description:
						"Combine sex, breed, herd, age, dates and records to define a cohort of animals.",
					apply: "Apply",
					clearAll: "Clear all",
					breedPlaceholder: "Select breeds",
					herdPlaceholder: "Select herds",
					selectedCount: "{{count}} selected",
					age: "Age",
					agePresets: {
						"0-12": "0 to 12 months",
						"12-24": "12 to 24 months",
						"24-36": "24 to 36 months",
						"36+": "Over 36 months",
					},
					acquisition: "Acquisition date",
					clearRange: "Clear range",
					onlineOnly: "Available online only.",
				},
				records: {
					title: "Records",
					empty: "No record conditions added.",
					add: "Add condition",
					remove: "Remove condition",
					more: "More types...",
					has: "Has",
					hasNot: "Doesn't have",
					from: "From",
					to: "To",
					anyDate: "Any date",
					presets: {
						last30: "Last 30 days",
						last90: "Last 90 days",
						last365: "Last 12 months",
					},
				},
			},
			managementViews: {
				label: "Management view",
				general: "General",
				cowCalf: "Cow-calf",
				stocking: "Stocking",
				fattening: "Fattening",
			},
			managementColumns: {
				tagHerd: "Tag / Herd",
				categoryBreed: "Category / Breed",
				acquisitionValueDate: "Acquisition value / date",
				statusFarm: "Status / Farm",
				ageSex: "Age / Sex",
				dam: "Dam",
				calf: "Latest calf / calving",
				pregnancy: "Pregnancy",
				expectedCalving: "Expected calving",
				sire: "Bull / AI",
				acquisitionDate: "Arrival",
			},
			managementColumnHelp: {
				label: "About {{column}}",
				calf: "Shows the tag of the latest calf linked to the dam and the last calving date. Open the dam and record a Calving to create and link the calf automatically. A calf registered with this dam as its mother also appears here.",
				pregnancy:
					"Shows the latest pregnancy diagnosis result. Open the dam, choose Record, and add a Pregnancy diagnosis. After calving, the status returns to Unknown until a new diagnosis.",
				expectedCalving:
					"Expected date for a cow diagnosed pregnant. Record AI or Natural breeding before the diagnosis to calculate it; without breeding, enter the estimated gestation age in the diagnosis. Without either, the date stays empty.",
				sire: "Bull tag or semen bull identifier for the current pregnancy. Record Natural breeding with the bull tag, or AI with the semen bull identifier, before a positive Pregnancy diagnosis. It appears only for a confirmed pregnancy.",
			},
			quickFilters: {
				rowLabel: "Quick filters",
				notVaccinated12m: "Not vaccinated (12 months)",
				noRecentWeighing90d: "No recent weighing (90 days)",
				femalesNoPregnancyCheck12m:
					"Females without pregnancy check (12 months)",
				recentCalvings30d: "Recent calvings (30 days)",
				calves: "Calves (up to 12 months)",
				toWean: "To wean",
				recentTreatment30d: "Recent treatment (30 days)",
				recentlyAcquired60d: "Recently acquired (60 days)",
				offlineUnavailable: "Unavailable offline",
				// Phone-width chip labels. Same cohort, field shorthand for the
				// window (12m / 90d) — never a shorter *claim* than the full label
				// above, which stays the accessible name at every width.
				short: {
					notVaccinated12m: "Not vaccinated 12m",
					noRecentWeighing90d: "No weighing 90d",
					femalesNoPregnancyCheck12m: "Females, no preg 12m",
					recentCalvings30d: "Calvings 30d",
					calves: "Calves ≤12m",
					toWean: "To wean",
					recentTreatment30d: "Treated 30d",
					recentlyAcquired60d: "Acquired 60d",
				},
			},
			create: {
				title: "Create Animal",
				button: "Add Animal",
				// One word — the phone toolbar has no room for "Add Animal" beside
				// "Import".
				buttonShort: "Animal",
			},
			saveAndAddAnother: "Save & add another",
			batchAdded_one: "{{count}} animal added this session",
			batchAdded_other: "{{count}} animals added this session",
			requiredHint: "Required",
			draft: {
				title: "Unsaved draft found",
				description: "You started adding an animal earlier on this farm.",
				restore: "Restore",
				discard: "Discard",
			},
			age: {
				label: "Age",
				newborn: "Newborn",
				year_one: "{{count}} year",
				year_other: "{{count}} years",
				month_one: "{{count}} month",
				month_other: "{{count}} months",
				monthShort: "{{count}} mo",
			},
			validation: {
				sexRequired: "Choose the animal's sex",
				tagIdRequired: "Tag ID is required",
				tagIdDuplicate: "An animal with this tag already exists",
				tagIdFormat: "Use only letters, numbers, dashes and spaces",
				birthDateRequired: "Birth date is required",
				birthDateFuture: "Birth date can't be in the future",
				birthWeightRange: "Enter a birth weight between 0 and 100 kg",
				costNegative: "Cost can't be negative",
				acquisitionBeforeBirth:
					"Acquisition date can't be before the birth date",
				acquisitionFuture: "Acquisition date can't be in the future",
			},
			edit: {
				title: "Edit Animal",
			},
			placeholders: {
				// Deliberately short: the tag field is a 26px display number, and a
				// long example overflows the line on a phone.
				tagId: "042",
				electronicId: "Enter RFID/EID number",
				registrationNumber: "Enter registry number",
				sex: "Select sex",
				breed: "Enter breed (e.g., Angus)",
				coatColor: "Enter coat color (e.g., Black)",
				herd: "Select herd (optional)",
				originType: "Select origin",
				birthWeight: "Weight in kg",
				acquisitionCost: "Cost",
				supplier: "Supplier name",
				sire: "Select sire",
				dam: "Select dam",
				sireTagId: "Enter sire tag ID",
				damTagId: "Enter dam tag ID",
				notes: "E.g. careful in the chute, calm with the handler",
				gtaNumber: "Entry GTA (animal transit guide) number",
				invoiceNumber: "Purchase invoice number",
				originFarmName: "Name of the farm of origin",
			},
			sexes: {
				MALE: "Male",
				FEMALE: "Female",
			},
			statuses: {
				ACTIVE: "Active",
				SOLD: "Sold",
				DECEASED: "Dead",
				TRANSFERRED: "Transferred",
			},
			origins: {
				born_on_farm: "Born on Farm",
				purchased: "Purchased",
			},
			hornStatuses: {
				HORNED: "Horned",
				POLLED: "Polled",
				DEHORNED: "Dehorned",
				SCURRED: "Scurred",
			},
			confirmDelete: "Are you sure you want to delete this animal?",
			deleteConfirm: {
				title: "Delete animal {{name}}?",
				body: "This can't be undone. Records attached to this animal will be removed.",
			},
			notFound: "Animal not found",
			remove: "Remove",
			addResult: {
				success_one: "{{count}} animal added to the lot",
				success_other: "{{count}} animals added to the lot",
				partial: "{{success}} added, {{failed}} failed. Try the rest again.",
				allFailed: "Couldn't add the animals. Try again.",
			},
			bulk: {
				select: "Select",
				cancelSelect: "Cancel selection",
				actions: "Actions",
				moveToHerd: "Move to herd",
				vaccinate: "Record vaccination",
				weigh: "Weigh (session)",
				exportCsv: "Export CSV",
				selectNeedsFarm:
					"Multi-select is only available with a farm selected. Pick a farm in the selector above.",
				capExceeded: "Bulk actions are limited to {{max}} animals per run.",
				mixedFarms: "Select animals from a single farm to run bulk actions.",
				moveTitle: "Move to herd",
				moveDescription_one:
					"The selected animal will be moved to the chosen herd.",
				moveDescription_other:
					"The {{count}} selected animals will be moved to the chosen herd.",
				moveCta_one: "Move {{count}} animal",
				moveCta_other: "Move {{count}} animals",
				moveSuccess_one: "{{count}} animal moved",
				moveSuccess_other: "{{count}} animals moved",
				partialResult:
					"{{success}} done, {{failed}} failed. Review the list and retry.",
				allFailed: "Nothing was processed. Check the errors and try again.",
				failuresTitle: "Failed items",
				herdPaddock_one: "{{paddock}} · for {{count}} day",
				herdPaddock_other: "{{paddock}} · for {{count}} days",
				csvExported: "CSV exported",
				csvWeightHeader: "Latest weight (kg)",
				delete: "Delete",
				deleteTitle_one: "Delete 1 animal?",
				deleteTitle_other: "Delete {{count}} animals?",
				deleteBody:
					"The weighing, vaccination and movement history of these animals leaves the lists and the reports. You can record an exit instead of deleting.",
				deleteCancel: "Not now",
				// The word that arms the delete button — a verb, not a tag.
				deleteWord: "Delete",
				deleteConfirm_one: "Delete animal",
				deleteConfirm_other: "Delete {{count}} animals",
				deleteSuccess_one: "{{count}} animal deleted",
				deleteSuccess_other: "{{count}} animals deleted",
			},
			viewFullProfile: "View Full Profile",
			addToHerd: "Add Animal",
			addToHerdDescription: "Select unassigned animals to add to this herd.",
			addCount: "Add {{count}} animal(s)",
			noUnassigned: "No unassigned animals available",
			transferToHerd: "Transfer to Herd",
			transferToHerdDescription: "Select the herd to transfer {{tagId}} to.",
			removeFromHerd: "Remove from Herd",
			confirmRemoveFromHerd: "Remove {{tagId}} from this herd?",
			noOtherHerds: "No other herds available",
			transfer: "Transfer",
			createSuccess: "Animal created successfully",
			createError: "Failed to create animal",
			updateSuccess: "Animal updated successfully",
			updateError: "Failed to update animal",
			deleteSuccess: "Animal deleted successfully",
			deleteError: "Failed to delete animal",
		},
		// Pedigree (2026-09-04 plan, phase 7): two generations; off-farm
		// ancestors shown by tag only.
		pedigree: {
			title: "Pedigree",
			subtitle:
				"Parents and grandparents; off-farm animals show their tag only.",
			sire: "Sire",
			dam: "Dam",
			sireSire: "Paternal grandsire",
			sireDam: "Paternal granddam",
			damSire: "Maternal grandsire",
			damDam: "Maternal granddam",
			unknown: "Unknown",
		},
		offspring: {
			title: "Offspring",
			subtitle_one:
				"{{count}} offspring recorded with this animal as sire or dam.",
			subtitle_other:
				"{{count}} offspring recorded with this animal as sire or dam.",
			columns: {
				tag: "Tag",
				sex: "Sex",
				birthDate: "Born",
				status: "Status",
				otherParent: "Other parent",
				weaningWeight: "Weaning weight",
				weaningDate: "Weaned",
				latestWeight: "Latest weight",
				gmd90: "ADG 90d",
			},
			empty: "No offspring found.",
		},
		animalImport: {
			commitStopped:
				"The import was stopped: some animals need review. Check the list and try again.",
			noAnimalsTitle: "No individual animals in this file",
			noAnimalsBody:
				"Every line read was a total or a summary. Send a list with one line per animal.",
			pageTitle: "Import animals",
			eyebrow: "Herd",
			pageSubtitle:
				"Send the list the way you already keep it. Nothing is saved to your herd until you confirm.",
			importAction: "Import animals",
			importActionShort: "Import",
			emptyStateHint:
				"Already have your herd in a spreadsheet, a report or a notebook? Import it instead of typing each animal.",
			steps: {
				upload: "Upload",
				read: "Read",
				match: "Match",
				check: "Check",
			},
			volume: {
				title: "How many animals fit in one import",
				spreadsheet:
					"A spreadsheet (.xlsx or .csv) brings in up to {{max}} animals per import.",
				visual:
					"A PDF or a photo is read in a single pass, so it is limited to {{visualMax}} animals. The {{max}}-animal limit is a spreadsheet limit for now — a longer list has to come in as a spreadsheet, or be split into more than one file.",
				splitFile:
					"This PDF or photo has more animals than one pass can read ({{visualMax}} maximum). Split it into smaller files, or export the list as a spreadsheet to bring up to {{max}} animals at once.",
				spreadsheetTooManyRows:
					"This file has {{rows}} rows and one import takes up to {{max}} animals. Split it into smaller files and import them one after the other — nothing is dropped that way.",
			},
			upload: {
				title: "Send your animal list",
				helper:
					"Spreadsheet, PDF or a photo of the paper list. The assistant reads each animal and you check it before anything is saved.",
				dropTitle: "Drop your file here",
				dropTypes: ".xlsx · .csv · PDF · JPEG · PNG",
				chooseFile: "Choose a file",
				takePhoto: "Take a photo",
				readingFile: "Reading the file...",
				removeFile: "Remove",
				fileMetaSheet_one:
					"{{size}} · {{count}} row · {{columns}} columns found",
				fileMetaSheet_other:
					"{{size}} · {{count}} rows · {{columns}} columns found",
				fileMetaPhoto: "{{size}} · photo · read in one pass",
				fileMetaPdf: "{{size}} · PDF · read in one pass",
				submit_one: "Read {{count}} animal",
				submit_other: "Read {{count}} animals",
				submitPhoto: "Read this photo",
				submitPdf: "Read this PDF",
				limitsLine:
					"Up to {{max}} animals per spreadsheet · up to {{visualMax}} per PDF or photo",
				showLimits: "What can I upload?",
				hideLimits: "Hide file limits",
				limitSheet:
					"Spreadsheet up to {{sheetMax}}, {{max}} animals per import.",
				limitVisual:
					"PDF or photo up to {{mediaMax}}, {{visualMax}} animals per import — they are read in one pass.",
				limitSplit:
					"A longer list can come as a spreadsheet or be split into more than one file.",
				unsupportedType:
					"Unsupported file type. Send a spreadsheet (.xlsx, .csv), a PDF, or a photo (JPEG, PNG, WebP).",
				tooLarge: "This file is too large. The limit is {{max}}.",
				emptyFile:
					"This file has no rows to read. Check that the list is really in it and send it again.",
				unreadableFile:
					"This file could not be read. Save it again as .xlsx or .csv and send it once more.",
			},
			sheetPicker: {
				label: "Which sheet holds the animals?",
				placeholder: "Select a sheet",
				helper:
					"This workbook has more than one sheet. Pick the one with the animal list so the import does not read the wrong tab.",
				sheetRows_one: "{{count}} row",
				sheetRows_other: "{{count}} rows",
				emptySheet: "This sheet has no rows.",
			},
			expectedRowCount: {
				label: "How many animals should this file have?",
				optional: "Optional",
				placeholder: "e.g. 120",
				helper:
					"Only for PDF and photo. Fill it in and we compare it against what the AI read, so a missing animal is flagged instead of passing unnoticed.",
				blankNote:
					"Left blank — the import still runs, but we cannot tell you whether an animal went missing.",
				invalid: "Enter a whole number of animals, or leave it blank.",
				aboveVisualCap:
					"A PDF or photo is read in a single pass, up to {{visualMax}} animals. For more than that, send the list as a spreadsheet or split the file.",
			},
			extraction: {
				title: "Reading your list",
				helper:
					"This may take a few minutes. The page updates on its own. Nothing is saved yet.",
				doneTitle_one: "{{count}} animal read",
				doneTitle_other: "All {{count}} animals read",
				doneTitleUnknown_one: "{{count}} animal read",
				doneTitleUnknown_other: "{{count}} animals read",
				doneHelper: "Next, confirm where the animals go. Nothing is saved yet.",
				animalsRead: "{{read}} of {{total}} animals read",
				animalsReadUnknown_one: "{{count}} animal read",
				animalsReadUnknown_other: "{{count}} animals read",
				needsYouEyebrow: "Needs you",
				leaveNote:
					"You can leave this page. The reading keeps going and the import waits for you under Herd.",
				cancel: "Cancel import",
				continue: "Match the lots",
				chunk: {
					label: "Part {{index}} of {{total}}",
					rows: "Rows {{from}}–{{to}}",
					failedNote:
						"This part could not be read. Ask for it again — the other parts are kept.",
					failedNoRetry:
						"This part could not be read. Send another file to continue.",
					shortfall:
						"This part returned fewer animals than it had rows. Read it again before going on, so you do not import half a herd.",
					stalled:
						"This part has been reading for longer than it should. Nothing is working on it now — ask for it again.",
					retry: "Read this part again",
					retrying: "Reading again...",
				},
			},
			failures: {
				retry: "Try again",
				manualEntry: "Add the animals by hand instead",
				chooseAnotherFile: "Send another file",
				budgetExceeded: {
					title: "Your AI allowance for this month is used up",
					helper:
						"Nothing was read and nothing was charged. The allowance resets at the start of next month, or an administrator can raise it in the organisation settings. You can add the animals by hand in the meantime.",
				},
				noAccess: {
					title: "AI extraction is not available on this organisation",
					helper:
						"Ask an administrator to turn AI features on for this organisation. Until then, animals can still be added by hand.",
				},
				subscriptionInactive: {
					title: "Your subscription is not active",
					helper:
						"AI extraction is off while the plan is inactive. Reactivate the subscription to import files — adding animals by hand keeps working either way.",
				},
				providerRefusal: {
					title: "The AI declined to read this file",
					helper:
						"Sending the same file again will not help — it will be declined again and the attempt still spends from your AI allowance. Check that the file really is an animal list, export it another way, or add the animals by hand.",
					noRetryNote:
						"There is nothing to retry here: the same file gets the same answer.",
				},
				coverageMismatch: {
					title: "The animals read do not match your file",
					helper:
						"We expected {{expected}} animals and got {{extracted}}, so we stopped instead of importing a partial herd. Read the failed part again, or split the file and import it in pieces.",
				},
				tooManyRows: {
					title: "This file has too many lines",
					helper:
						"This file has more lines than we can read in one go (up to {{max}} lines for a PDF or photo). Split it into smaller files, or send the list as a spreadsheet.",
				},
				mediaContentMismatch: {
					title: "This file is not an animal list",
					helper:
						"Send an animal list to continue. Reading this file again will give the same result and use more of your AI allowance.",
				},
				extractionFailed: {
					title: "We could not read this file",
					helper:
						"Something went wrong while reading it. Try again — if it fails a second time, send the list as a spreadsheet or add the animals by hand.",
				},
			},
			slots: {
				shortfallTitle: "Your plan does not have room for this many animals",
				shortfall:
					"Your plan has {{remaining}} animal slots left and this file asks for {{requested}}.",
				shortfallAdvice:
					"Remove {{excess}} animals from the file, leave them out in the review step, or move to a larger plan — then import again.",
			},
			match: {
				continue_one: "Check {{count}} animal",
				continue_other: "Check {{count}} animals",
			},
			check: {
				allClearTitle: "Everything is ready",
				allClearBody:
					"Every animal was read with no problems. Import when you are ready.",
				metricReady: "Ready to import",
				metricReadyCaption: "Read with no problems",
				metricAttention: "Need your attention",
				metricAttentionCaption: "Fix or leave out",
				metricAttentionNone: "Nothing pending",
				metricExcluded: "Left out",
				metricExcludedCaption: "Not coming in",
				metricExcludedNone: "None so far",
				leaveThisOut: "Leave this one out",
				tagHeading: "Tag {{tag}}",
				leaveOutShort: "Leave out",
				backToLots: "Back to the lots",
				filterLabel: "Which animals to show",
				filterAll: "All {{count}}",
				filterAttention: "Needs attention {{count}}",
				listEmpty:
					"No animal needs attention. Everything on this list is ready to import.",
				statusAttention: "Pending",
				statusReady: "Ready",
				lotLabel: "Lot",
				lotNone: "No lot",
				lotNew: "{{name}} (new)",
				lotUndecided: "{{name}} (undecided)",
				moreFields: "More fields",
				nothingToImport: "Nothing to import yet",
				bulk: {
					birthDate: {
						title_one: "{{count}} animal has no birth date",
						title_other: "{{count}} animals have no birth date",
						label: "Birth date for all of them",
						apply: "Fill in the date",
					},
					sex: {
						title_one: "{{count}} animal has no sex",
						title_other: "{{count}} animals have no sex",
						label: "Sex for all of them",
						apply: "Set the sex",
					},
					currentWeightDate: {
						title_one: "{{count}} animal has a weight but no weighing date",
						title_other: "{{count}} animals have a weight but no weighing date",
						label: "Weighing date for all of them",
						apply: "Fill in the date",
					},
				},
			},
			/** Attention-card titles, one per diagnostic code. `{{tag}}` is the row's ear tag. */
			attention: {
				MISSING_TAG_ID: "No tag on this animal",
				MISSING_SEX: "Sex not read for tag {{tag}}",
				MISSING_BIRTH_DATE: "Birth date not read for tag {{tag}}",
				INVALID_ENUM_VALUE: "A value on tag {{tag}} is not one of the options",
				INVALID_DATE: "A date on tag {{tag}} could not be read",
				DATE_OUT_OF_RANGE: "A date on tag {{tag}} does not make sense",
				WEIGHT_OUT_OF_RANGE: "A weight on tag {{tag}} does not make sense",
				MISSING_WEIGHING_DATE: "A weight on tag {{tag}} has no date",
				REPRODUCTION_ON_MALE: "Tag {{tag}} is a male with breeding data",
				COST_OUT_OF_RANGE: "Purchase price on tag {{tag}} does not make sense",
				DUPLICATE_TAG_IN_PAYLOAD: "Tag {{tag}} appears twice in this file",
				TAG_ALREADY_EXISTS: "Tag {{tag}} is already in your herd",
				HERD_REF_UNRESOLVED: "The lot for tag {{tag}} is not decided",
				HERD_NOT_ON_ESTABLISHMENT:
					"The lot chosen for tag {{tag}} is not an active lot on this farm",
				NON_ACTIVE_STATUS: "Tag {{tag}} is marked as gone",
			},
			review: {
				title: "Check before saving",
				helper:
					"Nothing is saved until you confirm. Fix what needs fixing, leave out what should not come in.",
				rowLabel: "Row {{n}}",
				pagination: "Rows {{from}}–{{to}} of {{total}}",
				dateFormatHint: "day/month/year",
				notRead: "not read",
				notReadHint:
					"A blank field means we could not read it or the data is missing — please fill it in by hand.",
				fields: {
					notes: "Notes",
					currentWeightKg: "Current weight (kg)",
					currentWeightDate: "Weighing date",
					acquisitionWeightKg: "Weight at purchase (kg)",
					reproductiveStatus: "Reproductive status",
					breedingDate: "Breeding date",
					breedingMethod: "Breeding method",
					sireName: "Bull / semen",
					pregnancyDiagnosisDate: "Pregnancy diagnosis date",
					expectedCalvingDate: "Expected calving date",
					lastCalvingDate: "Last calving date",
				},
				reproductiveStatuses: {
					PREGNANT: "Pregnant",
					EMPTY: "Empty",
				},
				breedingMethods: {
					ARTIFICIAL_INSEMINATION: "Artificial insemination",
					NATURAL_BREEDING: "Natural breeding",
				},
				vaccinations: "Vaccinations",
				nonAnimalRowsTitle_one:
					"{{count}} line was ignored as a total or summary",
				nonAnimalRowsTitle_other:
					"{{count}} lines were ignored as totals or summaries",
				nonAnimalRowsBody:
					"These are not animals, so they will not be imported. Lines: {{lines}}.",
				excludeRow: "Leave this animal out",
				includeRow: "Bring this animal back in",
				excludedBadge: "Left out",
				excludeExisting_one:
					"Leave out the {{count}} animal that already exists on this farm",
				excludeExisting_other:
					"Leave out the {{count}} animals that already exist on this farm",
				includedCount_one: "{{count}} animal will be imported",
				includedCount_other: "{{count}} animals will be imported",
				commit: "Import {{count}} animals",
				committing: "Importing...",
				commitDisabledHint:
					"Fix or leave out the rows with problems before importing.",
				discard: "Discard this import",
				discardConfirm:
					"Discard this import? The file and everything you fixed here are lost.",
			},
			herdMapping: {
				title: "Where do these animals go?",
				noLotColumn:
					"Your file has no lot column, so choose once for the whole import.",
				hasLotColumn_one:
					"Your file has a lot column with {{count}} name. Map it, or put every animal in a single lot.",
				hasLotColumn_other:
					"Your file has a lot column with {{count}} names. Map each one or put every animal in a single lot.",
				mode: {
					existing: "An existing lot",
					new: "A new lot",
					none: "No lot",
				},
				existingLabel: "Lot",
				newNamePlaceholder: "e.g. Calves 2026",
				noneHint:
					"Every animal comes in without a lot. You can group them later from the herd list.",
				sourceName: "Name in the file",
				decision: "What to do",
				animalsUsing_one: "{{count}} animal",
				animalsUsing_other: "{{count}} animals",
				kind: {
					existing: "Use an existing lot",
					new: "Create a new lot",
					unmapped: "Import without a lot",
				},
				existingPlaceholder: "Select a lot",
				newNameLabel: "Name of the new lot",
				newPurposeLabel: "Purpose of the new lot",
				newPurposePlaceholder: "Select a purpose",
				unmappedHint:
					"The animals come in with no lot. You can put them in one later.",
				assignAllLabel: "Put every animal in a single lot",
				assignAllHelper:
					"Use this when the whole file is one lot — it replaces the mapping above.",
				noHerdsOnFarm:
					"This farm has no lots yet. Create one here, or import without a lot.",
				unresolved: "Say what to do with this lot before going on.",
				newNameRequired: "Give the new lot a name.",
				newPurposeRequired: "Choose a purpose for the new lot.",
				duplicateNewName: "Two new lots cannot have the same name.",
			},
			diagnostics: {
				title: "What needs fixing",
				rowProblems_one: "{{count}} problem",
				rowProblems_other: "{{count}} problems",
				codes: {
					MISSING_TAG_ID:
						"No ear tag number. Every animal needs one — type it in or leave this animal out.",
					MISSING_SEX:
						"Sex not filled in. Choose male or female — the system needs it for every animal.",
					MISSING_BIRTH_DATE:
						"Birth date not filled in. Every animal needs one — type it in or leave this animal out.",
					INVALID_ENUM_VALUE:
						"This value is not one of the options RuralOps accepts. Pick one from the list.",
					INVALID_DATE:
						"This date could not be understood. Write it as day/month/year.",
					DATE_OUT_OF_RANGE:
						"This date is outside what makes sense — it is in the future or too far in the past. Check the year.",
					WEIGHT_OUT_OF_RANGE:
						"This weight is outside what makes sense for an animal. Check whether the unit or the decimal point slipped.",
					COST_OUT_OF_RANGE:
						"This purchase price is outside what RuralOps can record. Check whether a document number or an ID ended up in the price column.",
					DUPLICATE_TAG_IN_PAYLOAD:
						"This ear tag appears more than once in the file. Keep one row and leave the other out.",
					TAG_ALREADY_EXISTS:
						"An animal with this ear tag is already on this farm. Leave this row out so you do not end up with two.",
					HERD_REF_UNRESOLVED:
						"The lot for this animal was not decided yet. Sort it out in the lots list above.",
					HERD_NOT_ON_ESTABLISHMENT:
						"The chosen lot is not an active lot on this farm. Pick an active lot from this farm or create a new one.",
					NON_ACTIVE_STATUS:
						"This animal is marked as sold, dead or otherwise gone. The import only creates active animals, so it is left out by default.",
					MISSING_WEIGHING_DATE:
						"A weight needs the date it was taken (for the purchase weight, the purchase date). Fill in the date or clear the weight.",
					REPRODUCTION_ON_MALE:
						"Pregnancy, breeding and calving only apply to females. Fix the sex or clear the reproduction fields.",
				},
			},
			success: {
				title: "Import finished",
				summary:
					"{{animals}} animals, {{herds}} lots and {{records}} records created.",
				animalsCreated_one: "{{count}} animal created",
				animalsCreated_other: "{{count}} animals created",
				herdsCreated_one: "{{count}} lot created",
				herdsCreated_other: "{{count}} lots created",
				noHerdsCreated: "No new lot was created.",
				alreadyImported:
					"This file had already been imported, so nothing was created a second time.",
				viewAnimals: "See the animals",
			},
			done: {
				title_one: "{{count}} animal is in your herd",
				title_other: "{{count}} animals are in your herd",
				alreadyImportedTitle: "This list was already imported",
				withLots_one:
					"They were added to {{count}} lot. Weights and movements can be recorded from the app.",
				withLots_other:
					"They were added to {{count}} lots. Weights and movements can be recorded from the app.",
				withoutLot:
					"They came in without a lot. You can group them from the herd list whenever you want.",
				seeHerd: "See the herd",
				importAnother: "Import another list",
			},
			/**
			 * Producer-facing lifecycle state of an import. Keys are the wire values of
			 * the shared `AnimalImportStatus` union, so a renderer can key a total
			 * `Record<AnimalImportStatus, …>` off them.
			 */
			status: {
				EXTRACTING: {
					label: "Reading the file",
					description:
						"The AI is still reading your list. This page updates on its own.",
				},
				READY_FOR_REVIEW: {
					label: "Ready for review",
					description:
						"The reading finished. Check the animals before anything is saved.",
				},
				NEEDS_ATTENTION: {
					label: "Needs attention",
					description: "Something went wrong while reading this file.",
				},
				IMPORTED: {
					label: "Imported",
					description: "The animals from this file are already on the farm.",
				},
				CANCELLED: {
					label: "Cancelled",
					description: "You cancelled this import. Nothing from it was saved.",
				},
			},
			landing: {
				title: "An import is already under way",
				helper:
					"Pick it up where you left off, or cancel it to send a different file.",
				fileLabel: "File",
				uploadedBy: "Sent by {{name}}",
				uploadedAt: "Sent on {{date}}",
				unknownUploader: "someone on this farm",
				extractedRows_one: "{{count}} animal read so far",
				extractedRows_other: "{{count}} animals read so far",
				declaredRows_one: "{{count}} row in the file",
				declaredRows_other: "{{count}} rows in the file",
				failureCodesLabel: "What went wrong",
				continue: "Continue this import",
				cancel: "Cancel this import",
				newImportWithheld:
					"You can send another file once this import is finished or cancelled.",
			},
			/** Bodies are keyed by the three cancellable states — the copy must differ. */
			cancelDialog: {
				title: "Cancel this import?",
				body: {
					EXTRACTING:
						"The reading stops where it is and nothing is saved. What the AI has already read does not come back to this month's allowance.",
					READY_FOR_REVIEW:
						"Everything you fixed, left out and mapped in the review is discarded and cannot be recovered. Nothing is saved to the farm, and what the AI has already read does not come back to this month's allowance.",
					NEEDS_ATTENTION:
						"The failed import is cleared out of your way and nothing is saved. What the AI already tried to read does not come back to this month's allowance.",
				},
				confirm: "Cancel import",
				keep: "Keep it",
				pending: "Cancelling...",
				success: "Import cancelled",
				error: "The import could not be cancelled. Try again.",
				notCancellable:
					"This import was already saved to the farm, so it cannot be cancelled.",
			},
			cancelledNotice: {
				title: "This import was cancelled",
				helper: "Nothing from it was saved. Send another file to start again.",
			},
			unknownImport: {
				title: "This import was not found",
				helper:
					"The address may be old, or the import belongs to another farm. Send a file to start a new one.",
			},
			/** Upload admission refusals — one per error the backend can raise. */
			admission: {
				alreadyActive: {
					title: "There is already an import under way on this farm",
					helper:
						'"{{name}}" — {{state}}. Finish it or cancel it before sending another file.',
					action: "Go to that import",
				},
				uploadInProgress: {
					title: "Another file is still being sent",
					helper:
						"Wait a moment and send yours again. If that send never finishes, the farm frees up on its own shortly.",
					blockingFile:
						'"{{name}}" is still being sent. Upload and extract will become available when that send finishes or its reservation expires.',
				},
				/**
				 * The plan-slot refusal. Unlike the two above it will never succeed on
				 * retry, so the copy says what to change instead of what to wait for.
				 */
				slotsExhausted: {
					title: "Your plan does not have room for this many animals",
					helper:
						"This file asks for more animals than your plan has slots left. Nothing was sent. Remove some rows from the file, or move to a larger plan, then send it again.",
				},
			},
		},
		batch: {
			errors: {
				animalNotFound: "Animal not found",
				listedInOffer:
					"Listed in an active marketplace offer. Cancel the offer first.",
				differentFarm: "Animal belongs to a different farm than the target",
				notActive: "Only active animals can change herd",
				animalNotActive:
					"This animal is no longer active (sold, dead or transferred)",
				invalidValue: "Value must be greater than 0",
				femaleOnly: "Only allowed for female animals",
				maleOnly: "Only allowed for male animals",
				unknown: "Unknown error",
			},
		},
		weighing: {
			title: "Batch weighing",
			startSession: "Weigh batch",
			/** Short form for dense action rows (herd header) where scope is implicit. */
			startSessionShort: "Weigh",
			captureHint: "Chute flow: tag → weight → Enter, one animal after another",
			scopeHerd: "Herd: {{name}}",
			scopeSelection_one: "{{count}} selected animal",
			scopeSelection_other: "{{count}} selected animals",
			scopeFarm: "All animals on this farm",
			tagLabel: "Tag",
			tagPlaceholder: "e.g. 042",
			weightLabel: "Weight (kg)",
			weightPlaceholder: "0.0",
			weightBelowMin: "Weight below {{min}} kg — check the reading",
			weightAboveMax: "Weight above {{max}} kg — check the reading",
			add: "Add",
			lastAdded: "Added {{tag}} · {{weight}} kg",
			suggestionCount_one: "{{count}} matching tag",
			suggestionCount_other: "{{count}} matching tags",
			unknownTag: "Tag not found in the roster.",
			unknownTagOffline:
				"Tag not found in the offline data. Register it now and weigh it after syncing.",
			createAnimal: 'Create animal "{{tag}}"',
			empty: "No weighings in this session yet",
			emptyHint:
				"Type the tag and the weight — Enter appends the row and returns to the tag.",
			date: "Weighing date",
			dateToday: "Today",
			save_one: "Save {{count}} weighing",
			save_other: "Save {{count}} weighings",
			confirmSaveTitle_one: "Save {{count}} weighing?",
			confirmSaveTitle_other: "Save {{count}} weighings?",
			confirmSaveDescription: "Review the captured weights before saving.",
			totalWeight: "Total {{total}} kg",
			autosaved: "autosaved",
			saveSuccess_one: "{{count}} weighing recorded",
			saveSuccess_other: "{{count}} weighings recorded",
			savePartial:
				"{{success}} saved, {{failed}} failed. The failed rows stay in the session.",
			saveAllFailed: "No weighing was saved. Check the errors and try again.",
			replayPartial:
				"Weighings synced: {{success}} saved, {{failed}} rejected by the server.",
			discard: "Discard session",
			discardConfirm_one: "Discard {{count}} unsaved weighing?",
			discardConfirm_other: "Discard {{count}} unsaved weighings?",
			removeEntry: "Remove weighing",
			entryRemoved: "Removed {{tag}}",
			undo: "Undo",
			rosterTitle: "Animals in session",
			rosterProgress: "{{done}} of {{total}} weighed",
			rosterToWeigh: "To weigh",
			rosterWeighed: "Weighed",
			rosterSearchPlaceholder: "Filter by tag…",
			rosterEmpty: "No animals in this session's scope",
			rosterNoMatch: "No animal matches the filter",
			rosterLastWeight: "Last: {{weight}} kg",
			rosterSelect: "Select tag {{tag}} to weigh",
			rosterFilterHint_one: "{{count}} animal — type to filter",
			rosterFilterHint_other: "{{count}} animals — type to filter",
			rosterToggle: "Show/hide session animals",
		},
		record: {
			target: {
				appliesTo: "Applies to",
				animalCount_one: "{{count}} animal",
				animalCount_other: "{{count}} animals",
			},
		},
		operations: {
			create: {
				title: "Record Operation",
				titleForHerd: "Record Operation for {{herdName}}",
				titleForType: "Record {{type}}",
				cohortDescription_one:
					"The record will be applied to the selected animal.",
				cohortDescription_other:
					"The same record will be applied to each of the {{count}} selected animals.",
				cohortSuccess_one: "Recorded for {{count}} animal",
				cohortSuccess_other: "Recorded for {{count}} animals",
				ctaForCount_one: "Record for {{count}} animal",
				ctaForCount_other: "Record for {{count}} animals",
				ctaForTarget: "Record for {{target}}",
			},
			validation: {
				gestationDaysEstimate: "Enter 1 to 320 days of gestation.",
				grazingRotationNeedsHerd:
					"This animal is not in a herd. A grazing rotation can only be recorded for a herd.",
			},
			list: {
				empty: "No operations recorded yet",
				addFirst: "Record the first operation to start tracking this animal.",
				addOperation: "Add operation",
				viaHerd: "Via herd: {{herdName}}",
				appliedToAnimals_one: "Applied to {{count}} animal",
				appliedToAnimals_other: "Applied to {{count}} animals",
			},
			page: {
				title: "Operations",
				empty: "No operations recorded yet",
			},
			fields: {
				target: "Target",
				type: "Operation Type",
				operationDate: "Date",
				endDate: "End Date",
				isActive: "Active",
				description: "Description",
				cost: "Cost",
				performedBy: "Performed By",
				paddock: "Paddock",
				notes: "Notes",
				medication: "Medication",
				vaccine: "Vaccine",
				vaccinePlaceholder: "Select a vaccine",
				vaccineHelp: "If the vaccine you want is not listed, you can",
				vaccineRegisterLink: "register a new one",
				dosage: "Dosage",
				batchNumber: "Medication Batch",
				nextDoseDate: "Next Dose Date",
				nextDoseDateHelpRecurring:
					"From the sanitary calendar (every {{days}} days).",
				nextDoseDateHelpSingleDose:
					"This calendar entry is a single dose, so no next date is set.",
				diagnosis: "Diagnosis",
				durationDays: "Duration (days)",
				withdrawalDays: "Withdrawal (days)",
				withdrawalDaysHelp:
					"Meat withdrawal period, counted from the last dose.",
				severity: "Severity",
				quarantined: "In quarantine",
				cause: "Cause",
				disposalMethod: "Disposal Method",
				planName: "Plan Name",
				feedType: "Feed Type",
				dailyAmountKg: "Daily Amount (kg)",
				costPerKg: "Cost per Kg",
				supplementType: "Supplement Type",
				amountKg: "Amount (kg)",
				frequency: "Frequency",
				plannedDays: "Planned Days",
				stockingRate: "Stocking Rate (AU/ha)",
				weaningWeightKg: "Weaning Weight (kg)",
				weaningMethod: "Weaning Method",
				method: "Method",
				tagType: "Tag Type",
				tagNumber: "Tag Number",
				cleaningType: "Cleaning Type",
				area: "Area",
				pestType: "Pest Type",
				product: "Product",
				applicationMethod: "Application Method",
				semenBullId: "Semen Bull ID",
				semenBatchNumber: "Semen Batch Number",
				technique: "Technique",
				observedSigns: "Observed Signs",
				bullTagId: "Bull Tag",
				result: "Result",
				technician: "Technician",
				gestationDaysEstimate: "Estimated gestation age (optional)",
				gestationDaysEstimateHelp:
					"Use when no AI or breeding was recorded; helps calculate the expected calving date.",
				daysUnit: "days",
				operationType: "Operation Type",
				severityPlaceholder: "Select severity",
				feedTypePlaceholder: "Select feed type",
				paddockPlaceholder: "Select paddock",
			},
			placeholders: {
				medication: "e.g., Ivermectin",
				vaccine: "e.g., Febre Aftosa",
				dosage: "e.g., 5ml",
				batchNumber: "e.g., LOT-2026-001",
				cause: "e.g., Unknown, Disease, Accident",
				disposalMethod: "e.g., Burial, Rendering",
				planName: "e.g., Winter Feeding Plan",
				supplementType: "e.g., Mineral salt, Protein",
				method: "e.g., Burdizzo, Surgical",
				tagType: "e.g., Ear tag, RFID",
				tagNumber: "e.g., BR-001",
				technique: "e.g., Conventional, Fixed-time AI",
				semenBullId: "e.g., BULL-2026-001",
				performedBy: "e.g., Dr. Silva",
				description: "Describe what was done...",
				notes: "Additional observations...",
				cost: "0.00",
				cleaningType: "e.g., Trough, Barn",
				area: "e.g., Barn A, Paddock 3",
				pestType: "e.g., Tick, Horn fly",
				product: "e.g., Fipronil, Ivermectin",
				weaningWeight: "e.g., 180",
				weaningMethod: "e.g., Gradual, Abrupt",
				stockingRate: "e.g., 2.5",
				plannedDays: "e.g., 30",
				dailyAmountKg: "e.g., 2.5",
				frequency: "e.g., Daily, Twice a week",
				applicationMethod: "e.g., Pour-on, Spray",
				observedSigns: "e.g., Mounting, restlessness",
				bullTagId: "e.g., BULL-07",
				result: "Select the result",
				pregnancyMethod: "Select the method",
				technician: "e.g., Dr. Silva",
			},
			helpers: {
				cost: "Currency value in R$",
				stockingRate: "Animal units per hectare",
				dailyAmountKg: "Per animal, per day",
			},
			sections: {
				details: "Details",
				additionalInfo: "Additional Information",
			},
			addNotes: "Add Notes",
			saveAndAddAnother: "Save & add another",
			batchAdded_one: "{{count}} operation recorded in this session",
			batchAdded_other: "{{count}} operations recorded in this session",
			categories: {
				health: "Health",
				nutrition: "Nutrition",
				pasture: "Pasture",
				reproduction: "Reproduction",
				management: "Management",
				hygiene: "Hygiene",
				other: "Other",
			},
			operationTypes: {
				VACCINATION: "Vaccination",
				TREATMENT: "Treatment",
				DISEASE: "Disease",
				MORTALITY: "Mortality",
				FEEDING_PLAN: "Feeding Plan",
				SUPPLEMENTATION: "Supplementation",
				GRAZING_ROTATION: "Grazing Rotation",
				WEANING: "Weaning",
				CASTRATION: "Castration",
				DEHORNING: "Dehorning",
				IDENTIFICATION: "Identification",
				HOOF_TRIMMING: "Hoof Trimming",
				CLEANING: "Cleaning",
				PEST_CONTROL: "Pest Control",
				ARTIFICIAL_INSEMINATION: "Artificial Insemination",
				SALE: "Sale",
				OTHER: "Other",
				HEAT_DETECTION: "Heat Detection",
				NATURAL_BREEDING: "Natural Breeding",
				PREGNANCY_DIAGNOSIS: "Pregnancy Diagnosis",
				ABORTION: "Abortion",
				CALVING: "Calving",
			},
			pregnancyResults: {
				PREGNANT: "Pregnant",
				EMPTY: "Empty (open)",
				DOUBTFUL: "Doubtful",
			},
			pregnancyMethods: {
				PALPATION: "Rectal palpation",
				ULTRASOUND: "Ultrasound",
				BLOOD_TEST: "Blood test",
			},
			feedTypes: {
				HAY: "Hay",
				SILAGE: "Silage",
				GRAIN: "Grain",
				CONCENTRATE: "Concentrate",
				MINERAL: "Mineral",
				PASTURE: "Pasture",
				OTHER: "Other",
			},
			severities: {
				mild: "Mild",
				moderate: "Moderate",
				severe: "Severe",
				critical: "Critical",
			},
			createSuccess: "Operation recorded successfully",
			createError: "Failed to record operation",
			updateSuccess: "Operation updated successfully",
			updateError: "Failed to update operation",
			deleteSuccess: "Operation deleted successfully",
			deleteError: "Failed to delete operation",
			filters: {
				fromDate: "From",
				toDate: "To",
				animal: "Animal",
				type: "Type",
				clear: "Clear Filters",
				allAnimals: "All Animals",
				allTypes: "All Types",
			},
			view: {
				timeline: "Timeline",
				table: "Table",
			},
			table: {
				animalOrHerd: "Animal / Herd",
				type: "Type",
				date: "Date",
				description: "Description",
				performedBy: "Performed By",
				paddock: "Paddock",
				cost: "Cost",
			},
			details: {
				edit: "Edit Operation",
				delete: "Delete Operation",
				confirmDelete: "Are you sure you want to delete this operation?",
			},
		},
		measurements: {
			page: {
				title: "Measurements",
				empty: "No measurements recorded yet",
			},
			list: {
				empty: "No measurements recorded yet",
			},
			create: {
				title: "Record Measurement",
			},
			types: {
				WEIGHT: "Weight",
				BODY_CONDITION_SCORE: "Body Condition Score",
				HIP_HEIGHT: "Hip Height",
				SCROTAL_CIRCUMFERENCE: "Scrotal Circumference",
				BODY_TEMPERATURE: "Body Temperature",
				PREGNANCY_CHECK: "Pregnancy Check",
				CALVING: "Calving",
				BLOOD_EXAM: "Blood Exam",
				GENERAL_EXAMINATION: "General Examination",
			},
			fields: {
				target: "Target",
				type: "Measurement Type",
				date: "Date",
				value: "Value",
				weightKg: "Weight (kg)",
				bcsScore: "Score (1-9)",
				heightCm: "Height (cm)",
				circumferenceCm: "Circumference (cm)",
				temperatureC: "Temperature (°C)",
				daysPregnant: "Days Pregnant",
				calfBirthWeightKg: "Calf Birth Weight (kg)",
				examType: "Exam Type",
				description: "Description",
				expectedCalvingDate: "Expected Calving Date",
			},
			helpers: {
				bcsScale: "1 = Emaciated, 5 = Moderate, 9 = Obese",
			},
			placeholders: {
				weightKg: "e.g. 450",
				bcsScore: "e.g. 5",
				heightCm: "e.g. 140",
				circumferenceCm: "e.g. 35",
				temperatureC: "e.g. 38.5",
				daysPregnant: "e.g. 90",
				calfBirthWeightKg: "e.g. 30",
				examType: "e.g. Hemogram, Biochemistry",
				description: "Describe the examination or results...",
				expectedCalvingDate: "Select expected calving date",
			},
			saveAndAddAnother: "Save & Add Another",
			batchAdded_one: "{{count}} measurement recorded in this session",
			batchAdded_other: "{{count}} measurements recorded in this session",
			table: {
				animalOrHerd: "Animal / Herd",
				type: "Type",
				value: "Value",
				date: "Date",
				description: "Description",
			},
			stats: {
				totalMeasurements: "Total Measurements",
				avgWeight: "Avg. Weight",
				typesRecorded: "Types Recorded",
			},
			filters: {
				fromDate: "From",
				toDate: "To",
				animal: "Animal",
				type: "Type",
				clear: "Clear Filters",
				allAnimals: "All Animals",
				allTypes: "All Types",
			},
			view: {
				timeline: "Timeline",
				table: "Table",
			},
			details: {
				title: "Measurement Details",
				edit: "Edit Measurement",
				delete: "Delete Measurement",
				confirmDelete: "Are you sure you want to delete this measurement?",
			},
			createSuccess: "Measurement recorded successfully",
			createError: "Failed to record measurement",
			updateSuccess: "Measurement updated successfully",
			deleteSuccess: "Measurement deleted successfully",
			addNotes: "Add Notes",
		},
		timeline: {
			animals: "Animals",
			performedBy: "Performed by",
			paddock: "Paddock",
			cost: "Cost",
			expectedCalvingDate: "Expected calving",
			fromHerd: "From",
			toHerd: "To",
		},
		movements: {
			types: {
				HERD_TRANSFER: "Herd transfer",
			},
			herdTransferFromTo: "Moved from {{from}} to {{to}}",
			herdTransferTo: "Moved to {{to}}",
			herdTransferFrom: "Removed from {{from}}",
		},
		calving: {
			title: "Record Calving",
			submit: "Record Calving",
			createSuccess: "Calving recorded successfully",
			calfSection: "Calf",
			calfSectionHint:
				"The calf is created as a new born-on-farm animal with dam and sire filled in automatically.",
			fields: {
				dam: "Dam (cow)",
				calvingDate: "Calving Date",
				stillborn: "Stillborn",
				complications: "Complications",
				calfSex: "Calf Sex",
				birthWeight: "Birth Weight (kg)",
				calfTag: "Calf Tag (optional)",
			},
			placeholders: {
				calfSex: "Select the sex",
				calfTag: "e.g., BR-1024",
				complications: "e.g., Dystocia, assisted delivery",
			},
			helpers: {
				calfTag:
					"No tag yet? A provisional PEND-… tag is generated and the calf is flagged as pending a tag.",
			},
		},
		registries: {
			tab: "Records",
			tabWithCount: "Records ({{count}})",
			empty: "No records yet",
			// Derived row (not a record): opens a freshly registered animal's
			// timeline on its own beginning instead of on nothing.
			registered: {
				badge: "Registration",
				title: "Animal registered",
			},
			firstUse: {
				title: "No records yet",
				body: "This animal's weighings, vaccinations, treatments and herd moves will show up here.",
				primary: "Record first weight",
				secondary: "Record something else",
			},
			loadMore: "Load more",
			page: {
				title: "Records",
				empty: "No records yet",
			},
			view: {
				timeline: "Timeline",
				table: "Table",
			},
			table: {
				date: "Date",
				kind: "Kind",
				animalOrHerd: "Animal / Herd",
				type: "Record",
				detail: "Detail",
			},
			kind: {
				all: "All",
				operation: "Operations",
				measurement: "Measurements",
				movement: "Movement",
			},
			filters: {
				clear: "Clear",
				period: "Period",
				presets: {
					last12: "Last 12 months",
					last24: "Last 24 months",
					all: "All time",
					last12Short: "12 mo",
					last24Short: "24 mo",
					allShort: "All",
				},
			},
			export: {
				button: "Export CSV",
				empty: "No records in range",
				error: "Failed to export records",
			},
			batch: {
				mean: "Average",
			},
			register: {
				button: "Record",
				title: "New record",
				search: "Search type…",
				recents: "Recent",
				empty: "No type found",
			},
			capture: {
				recordAnotherPrompt: "Record saved. Add another?",
				recordAnother: "Record another",
			},
		},
		weight: {
			title: "Weight",
			averageWeight: "Average Weight",
			kgUnit: "kg",
			measurementCount: "Measurements",
		},
	},
	financial: {
		nav: {
			title: "Financial",
			hubTitle: "Financial Hub",
			overview: "Overview",
			transactions: "Transactions",
			vendors: "Vendors",
			reports: "Reports",
			loans: "Loans",
			recurring: "Recurring entries",
			receivables: "Receivables",
			payables: "Payables",
			payroll: "Payroll",
		},
		vendors: {
			title: "Vendors",
			placeholder: "Select vendor",
			searchPlaceholder: "Search by name or document",
			noResults: "No vendors found",
			createNew: "+ Create vendor",
			createNamed: '+ Create "{{name}}"',
			clear: "Clear selection",
			nameLabel: "Name",
			namePlaceholder: "Vendor name",
			documentLabel: "Tax ID",
			documentPlaceholder: "Document (optional)",
			phoneLabel: "Phone",
			emailLabel: "Email",
			defaultCategoryLabel: "Default category",
			noDefaultCategory: "No default category",
			notesLabel: "Notes",
			createSubmit: "Create",
			createTitle: "New vendor",
			editTitle: "Edit vendor",
			formSubtitle: "Vendors are shared across all farms.",
			confirmDelete: "Confirm removal?",
			createSuccess: "Vendor created",
			updateSuccess: "Vendor updated",
			deleteSuccess: "Vendor removed",
			newVendor: "New vendor",
			newVendorShort: "Vendor",
			removedBadge: "Removed",
			summary: {
				totalSpend: "Total spend in period",
				supplierCount: "Suppliers with spend",
			},
			empty: {
				title: "No vendors yet",
				description:
					"Register vendors or link them on entries to see spend per supplier.",
			},
			columns: {
				vendor: "Vendor",
				defaultCategory: "Default category",
				transactions: "Entries",
				spend: "Spend",
			},
		},
		kinds: {
			REVENUE: "Revenue",
			DIRECT_COST: "Direct cost",
			OVERHEAD: "Overhead",
			CAPITAL: "Capital",
			FINANCING: "Financing",
		},
		directions: {
			IN: "In",
			OUT: "Out",
		},
		reviewStates: {
			UNREVIEWED: "To review",
			REVIEWED: "Reviewed",
		},
		entryOrigins: {
			AUTO: "Automatic",
			MANUAL: "Manual",
		},
		sourceModules: {
			livestock: "Livestock",
			grass: "Grass",
			equipment: "Equipment",
			manual: "Manual entry",
		},
		periods: {
			thisMonth: "This month",
			lastMonth: "Last month",
			last3Months: "Last 3 months",
			last6Months: "Last 6 months",
			last12Months: "Last 12 months",
			thisYear: "This year",
			safraCurrent: "Current season",
			safraPrevious: "Previous season",
		},
		overview: {
			title: "Financial overview",
			subtitle: "Track money in, money out and result by period",
			moneyIn: "Money in",
			moneyOut: "Money out",
			net: "Net result",
			monthlyChart: {
				title: "Money in & out by month",
				in: "Money in",
				out: "Money out",
			},
			categoryBreakdown: {
				title: "Expenses by category",
				empty: "No expenses this period",
				totalOut: "total out",
				other: "Other",
			},
			reviewCta: {
				title: "Records to review",
				description_one: "{{count}} auto-record awaiting review",
				description_other: "{{count}} auto-records awaiting review",
				cta: "Review now",
			},
			kpi: {
				new: "new",
				vsPreviousPeriod: "vs previous period",
				incomeSub_one: "{{count}} income category",
				incomeSub_other: "{{count}} income categories",
				expenseSub_one: "{{count}} expense category",
				expenseSub_other: "{{count}} expense categories",
			},
			attention: {
				title_one: "{{count}} thing needs your attention",
				title_other: "{{count}} things need your attention",
				reviewMeta: "Imported from field entries",
				pricesTitle: "Herd reference prices outdated",
				pricesMeta: "Production result may be imprecise",
				pricesCta: "Update",
				openingBalanceTitle: "Opening balance not set",
				openingBalanceMeta: "Needed for the running balance",
			},
			receivables: {
				open: "Open",
				overdue_one: "Overdue · {{count}}",
				overdue_other: "Overdue · {{count}}",
			},
			quickLinks: {
				view: "View",
			},
			recent: {
				title: "Recent activity",
				viewAll: "View all",
				seeAll: "See all →",
				toReview: "To review",
				empty: "No transactions in this period",
			},
			orgLevelFootnote_one:
				"+ {{count}} organization-level record (shown under All Farms)",
			orgLevelFootnote_other:
				"+ {{count}} organization-level records (shown under All Farms)",
			setup: {
				openingBalance:
					"Opening balance not set — set it to see the running balance and projected cash flow.",
				openingBalanceCta: "Set opening balance",
				referencePrices:
					"Herd reference prices are missing or out of date — the production result may be inaccurate.",
				referencePricesCta: "Update reference prices",
			},
		},
		receiptImport: {
			pageTitle: "Scan receipt",
			eyebrow: "Financial",
			pageSubtitle:
				"Send the nota the way you got it. Nothing is posted until you confirm the entry.",
			action: "Scan receipt",
			/** Short form for the phone header row, where the scan icon carries the verb. */
			actionShort: "Receipt",
			steps: {
				upload: "Upload",
				extract: "Extract",
				review: "Review",
			},
			upload: {
				title: "Upload the receipt or invoice",
				helper:
					"Take a photo or pick a PDF. We'll read the amount, date and supplier automatically.",
				dropTitle: "Drop your receipt here",
				dropTypes: "PDF · JPEG · PNG · WebP",
				chooseFile: "Choose a file",
				takePhoto: "Take a photo",
				removeFile: "Remove",
				fileMetaPhoto: "{{size}} · photo",
				fileMetaPdf: "{{size}} · PDF",
				submit: "Upload and read",
				unsupportedType: "Unsupported format. Use PDF, JPG, PNG or WebP.",
				limitsLine: "One document per entry, up to {{max}}.",
			},
			extracting: {
				title: "Reading the receipt…",
				helper: "This usually takes a few seconds.",
				failedTitle: "Couldn't read the receipt",
				failedHelper:
					"Try again or fill the entry in by hand — the document stays attached.",
				retry: "Try again",
				enterManually: "Enter manually",
			},
			review: {
				title: "Check the details",
				helper:
					"Review and fix anything before confirming. The entry lands as unreviewed.",
				matchingVendor: "Looking up the supplier by CNPJ/CPF…",
				vendorMatched: "Supplier matched: {{name}}",
				categoryHint: "AI suggestion: {{label}}",
			},
		},
		transactions: {
			title: "Transactions",
			new: "New entry",
			/** Short form for the phone header row, where the + icon carries "new". */
			newShort: "Entry",
			empty: {
				title: "No transactions",
				description:
					"Automatic records appear here as you log operations, or add a manual entry.",
			},
			columns: {
				date: "Date",
				description: "Description",
				category: "Category",
				amount: "Amount",
				source: "Source",
				review: "Review",
				status: "Status",
			},
			filters: {
				search: "Search transactions...",
				button: "Filters",
				title: "Filter transactions",
				kind: "Kind",
				category: "Category",
				sourceModule: "Source",
				reviewState: "Review",
				all: "All",
				includeVoided: "Include voided",
				clear: "Clear filters",
			},
			detail: {
				title: "Transaction detail",
				source: "Source",
				sourceLink: "View source record",
				notes: "Notes",
				party: "Party",
				dimensions: "Links",
				noSource: "Manual entry (no source record)",
				readOnlyNotice:
					"Amount and date of automatic records are owned by the source and cannot be edited here.",
			},
			manualEntry: {
				title: "New manual entry",
				quickTitle: "New entry",
				quickSubtitle: "Quickly record an expense or income",
				editTitle: "Edit entry",
				direction: "Type",
				income: "Money in (income)",
				expense: "Money out (expense)",
				amount: "Amount",
				eventDate: "Date",
				category: "Category",
				categoryKind: "Category group",
				establishment: "Farm",
				allFarms: "Organization (all farms)",
				party: "Party",
				partyPlaceholder: "Customer, supplier...",
				notes: "Notes",
				dimensionsToggle: "Links (optional)",
				duplicateLast: "Duplicate last entry",
			},
			attachments: {
				title: "Receipts",
				empty: "No receipts attached yet.",
				attach: "Attach receipt",
				uploadTitle: "Attach receipt",
				indicator: "Has an attached receipt",
				fieldLabel: "Receipt (optional)",
				fieldHelper:
					"Attach the invoice or receipt. Photo or PDF, up to 10 MB.",
				chooseFile: "Attach photo or PDF",
				uploadFailed:
					"Entry saved, but the receipt upload failed. Attach it from the detail page.",
			},
			dimensions: {
				herd: "Herd",
				animal: "Animal",
				paddock: "Paddock",
				equipment: "Equipment",
				task: "Task",
			},
			review: {
				markReviewed: "Mark as reviewed",
				changeCategory: "Change category",
				bulkReview: "Review selected",
				bulkCategorize: "Categorize & review",
				bulkCategoryPlaceholder: "Choose category",
				selected_one: "{{count}} selected",
				selected_other: "{{count}} selected",
				reviewSuccess: "Review saved",
				needsReview: "To review",
			},
			delete: {
				title: "Delete entry",
				confirm: "Delete this manual entry?",
			},
		},
		loans: {
			title: "Loans",
			new: "New loan",
			newShort: "Loan",
			editTerms: "Edit terms",
			empty: {
				title: "No loans",
				description:
					"Add a loan to track installments, interest and outstanding balance.",
			},
			filters: {
				status: "Status",
				all: "All",
			},
			columns: {
				lender: "Lender",
				principal: "Principal",
				amortization: "Amortization",
				installments: "Installments",
				firstPayment: "First payment",
				status: "Status",
			},
			statuses: {
				ACTIVE: "Active",
				SETTLED: "Settled",
				CANCELED: "Canceled",
			},
			amortizations: {
				SAC: "SAC",
				PRICE: "PRICE",
				MANUAL: "Manual",
			},
			frequencies: {
				MONTHLY: "Monthly",
				ANNUAL: "Annual",
				CUSTOM: "Custom",
			},
			installmentStatuses: {
				SCHEDULED: "Due",
				PAID: "Paid",
			},
			form: {
				newTitle: "New loan",
				lender: "Lender",
				lenderPlaceholder: "Bank, cooperative...",
				description: "Description",
				principal: "Principal amount",
				ratePrecision:
					"Enter a percentage from 0 to 99999.99 with at most two decimal places.",
				graceInvalid:
					"Grace months must be a whole number from zero to less than the installment count.",
				paymentBeforeDisbursement:
					"The payment date must be on or after the disbursement date.",
				interestRateAnnual: "Annual interest (%)",
				amortization: "Amortization system",
				frequency: "Frequency",
				customIntervalMonths: "Interval (months)",
				disbursementDate: "Disbursement date",
				firstPaymentDate: "First payment date",
				installmentsCount: "Number of installments",
				graceMonths: "Grace period (months)",
				establishment: "Farm",
				allFarms: "Organization (all farms)",
				notes: "Notes",
				equipment: "Equipment (optional)",
				equipmentNone: "None",
				manualSchedule: {
					title: "Manual schedule",
					hint: "Paste the installments: the sum of principal must equal the principal amount.",
					addRow: "Add installment",
					removeRow: "Remove installment",
					dueDate: "Due date",
					principal: "Principal",
					interest: "Interest",
					sumLabel: "Sum of principal",
					empty: "Add at least one installment to the manual schedule.",
					incomplete:
						"Fill in the due date, principal, and interest for every installment.",
					ascending:
						"Due dates must be in ascending order — fix installment {{n}}.",
					ascendingRow:
						"The due date must be later than the previous installment.",
					sumMismatch:
						"The installments' total principal ({{sum}}) must equal the loan principal ({{principal}}).",
					sumInvalid:
						"The installments' total principal must equal the loan principal.",
				},
			},
			schedule: {
				title: "Installment schedule",
				dueDate: "Due date",
				principal: "Principal",
				interest: "Interest",
				total: "Total",
				remainingBalance: "Balance",
			},
			pay: {
				action: "Pay",
				title: "Pay installment {{seq}}",
				description:
					"Record this installment's payment. Principal and interest can be adjusted.",
				paidDate: "Payment date",
				paidPrincipal: "Principal paid",
				paidInterest: "Interest paid",
				confirm: "Confirm payment",
				success: "Installment paid",
			},
			unpay: {
				action: "Undo",
				title: "Undo payment",
				confirm:
					"Reverse this installment's payment? The financial entries will be voided.",
				success: "Payment reversed",
			},
			settle: {
				action: "Settle",
				title: "Settle loan",
				confirm: "Settle early? Remaining open installments will be closed.",
				success: "Loan settled",
			},
			cancel: {
				action: "Cancel",
				title: "Cancel loan",
				confirm:
					"Cancel this loan? The disbursement will be voided. Only possible when no installment has been paid.",
				success: "Loan canceled",
			},
		},
		receivables: {
			title: "Receivables",
			subtitle: "Credit sales and buyer receipts",
			viewSale: "View sale",
			buyerDocument: "Document",
			saleDate: "Sale date",
			dueDate: "Due date",
			expected: "Expected amount",
			received: "Received",
			outstanding: "Outstanding",
			notes: "Notes",
			empty: {
				title: "No receivables",
				description: "Flag a sale as credit to track receipts and due dates.",
			},
			summary: {
				totalOutstanding: "Total outstanding",
				overdueCount: "Overdue",
				overdueAmount: "Overdue amount",
			},
			columns: {
				buyer: "Buyer",
				saleDate: "Sale date",
				dueDate: "Due date",
				expected: "Expected",
				received: "Received",
				outstanding: "Outstanding",
				status: "Status",
			},
			displayStatuses: {
				OPEN: "Open",
				PARTIAL: "Partial",
				SETTLED: "Settled",
				OVERDUE: "Overdue",
				VOID: "Void",
			},
			overview: {
				summary: "{{outstanding}} outstanding · {{overdue}} overdue",
			},
			void: {
				title: "Sale reversed",
				description: "The related sale was reversed. This receivable is void.",
			},
			flag: {
				action: "Flag as credit",
				title: "Flag sale as credit",
				description:
					"Track this sale as a receivable. Adjust the expected amount and set a due date if you like.",
				expectedAmount: "Expected amount",
				dueDate: "Due date (optional)",
				confirm: "Flag as credit",
				success: "Sale flagged as credit",
			},
			delete: {
				action: "Remove credit flag",
				title: "Remove receivable",
				confirm:
					"Remove the credit flag from this sale? Recorded receipts will be reversed.",
				success: "Credit flag removed",
			},
			saleBlock: {
				title: "Receivable",
				viewReceivable: "View receivable",
				notFlagged: "This sale has not been flagged as credit.",
				canceled: "This sale was canceled and cannot be flagged as credit.",
			},
			receipts: {
				empty: "No receipts recorded",
				columns: {
					date: "Date",
					amount: "Amount",
					notes: "Notes",
				},
				add: {
					action: "Record receipt",
					title: "Record receipt",
					description: "Record an amount received from the buyer.",
					amount: "Amount",
					receivedDate: "Received date",
					notes: "Notes",
					confirm: "Record",
					success: "Receipt recorded",
				},
				delete: {
					action: "Delete",
					title: "Delete receipt",
					confirm: "Delete this receipt? The financial entry will be reversed.",
					success: "Receipt deleted",
				},
			},
		},
		payables: {
			title: "Payables",
			issueDate: "Issue date",
			total: "Total amount",
			paid: "Paid",
			outstanding: "Outstanding",
			notes: "Notes",
			empty: {
				title: "No payables",
				description:
					"Record a supplier bill to track installments and due dates.",
			},
			summary: {
				totalOutstanding: "Total outstanding",
				overdueCount: "Overdue",
				overdueAmount: "Overdue amount",
			},
			columns: {
				vendor: "Vendor",
				issueDate: "Issued",
				nextDueDate: "Next due",
				total: "Total",
				paid: "Paid",
				outstanding: "Outstanding",
				status: "Status",
			},
			displayStatuses: {
				OPEN: "Open",
				PARTIAL: "Partial",
				SETTLED: "Settled",
				OVERDUE: "Overdue",
				VOID: "Void",
			},
			overview: {
				summary: "{{outstanding}} outstanding · {{overdue}} overdue",
			},
			void: {
				action: "Void bill",
				title: "Void bill",
				description:
					"This bill was voided. The financial entry was reversed and the installments removed.",
				confirm:
					"Void this bill? The financial entry will be reversed and its installments removed.",
				success: "Bill voided",
			},
			form: {
				newAction: "New bill",
				newActionShort: "Bill",
				newTitle: "New supplier bill",
				vendor: "Vendor",
				issueDate: "Issue date",
				description: "Description",
				descriptionPlaceholder: "e.g. Feed, 3 installments",
				installments: "Installments",
				addInstallment: "Add installment",
				dueDate: "Due date",
				amount: "Amount",
				total: "Total",
				notes: "Notes",
				submit: "Record bill",
				success: "Bill recorded",
			},
			installments: {
				columns: {
					seq: "No.",
					dueDate: "Due date",
					amount: "Amount",
					outstanding: "Outstanding",
					status: "Status",
				},
				statuses: {
					OPEN: "Open",
					PARTIAL: "Partial",
					SETTLED: "Settled",
					OVERDUE: "Overdue",
				},
			},
			payments: {
				empty: "No payments recorded",
				columns: {
					date: "Date",
					installment: "Installment",
					amount: "Amount",
					notes: "Notes",
				},
				add: {
					action: "Record payment",
					title: "Record payment",
					description: "Record an amount paid to the supplier.",
					amount: "Amount",
					paidDate: "Payment date",
					notes: "Notes",
					confirm: "Record",
					success: "Payment recorded",
				},
				delete: {
					action: "Delete",
					title: "Delete payment",
					confirm: "Delete this payment?",
					success: "Payment deleted",
				},
			},
		},
		recurring: {
			title: "Recurring entries",
			new: "New template",
			newShort: "Template",
			variableAmount: "Variable",
			filters: {
				all: "All",
				activeOnly: "Active only",
			},
			empty: {
				title: "No recurring templates",
				description:
					"Create a template to project fixed bills, subscriptions and recurring income.",
			},
			templates: {
				title: "Templates",
			},
			columns: {
				description: "Description",
				direction: "Type",
				frequency: "Frequency",
				amountDefault: "Default amount",
				status: "Status",
			},
			frequencies: {
				MONTHLY: "Monthly",
				QUARTERLY: "Quarterly",
				SEMIANNUAL: "Semiannual",
				ANNUAL: "Annual",
				CUSTOM: "Custom",
			},
			form: {
				newTitle: "New recurring template",
				description: "Description",
				descriptionPlaceholder: "Rent, electricity, subscription...",
				direction: "Type",
				income: "Money in (income)",
				expense: "Money out (expense)",
				category: "Category",
				amountDefault: "Default amount",
				amountDefaultHint:
					"Optional. Leave blank for variable amounts (entered on confirmation).",
				party: "Counterparty",
				partyPlaceholder: "Customer, supplier...",
				establishment: "Farm",
				allFarms: "Organization (all farms)",
				frequency: "Frequency",
				dueDayOfMonth: "Due day of month",
				customIntervalMonths: "Interval (months)",
				startDate: "Start",
				endBeforeStart: "The end date must be on or after the start date.",
				endDate: "End",
				autoPost: "Post automatically",
				autoPostHint:
					"Creates the entry on the due date without manual confirmation.",
				autoPostDisabledHint:
					"Set a default amount to enable automatic posting.",
				isActive: "Active",
				isActiveHint: "Inactive templates don't project new occurrences.",
			},
			delete: {
				action: "Delete template",
				title: "Delete recurring template",
				confirm:
					"Delete this template? Already-confirmed occurrences are kept; no new ones will be projected.",
			},
			occurrences: {
				title: "This month's occurrences",
				subtitle: "Confirm, skip or undo the entries projected for this month.",
				empty: "No occurrences projected for this month.",
				acted: "Occurrence updated",
				undone: "Occurrence undone",
				columns: {
					dueDate: "Due date",
					description: "Description",
					amount: "Amount",
					status: "Status",
				},
				statuses: {
					PROJECTED: "Projected",
					CONFIRMED: "Confirmed",
					SKIPPED: "Skipped",
				},
				confirm: {
					action: "Confirm",
					title: "Confirm occurrence",
					description:
						"Record this occurrence in the ledger. The amount can be adjusted.",
					amount: "Amount",
				},
				skip: {
					action: "Skip",
				},
				undo: {
					action: "Undo",
				},
			},
		},
		timeline: {
			title: "Projected cash flow",
			subtitle: "Actual and projected money in and out over the coming months",
			empty: "Nothing to project",
			noAnchorHint: "Set the opening balance to see the running balance.",
			noAnchorCta: "Set opening balance",
			legend: {
				actualIn: "Actual in",
				projectedIn: "Projected in",
				actualOut: "Actual out",
				projectedOut: "Projected out",
				runningBalance: "Running balance",
			},
		},
		openingBalance: {
			title: "Opening balance",
			description:
				"The cash starting point. Anchors the running-balance line on the projected cash flow.",
			asOfDate: "As of date",
			amount: "Amount",
			amountHint: "May be negative (an overdraft).",
			notes: "Notes",
		},
		productionResult: {
			title: "Result incl. herd change",
			operatingResult: "Operating result",
			deltaHerdValue: "Herd value change",
			productionResult: "Result incl. herd change",
			openHerdValue: "Herd value (open)",
			closeHerdValue: "Herd value (close)",
			deathWriteOffs: "Death write-offs",
			births: "Births",
			birthsValue: "{{count}} head — {{value}}",
			birthsValue_other: "{{count}} head — {{value}}",
			contextCaption:
				"Context for the herd-value change — already reflected in the open/close value, not added to the total again.",
			byCategoryTitle: "Herd by category",
			openCount: "Head (open)",
			closeCount: "Head (close)",
			openValue: "Value (open)",
			closeValue: "Value (close)",
			pricePerHead: "Price per head",
			stale: "outdated",
			staleNudge:
				"Some reference prices are outdated (over 120 days). Update them for a truer valuation.",
			staleNudgeCta: "Update reference prices",
			unvaluedUnknown_one:
				"{{count}} animal without a birth date could not be categorized or valued.",
			unvaluedUnknown_other:
				"{{count}} animals without a birth date could not be categorized or valued.",
			unpriced:
				"No reference price for: {{categories}}. Set a price to include these categories in the valuation.",
			label: "reference prices (automatic or entered by you)",
			empty: "No data in this period",
			basis: {
				reference: "Reference",
				market: "Market",
			},
			marketQuote: "Market quote: {{value}}/@",
			marketNoQuote:
				"No market quote recorded — the herd cannot be valued at market.",
			marketNoQuoteCta: "Record a market quote",
			arrobasPerHead: "@/head (standard)",
		},
		referencePrice: {
			title: "Price per head",
			category: "Category",
			standardArrobas: "Standard @",
			pricePerHead: "Price per head",
			validFrom: "Valid from",
			notSet: "Not set",
			stale: "outdated",
			edit: "Edit",
			set: "Set price",
			save: "Save",
			cancel: "Cancel",
			positiveAmount: "Enter a positive amount.",
		},
		marketQuote: {
			title: "Fat cattle quote (R$/@)",
			perArroba: "per @",
			since: "since {{date}}",
			stale: "outdated",
			empty: "No quote yet. Enter one below.",
			edit: "Enter my own quote",
			cancel: "Cancel",
			price: "Price per @ (R$)",
			validFrom: "Valid from",
			notes: "Note",
			notesPlaceholder: "e.g. CEPEA/B3, fat cattle",
			historyTitle: "Quote history",
		},
		priceOrigin: {
			automatic: "Automatic",
			user: "Your value",
			sourceLink: "{{label}} — open the source",
		},
		automaticPrices: {
			hint: {
				PINNED:
					"When available, estimated from the published quote for {{state}} (state pinned by you): price per @ × the standard arrobas of each category. Your manual quote per @ does not generate per-head prices; enter them by category.",
				RESOLVED:
					"When available, estimated from the published quote for {{state}}: price per @ × the standard arrobas of each category. Your manual quote per @ does not generate per-head prices; enter them by category.",
				DISABLED: "Automatic prices are off. Only the values you enter apply.",
				UNAVAILABLE:
					"No published price for the configured state. Only the values you enter apply.",
			},
			takeover:
				"Your value replaces the automatic price until you go back to automatic.",
			resume: "Back to automatic",
			resumeAria: "Back to automatic: {{series}}",
			returnsOn: "your value returns on {{date}}",
		},
		cattlePrices: {
			title: "Cattle prices",
			description:
				"The published fat-cattle quote for your farms' state, and the price per head it implies. Anything you type wins over the automatic value until you hand it back.",
		},
		priceBasis: {
			effective: {
				PINNED: "Price for {{state}}, pinned by you.",
				RESOLVED: "Price for {{state}}, chosen from your farms.",
				DISABLED: "Automatic prices are off. Only the values you enter apply.",
				UNAVAILABLE:
					"No source in effect. Pick a state or go back to automatic.",
			},
			none: "No published price yet. Set your farms' location to get one.",
			since: "since {{date}}",
			resolvedHint: "Your farms point to {{state}}.",
			resolvedNone: "No farm has a state set.",
			change: "Change source",
			done: "Done",
			enabled: "Use automatic prices",
			stateLabel: "Quote state",
			statePlaceholder: "Pick a state",
			stateAuto: "From your farms",
			groupFarms: "Your farms",
			groupQuoted: "Other states with a published quote",
			pinnedElsewhere: "None of your farms is in {{state}}.",
			scheduled: "{{series}}: your value returns on {{date}}",
			marketQuoteSeries: "Fat cattle quote",
			historyTitle: "Source history",
			history: {
				PINNED: "{{state}} · pinned",
				RESOLVED: "{{state}} · from your farms",
				DISABLED: "Off",
				UNAVAILABLE: "Unavailable",
			},
		},
		animalCategory: {
			BEZERRO: "Male calf",
			BEZERRA: "Female calf",
			GARROTE: "Young steer",
			NOVILHA: "Heifer",
			BOI_MAGRO: "Lean ox",
			BOI_GORDO: "Fat ox",
			VACA: "Cow",
			TOURO: "Bull",
		},
		settings: {
			title: "Financial settings",
			categories: {
				title: "Chart of accounts",
				description:
					"Rename, deactivate or add categories. System categories cannot be deleted.",
				add: "Add category",
				label: "Name",
				kind: "Kind",
				system: "System",
				newLabelPlaceholder: "Category name",
				deactivate: "Deactivate",
				activate: "Activate",
				rename: "Rename",
			},
			mappings: {
				title: "Category mappings",
				description:
					"Set the default category for each automatic record type. Affects future records only.",
				sourceModule: "Source",
				sourceType: "Record type",
				category: "Category",
				empty: "No mappings configured",
			},
			backfill: {
				title: "Import history",
				description:
					"Generate the financial entries missing from records you already track (animal purchases, operations, sales, equipment and part consumption). Idempotent: re-running never duplicates anything.",
				analyze: "Analyze history",
				import_one: "Import {{count}} record",
				import_other: "Import {{count}} records",
				importConfirmTitle: "Import historical records?",
				importConfirmBody_one:
					"{{count}} entry will be created from your records. You can review the categories afterwards.",
				importConfirmBody_other:
					"{{count}} entries will be created from your records. You can review the categories afterwards.",
				importConfirmCta: "Import",
				applied_one: "{{count}} entry imported.",
				applied_other: "{{count}} entries imported.",
				upToDate: "All caught up — no historical records are missing.",
				done: "Analysis complete",
				source: "Source",
				scanned: "Found",
				wouldPost: "To import",
				posted: "Imported",
				sources: {
					livestockAnimals: "Animal purchases",
					livestockOperations: "Livestock operations",
					livestockSales: "Livestock sales",
					grassOperations: "Pasture operations",
					equipmentAssets: "Equipment",
					partConsumptions: "Part consumption",
				},
			},
			verifier: {
				title: "Verify integrity",
				description:
					"Re-derives every automatic entry from its source and flags drift or orphaned links. Read-only — it never changes an entry.",
				run: "Verify now",
				checked_one: "{{count}} automatic record checked.",
				checked_other: "{{count}} automatic records checked.",
				clean: "No discrepancies found.",
				manualSource: "Manual",
				columns: {
					kind: "Type",
					source: "Source",
					detail: "Detail",
				},
				kinds: {
					DRIFT: "Drift",
					SHOULD_BE_VOID: "Should be voided",
					MISSING_SOURCE: "Source removed",
					ORPHANED_DIMENSION: "Orphaned link",
				},
			},
		},
		reports: {
			title: "Reports",
			tabs: {
				pnl: "P&L",
				cashFlow: "Cash flow",
				timeline: "Projected",
				costCenters: "Cost centers",
				kpis: "Unit economics",
				productionResult: "Production result",
			},
			period: {
				start: "Start",
				end: "End",
			},
			export: {
				button: "Export CSV",
				buttonShort: "Export",
				filename: "financial-transactions",
			},
			pnl: {
				title: "Profit & loss",
				revenue: "Revenue",
				directCost: "Direct costs",
				grossMargin: "Gross margin",
				overhead: "Overhead",
				operatingResult: "Operating result",
				capital: "Capital (excluded from result)",
				financing: "Financing (excluded from result)",
				category: "Category",
				amount: "Amount",
				unreviewedHint_one:
					"{{count}} record to review may change these numbers",
				unreviewedHint_other:
					"{{count}} records to review may change these numbers",
				empty: "No data in this period",
				current: "Current",
				comparison: "Comparison",
				delta: "Change",
			},
			comparison: {
				label: "Compare with",
				none: "No comparison",
				previousPeriod: "Previous period",
				sameSeasonLastYear: "Same season last year",
			},
			cashFlow: {
				title: "Cash flow",
				period: "Period",
				moneyIn: "Money in",
				moneyOut: "Money out",
				net: "Net",
				empty: "No cash movement in this period",
				basis: {
					event: "Accrual basis",
					cash: "Cash basis",
					cashCaveat:
						"Cash basis: applies only to recorded receipts; expenses by event date",
				},
			},
			costCenters: {
				title: "Cost centers",
				subtitle: "Revenue, direct cost and margin per dimension",
				lens: "Group by",
				lensFarm: "Farm",
				lensHerd: "Herd",
				lensPaddock: "Paddock",
				lensEquipment: "Equipment",
				key: "Cost center",
				revenue: "Revenue",
				directCost: "Direct cost",
				margin: "Margin",
				unattributed: "Unattributed",
				total: "Total",
				removed: "(removed)",
				empty: "No data in this period",
				honestyProrated: "herd costs apportioned per head",
				honestyPasture: "pasture costs not included",
			},
			kpis: {
				empty: "No data in this period",
				insufficient: "Insufficient data",
				recordHint: "Record {{inputs}} to unlock this indicator",
				carcassYieldNote:
					"Produced arrobas computed at a {{percent}}% carcass yield (15 kg per @).",
				marketQuote: "Market: {{value}}/@",
				marketStale: "quote outdated",
				vsMarket: "vs market ({{value}}/@)",
				labels: {
					costPerKgGain: "Cost per kg gain",
					costPerArroba: "Cost per @ produced",
					costPerHead: "Cost per head",
					resultPerHectare: "Result per hectare",
					marginPerBreedingCow: "Margin per breeding cow",
					costPerWeanedCalf: "Cost per weaned calf",
					realizedPricePerKg: "Realized price per kg",
					realizedPricePerArroba: "Realized price per @",
					realizedPricePerHead: "Realized price per head",
					breakEvenPricePerKg: "Break-even price per kg",
					breakEvenPricePerArroba: "Break-even price per @",
				},
				inputs: {
					operatingCost: "Operating cost",
					operatingResult: "Operating result",
					revenue: "Revenue",
					weightGainKg: "Weight gain (kg)",
					arrobasProduced: "@ produced",
					arrobasSold: "@ sold",
					carcassYield: "Carcass yield",
					averageHeadCount: "Head count (avg)",
					weanedCalfCount: "Weaned calves",
					breedingFemaleCount: "Breeding females",
					pastureHectares: "Pasture hectares",
					soldWeightKg: "Sold weight (kg)",
					soldHeadCount: "Head sold",
					basis: "Basis",
					"livestock.measurements.weight": "weighings (min. 2 per animal)",
					"livestock.operations.weaning": "weanings",
					"livestock.animals": "animals",
					"livestock.animals.breedingFemale": "breeding females",
					"livestock.sales": "sales",
					"livestock.sales.weight": "sale weights",
					"grass.paddocks.area": "pasture areas",
				},
			},
		},
		payroll: {
			title: "Payroll",
			manage: "Manage",
			view: "View",
			notAuthorized: {
				title: "Access restricted",
				description: "You do not have permission to access payroll.",
			},
			employmentTypes: {
				MONTHLY: "Monthly",
				DAILY: "Daily",
				CONTRACTOR: "Contractor",
			},
			workers: {
				title: "Workers",
				new: "New worker",
				newShort: "Worker",
				newTitle: "New worker",
				editTitle: "Edit worker",
				perDay: "day",
				active: "Active",
				inactive: "Inactive",
				empty: {
					title: "No workers",
					description: "Add workers to build payroll runs.",
				},
				columns: {
					name: "Name",
					role: "Role",
					employmentType: "Type",
					rate: "Pay",
					status: "Status",
				},
				form: {
					name: "Name",
					cpf: "CPF",
					role: "Role",
					employmentType: "Employment type",
					baseSalary: "Base salary",
					dayRate: "Day rate",
					defaultEstablishment: "Default farm",
					noEstablishment: "None (organization)",
					isActive: "Active",
					isActiveHint: "Inactive workers are hidden from new runs.",
				},
			},
			runs: {
				new: "New run",
				newShort: "Run",
				newTitle: "New payroll run",
				addWorker: "Add worker",
				confirmedAt: "Confirmed at",
				empty: {
					title: "No payroll runs",
					description: "Create a run to record payments for a period.",
				},
				columns: {
					competency: "Competency",
					label: "Label",
					paymentDate: "Payment date",
					status: "Status",
				},
				status: {
					DRAFT: "Draft",
					CONFIRMED: "Confirmed",
				},
				form: {
					competency: "Competency",
					paymentDate: "Payment date",
					label: "Label",
					labelPlaceholder: "e.g. January payroll",
					defaultDaysWorked: "Default days worked (daily workers)",
				},
				grid: {
					worker: "Worker",
					daysWorked: "Days",
					gross: "Gross",
					encargos: "Charges",
					net: "Net",
					notes: "Notes",
					empty: "No items. Add workers to this run.",
					total: "Total",
					auto: "Auto",
					save: "Save items",
					grossRequired: "Enter a gross amount for every worker.",
				},
				confirm: {
					action: "Confirm",
					title: "Confirm payroll run",
					description:
						"Confirming posts the financial ledger entries and locks the run from further edits. Continue?",
					success: "Run confirmed",
				},
				revert: {
					action: "Revert",
					title: "Revert payroll run",
					description:
						"Reverting voids the posted ledger entries and returns the run to draft. Continue?",
					success: "Run reverted",
				},
			},
			labor: {
				title: "Labor report",
				from: "From",
				to: "To",
				empty: "No labor data in this period.",
				byWorker: "By worker",
				byFarm: "By farm",
				farm: "Farm",
				orgLevel: "Organization",
			},
		},
	},
	equipment: {
		nav: {
			title: "Equipment",
		},
		tabs: {
			dashboard: "Dashboard",
			fleet: "Fleet",
			parts: "Parts",
			schedule: "Schedule",
			inspections: "Inspections",
			details: "Details",
		},
		status: {
			ACTIVE: "Active",
			UNDER_REPAIR: "Under Repair",
			IDLE: "Idle",
			RETIRED: "Retired",
		},
		categories: {
			POWER_UNIT: "Tractor / Power Unit",
			HARVESTER: "Harvester",
			PLANTER: "Planter",
			SPRAYER: "Sprayer",
			TILLAGE: "Tillage",
			TRAILER: "Trailer",
			LOADER: "Loader",
			UTILITY_VEHICLE: "Utility Vehicle",
			IRRIGATION: "Irrigation",
			OTHER: "Other",
		},
		fuelTypes: {
			DIESEL: "Diesel",
			GASOLINE: "Gasoline",
			ELECTRIC: "Electric",
			HYBRID: "Hybrid",
			OTHER: "Other",
		},
		ownershipTypes: {
			OWNED: "Owned",
			LEASED: "Leased",
			RENTED: "Rented",
			SHARED: "Shared",
		},
		dashboard: {
			addEquipment: "Add Equipment",
			addPart: "Add Part",
			reportBreakdown: "Report Breakdown",
			totalActiveAssets: "Active Assets",
			fleetUptime: "Fleet Uptime",
			pmCompliance: "PM Compliance",
			openTasks: "Open Tasks",
			maintenanceDue: "Maintenance Due",
			noMaintenanceDue: "No maintenance due today",
			activeTasks: "Active Equipment Tasks",
			noActiveTasks: "No active equipment tasks",
			unassigned: "Unassigned",
			emptyTitle: "No Equipment Yet",
			emptyDescription:
				"Start building your fleet registry by adding your first piece of equipment.",
		},
		fleet: {
			assetId: "Asset ID",
			name: "Name",
			category: "Category",
			makeModel: "Make/Model",
			make: "Make",
			model: "Model",
			year: "Year",
			hours: "Hours",
			location: "Location",
			nextPm: "Next PM",
			create: {
				title: "Add Equipment",
			},
			edit: {
				title: "Edit Equipment",
			},
			notFound: "Equipment not found",
			confirmDelete: "Are you sure you want to delete this equipment?",
			list: {
				empty: {
					title: "No equipment found",
					description: "Add your first equipment to get started.",
				},
			},
		},
		form: {
			subCategory: "Sub-Category",
			serialNumber: "Serial Number",
			fuelType: "Fuel Type",
			horsepower: "Horsepower",
			initialHours: "Initial Hours",
			ownershipType: "Ownership",
			purchaseDate: "Purchase Date",
			purchasePrice: "Purchase Price",
			warrantyExpiry: "Warranty Expiry",
			dealer: "Dealer",
			pmIntervalHours: "PM Interval (Hours)",
			pmIntervalDays: "PM Interval (Days)",
			pmTemplateName: "PM Template",
			noEstablishments: "No farms registered yet",
			placeholders: {
				name: "e.g. Main Tractor",
				subCategory: "e.g. Row Crop",
				serialNumber: "e.g. 1LV6120MKRR000001",
				dealer: "e.g. Regional Equipment Co.",
				pmTemplateName: "e.g. Standard 250h Service",
				notes: "Additional notes about this equipment...",
			},
			sections: {
				identification: {
					title: "Identification",
					description: "Basic info to identify this equipment",
				},
				specifications: {
					title: "Specifications",
					description: "Technical specifications and capabilities",
				},
				ownership: {
					title: "Ownership & Location",
					description: "Ownership details and current location",
				},
				maintenance: {
					title: "Maintenance Setup",
					description: "Preventive maintenance schedule configuration",
				},
			},
		},
		detail: {
			totalTasks: "Total Tasks",
			inspections: "Inspections",
			lastInspection: "Last Inspection",
			totalMaintenanceCost: "Total Maintenance Cost",
		},
		parts: {
			partNumber: "Part Number",
			name: "Name",
			category: "Category",
			quantity: "Quantity",
			minimumStock: "Min. Stock",
			unitCost: "Unit Cost",
			unitOfMeasure: "Unit",
			location: "Storage Location",
			establishment: "Farm",
			central: "Central stock",
			oemPartNumbers: "OEM Part Numbers",
			adjustStock: "Adjust Stock",
			adjustQuantity: "Quantity Change",
			adjustQuantityPlaceholder: "e.g. 5 or -3",
			adjustQuantityHelp:
				"Use positive numbers to add stock, negative to remove.",
			adjustReason: "Reason",
			adjustReasonPlaceholder: "e.g. Restocked from supplier",
			stockMovements: "Stock Movements",
			noMovements: "No stock movements recorded yet.",
			lowStockAlert: "Low stock! Current: {{current}}, Minimum: {{minimum}}",
			notFound: "Part not found",
			confirmDelete: "Are you sure you want to delete this part?",
			create: {
				title: "Add Part",
			},
			edit: {
				title: "Edit Part",
			},
			list: {
				empty: {
					title: "No parts found",
					description: "Add your first part to track inventory.",
				},
			},
			summary: {
				totalParts: "Total Parts",
				lowStock: "Low Stock",
				inventoryValue: "Inventory Value",
			},
			form: {
				placeholders: {
					name: "e.g. Oil Filter",
					category: "e.g. Filters",
					oemPartNumbers: "e.g. RE504836, P550779",
					unitOfMeasure: "e.g. unit, liter, kg",
					location: "e.g. Shed A, Shelf 3",
				},
				sections: {
					identification: {
						title: "Identification",
						description: "Part identification and catalog info",
					},
					stock: {
						title: "Stock Management",
						description: "Inventory levels and pricing",
					},
					compatibility: {
						title: "Compatibility & Storage",
						description: "Equipment compatibility and storage location",
					},
				},
			},
		},
		inspection: {
			title: "Pre-Start Inspection",
			startInspection: "Start Inspection",
			hourMeter: "Hour Meter Reading",
			result: "Result",
			submittedBy: "Submitted By",
			submit: "Submit Inspection",
			pass: "Pass",
			defects: "{{count}} defect(s)",
			criticalDefects: "{{count}} critical",
			defectsFound: "defect(s) found",
			unsafeToOperate: "UNSAFE TO OPERATE — Critical defect detected",
			defectNotesPlaceholder: "Describe the defect...",
			severity: {
				LOW: "Low",
				MEDIUM: "Medium",
				HIGH: "High",
				CRITICAL: "Critical",
			},
			list: {
				empty: "No inspections recorded yet.",
			},
		},
		breakdown: {
			title: "Report Breakdown",
			reportBreakdown: "Report Breakdown",
			reportDetail: "Breakdown Report",
			selectEquipment: "Equipment",
			selectEquipmentPlaceholder: "Select equipment...",
			severity: "Severity",
			description: "Description",
			descriptionPlaceholder: "Describe the breakdown issue...",
			locationOnMachine: "Location on Machine",
			submit: "Submit Breakdown Report",
			equipment: "Equipment",
			linkedTask: "Linked Task",
			reportedBy: "Reported By",
			notFound: "Report not found",
			severityOptions: {
				CRITICAL: "Critical",
				HIGH: "High",
				MEDIUM: "Medium",
				LOW: "Low",
			},
			locations: {
				ENGINE: "Engine",
				TRANSMISSION: "Transmission",
				HYDRAULICS: "Hydraulics",
				ELECTRICAL: "Electrical",
				TIRES_WHEELS: "Tires/Wheels",
				BODY_FRAME: "Body/Frame",
				IMPLEMENT: "Implement",
				OTHER: "Other",
			},
		},
		schedule: {
			equipment: "Equipment",
			type: "Type",
			dueDate: "Due Date",
			noPmScheduled: "No PM scheduled",
			overdue: "Overdue",
			dueToday: "Due today",
			dueSoon: "Due in {{days}} days",
			dueThisWeek: "due this week",
			upcoming: "upcoming",
			statuses: {
				OVERDUE: "Overdue",
				DUE: "Due",
				UPCOMING: "Upcoming",
			},
			empty: {
				title: "No scheduled maintenance",
				description:
					"Maintenance schedules will appear here once equipment has PM intervals configured.",
			},
		},
	},
	marketplace: {
		nav: {
			title: "Marketplace",
		},
		browse: {
			title: "Marketplace",
			myOffers: "My offers",
			incomingRequests: "Incoming requests",
			myRequests: "My requests",
			loading: "Loading offers…",
			empty:
				"No published offers right now. Check back later or publish your own.",
			subtitle: "Buy and sell livestock across the network",
			search: "Search offers, breeds, sellers…",
			resultsCount: "{{count}} offer found",
			resultsCount_other: "{{count}} offers found",
			filters: "Filters",
			category: "Offer type",
			allKinds: "All types",
			productionCategory: "Category",
			allCategories: "All categories",
			state: "State",
			allStates: "All states",
			sex: "Sex",
			allSexes: "Any sex",
			priceRange: "Price range",
			animalCount: "Number of animals",
			weightRange: "Avg. weight (kg)",
			ageRange: "Age (months)",
			health: "Certifications",
			allHealth: "Any certification",
			healthSelected: "{{count}} certification",
			healthSelected_other: "{{count}} certifications",
			breed: "Breed",
			allBreeds: "All breeds",
			breedsSelected: "{{count}} breed selected",
			breedsSelected_other: "{{count}} breeds selected",
			min: "Min",
			max: "Max",
			sort: "Sort by",
			sortRelevant: "Most relevant",
			sortRecent: "Newest first",
			sortPriceAsc: "Lowest price",
			sortPriceDesc: "Highest price",
			viewGrid: "Grid view",
			viewList: "List view",
			contact: "Contact",
			save: "Save",
			clearFilters: "Clear filters",
			noResults: "No offers match your filters.",
			newBadge: "New",
		},
		offerCategory: {
			FEEDER: "Feeder",
			BREEDING_FEMALE: "Breeding female",
			BREEDING_BULL: "Breeding bull",
			SLAUGHTER: "Slaughter",
			CALF_WEANER: "Calf / weaner",
			CULL: "Cull",
			DAIRY: "Dairy",
			OTHER: "Other",
		},
		healthFlag: {
			FMD_VACCINATED: "FMD vaccinated",
			BRUCELLOSIS_VACCINATED: "Brucellosis vaccinated",
			BRUCELLOSIS_TESTED: "Brucellosis tested",
			TUBERCULOSIS_TESTED: "Tuberculosis tested",
			GTA_READY: "GTA ready",
		},
		sex: {
			MALE: "Male",
			FEMALE: "Female",
			MIXED: "Mixed",
		},
		kind: {
			WHOLE_HERD: "Herd",
			ANIMALS: "Animals",
		},
		headCount: "{{n}} head",
		offer: {
			notFound: "Offer not found.",
			back: "Marketplace",
			requestToBuy: "Request to buy",
			close: "Close",
			animals: "Animals ({{n}})",
		},
		detail: {
			about: "About this offer",
			details: "Details",
			attrType: "Offer type",
			attrHeadCount: "Head count",
			attrPricing: "Pricing model",
			attrTotalWeight: "Total weight",
			attrUnitPrice: "Unit price",
			attrPublished: "Published",
			attrExpires: "Available until",
			noDescription: "The seller didn't add a description for this offer.",
			seller: "Seller",
			verifiedSeller: "Registered seller",
			memberInfo: "Listed on RuralOps",
			whyTitle: "About buying here",
			whyTracked: "Animals tracked individually by tag",
			whySnapshot: "Herd snapshot frozen at publish time",
			whyTransfer: "Transfer handled inside the platform",
			safetyTitle: "Safety tips",
			safetyBody:
				"Inspect the animals and confirm documentation before sending any payment.",
			share: "Share",
			weightUnit: "{{n}} kg",
			contactSeller: "Contact seller",
			contactHint: "The seller's contact details are in the offer description.",
		},
		request: {
			title: "Request to buy",
			destinationFarm: "Destination farm",
			selectFarm: "Select a farm…",
			destinationHerd: "Destination herd (optional)",
			noSpecificHerd: "No specific herd",
			message: "Message (optional)",
			send: "Send request",
		},
		myOffers: {
			title: "My offers",
			back: "Back to marketplace",
			create: "Create whole-herd offer",
			empty:
				"You have no offers yet. Create a whole-herd offer to start selling.",
			publish: "Publish",
			resync: "Re-sync",
			requests: "Requests",
			subtitle: "Manage your listings and track buyer interest",
			allStatuses: "All",
			expiresIn: "Expires in {{n}}d",
			expired: "Expired",
			emptyTitle: "No offers yet",
			emptyHint:
				"Create a whole-herd offer to start selling to buyers across the network.",
			emptyCta: "Create your first offer",
			edit: "Edit",
		},
		createOffer: {
			title: "New whole-herd offer",
			herd: "Herd",
			selectHerd: "Select a herd…",
			titleLabel: "Title",
			titlePlaceholder: "e.g. 40 Nelore steers",
			pricingModel: "Pricing model",
			unitPrice: "Unit price",
			category: "Category",
			selectCategory: "Select a category…",
			state: "State",
			selectState: "Select a state…",
			healthFlags: "Certifications",
			submit: "Create draft",
			step1: "Details",
			step2: "Photos",
			photos: "Photos",
			photosHelper:
				"Add photos so buyers can see the animals. JPEG, PNG or WebP, up to 10 MB each.",
			addPhoto: "Add photo",
			noPhotos: "No photos added yet.",
			draftSaved: "Draft saved. Add photos, then publish your offer.",
			publishNow: "Publish offer",
			saveDraft: "Save as draft",
			removePhoto: "Remove photo",
			subtitle: "List your herd to buyers across the network",
			sectionBasics: "Herd & title",
			sectionPricing: "Pricing",
			sectionClassification: "Classification",
			continue: "Continue to photos",
			previewTitle: "Live preview",
			previewHint: "This is how buyers will see your listing",
			previewEmptyTitle: "Your offer title",
			cover: "Cover",
			dropzoneTitle: "Drag photos here or click to upload",
			dropzoneHint: "JPEG, PNG or WebP · up to 10 MB · max {{max}} photos",
			photoCount: "{{n}}/{{max}} photos",
			stepHintDetails: "Tell buyers what you're selling",
			stepHintPhotos: "Photos sell — add a few clear shots",
			editTitle: "Edit offer",
			editSubtitle: "Update your listing details",
			description: "Description",
			descriptionPlaceholder: "Add details buyers should know…",
			descriptionHint:
				"Include your contact details (phone, WhatsApp, email) so buyers can reach you directly.",
			save: "Save changes",
			herdLocked:
				"The herd and animals can't be changed here — use Re-sync to refresh the set.",
			herdInOffer: "already listed",
			viewOffer: "View offer",
			notFound: "Offer not found.",
		},
		incoming: {
			title: "Incoming requests",
			offer: "Offer",
			selectOffer: "Select one of your offers…",
			loading: "Loading requests…",
			empty: "No requests on this offer yet.",
			accept: "Accept…",
			reject: "Reject",
			confirmIntro:
				"This transfers {{n}} animal(s) to {{buyer}}. This is final.",
			confirmHint: "Type the buyer organization name to confirm.",
			confirmTransfer: "Confirm transfer",
			subtitle: "Approve or decline buyers requesting your herds",
			pickTitle: "Select an offer to begin",
			pickHint:
				"Choose one of your offers above to review the buyers who want it.",
			emptyTitle: "No requests yet",
			emptyHint:
				"When a buyer requests this offer, it'll appear here for you to accept or decline.",
			noOffersTitle: "You have no offers yet",
			noOffersHint: "Publish an offer and buyer requests will land right here.",
			noOffersCta: "Go to my offers",
			all: "All",
			pending: "{{n}} pending",
			requested: "Requested {{date}}",
			expiresIn: "Expires in {{n}}d",
		},
		myRequests: {
			title: "My purchase requests",
			empty: "You haven't requested to buy any offers yet.",
			requestLabel: "Request {{id}}",
		},
		status: "Status: {{status}}",
		offerStatus: {
			DRAFT: "Draft",
			PUBLISHED: "Published",
			RESERVED: "Reserved",
			SOLD: "Sold",
			CANCELLED: "Cancelled",
			EXPIRED: "Expired",
		},
		requestStatus: {
			PENDING: "Pending",
			ACCEPTED: "Accepted",
			REJECTED: "Rejected",
			CANCELLED: "Cancelled",
			EXPIRED: "Expired",
		},
	},
	media: {
		upload: {
			button: "Upload Image",
			title: "Upload Image",
			helper: "JPEG, PNG or WebP. Max 10 MB.",
			chooseFile: "Choose file",
			noFileChosen: "No file selected",
			submit: "Upload",
			uploading: "Uploading...",
			preparing: "Preparing upload...",
			// Neutral wording: this toast is fired by the shared upload hook, which
			// also serves PDF-capable surfaces (e.g. the soil lab-report import).
			success: "File uploaded successfully",
			failed: "Upload failed. Please try again.",
			helperWithPdf: "JPEG, PNG, WebP or PDF. Max 10 MB.",
			unsupportedTypeWithPdf:
				"Unsupported file type. Use JPEG, PNG, WebP or PDF.",
			tooLarge: "File is too large. Max size is 10 MB.",
			unsupportedType: "Unsupported file type. Use JPEG, PNG or WebP.",
			validation: {
				fileRequired: "Select a file to upload.",
			},
			analysisReasons: {
				title: "AI analysis (optional)",
				empty: "No analysis selected — the file will be stored only.",
				counter: "{{count}}/{{max}} selected",
				capReached: "Maximum {{max}} analyses per upload",
				labels: {
					ANIMAL_BODY_CONDITION_SCORE: "Body condition score",
					ANIMAL_LAMENESS_CHECK: "Lameness check",
					ANIMAL_HEALTH_INDICATORS: "Health indicators",
					ANIMAL_BREED_IDENTIFICATION: "Breed identification",
					PADDOCK_PASTURE_QUALITY: "Pasture quality",
					PADDOCK_WEED_DETECTION: "Weed detection",
					PADDOCK_FENCE_CONDITION: "Fence condition",
					PADDOCK_COVERAGE_ESTIMATION: "Coverage estimation",
					EQUIPMENT_WEAR_CHECK: "Wear check",
					EQUIPMENT_DAMAGE_DETECTION: "Damage detection",
					EQUIPMENT_MAINTENANCE_INDICATORS: "Maintenance indicators",
					SOIL_LAB_REPORT_EXTRACTION: "Soil lab report extraction",
					TRANSACTION_RECEIPT_EXTRACTION: "Receipt extraction",
					ANIMAL_LIST_IMPORT_EXTRACTION: "Animal list import",
				},
				descriptions: {
					ANIMAL_BODY_CONDITION_SCORE:
						"Estimate the animal's body condition score from the image.",
					ANIMAL_LAMENESS_CHECK:
						"Flag visible signs of lameness or gait issues.",
					ANIMAL_HEALTH_INDICATORS:
						"Detect visible health indicators (coat, eyes, posture).",
					ANIMAL_BREED_IDENTIFICATION:
						"Identify the most likely breed or breed mix.",
					PADDOCK_PASTURE_QUALITY:
						"Assess pasture quality, density and colour.",
					PADDOCK_WEED_DETECTION:
						"Detect presence of visible weeds in the paddock.",
					PADDOCK_FENCE_CONDITION:
						"Assess visible condition of fences in the image.",
					PADDOCK_COVERAGE_ESTIMATION: "Estimate ground coverage percentage.",
					EQUIPMENT_WEAR_CHECK: "Detect visible wear on the equipment.",
					EQUIPMENT_DAMAGE_DETECTION: "Detect visible damage on the equipment.",
					EQUIPMENT_MAINTENANCE_INDICATORS:
						"Surface visible indicators that maintenance is needed.",
					SOIL_LAB_REPORT_EXTRACTION:
						"Extract the structured soil-analysis results from a lab report (PDF or photo), one entry per sample.",
					TRANSACTION_RECEIPT_EXTRACTION:
						"Extract amount, date, issuer and suggested category from a receipt/nota (PDF or photo) to pre-fill a manual entry.",
					ANIMAL_LIST_IMPORT_EXTRACTION:
						"Transcribe one chunk of a herd list (spreadsheet, PDF or photo) into animal records for review before import.",
				},
			},
		},
		gallery: {
			title: "Images",
			empty: "No images yet.",
			emptyWithHint:
				"No images yet. Photos help identify the animal and track body condition.",
			loadError: "Failed to load images.",
			uploadedBy: "by {{name}}",
			unknownUploader: "Unknown",
		},
		viewer: {
			openImage: "Open {{name}}",
			viewLabel: "View",
			imageIdLabel: "Image ID",
			copyId: "Copy image ID",
			idCopied: "Image ID copied",
			idCopyFailed: "Could not copy ID",
			analysesTitle: "AI analyses",
			analysesSubtitle_one: "{{count}} analysis attached",
			analysesSubtitle_other: "{{count}} analyses attached",
			analysesSubtitle: "{{count}} analyses attached",
			noAnalyses: "No analyses on this image yet",
			noAnalysesHelper:
				"Add an AI analysis to extract insights from this photo.",
		},
		analyses: {
			statuses: {
				PENDING: "Queued",
				IN_PROGRESS: "Analyzing",
				COMPLETED: "Done",
				FAILED: "Failed",
			},
			modal: {
				pendingTitle: "Queued for analysis",
				pendingBody: "This analysis hasn't started yet.",
				inProgressTitle: "Analysis in progress",
				inProgressBody: "The result will appear here when it's ready.",
				errorTitle: "Analysis failed",
				emptyResult: "No result was returned.",
				attemptCount: "Attempt {{count}}",
				close: "Close",
			},
			add: {
				button: "Add analysis",
				title: "Add AI analysis",
				helper:
					"Pick one or more analyses to run on this image. Runs happen in the background.",
				pickerTitle: "Additional analyses",
				submit: "Run analyses",
				submitting: "Starting…",
				successToast: "Analyses queued",
				errorToast: "Failed to queue analyses",
				capReached: "All analyses have already been attached to this image.",
				validation: {
					selectReason: "Select at least one analysis.",
				},
			},
			retry: {
				button: "Retry",
				submitting: "Retrying…",
				successToast: "Analysis requeued",
				errorToast: "Failed to retry analysis",
				notRetriable: "This analysis cannot be retried right now.",
				availableIn: "Retry available in {{minutes}} min",
			},
		},
	},
	theme: {
		select: "Select theme",
		light: "Light",
		dark: "Dark",
	},
	planning: {
		tasks: {
			status: {
				pending: "Pending",
				approved: "Approved",
				scheduled: "Scheduled",
				in_progress: "In Progress",
				completed: "Completed",
				cancelled: "Cancelled",
			},
			priority: {
				critical: "Critical",
				high: "High",
				medium: "Medium",
				low: "Low",
			},
			voice: {
				play: "Play task audio",
				stop: "Stop audio",
				error: "Audio unavailable",
				// MUST match the server schedule clause (voiceTemplates) so the
				// Web-Speech fallback text is identical to the synthesized text.
				scheduleClause: "Scheduled for {{date}}",
			},
			kanbanView: "Kanban View",
			kanbanDescription: "Organize tasks by status with drag and drop",
			listView: "List View",
			calendarView: "Calendar View",
			selectEstablishment: "Select Establishment",
			selectEstablishmentPrompt: "Select an establishment to view tasks",
			markComplete: "Mark as Complete",
			completeQueued:
				"Completion saved — it will sync when you're back online.",
			recordPrompt: "Task completed. Record {{type}} for the herd records?",
			recordPromptAction: "Record",
			count: "{{count}} task",
			count_other: "{{count}} tasks",
			create: "Create Task",
			quickAdd: "Quick Add Task",
			quickAddDescription: "Add a new task quickly",
			emptyInScope: "No tasks here yet",
			form: {
				title: "Title",
				titlePlaceholder: "Enter task title...",
				description: "Description",
				descriptionPlaceholder: "Describe what needs to be done...",
				scheduledDate: "Scheduled Date",
				estimatedDuration: "Estimated Duration (min)",
				priority: "Priority",
			},
			detail: {
				scheduledDate: "Scheduled",
				dueDate: "Due Date",
				priority: "Priority",
				duration: "Duration",
				establishment: "Establishment",
				assignedTo: "Assigned To",
				createdBy: "Created By",
				approvedBy: "Approved By",
				domain: "Module",
				objective: "Objective",
				completedAt: "Completed on {{date}}",
				completedBy: "by {{name}}",
			},
		},
		assignee: {
			unassigned: "Unassigned",
		},
		comments: {
			title: "Comments",
			empty: "No comments yet",
			placeholder: "Write a comment...",
			post: "Comment",
			added: "Comment added",
		},
		panel: {
			details: "Details",
			addDescription: "Add a description…",
			subObjective: "Sub-objective",
			breadcrumb: {
				objective: "Objective",
				subObjective: "Sub-objective",
				task: "Task",
			},
		},
		subObjectives: {
			label: "Sub-objective (optional)",
			none: "None — attach directly to the objective",
			title: "Sub-objectives",
			create: "Create sub-objective",
			editTitle: "Edit sub-objective",
			empty: "No sub-objectives yet",
		},
		createMenu: {
			title: "Create",
			description: "What do you want to create?",
			objective: "Objective",
			subObjective: "Sub-objective",
			task: "Task",
			needsObjective: "Create an objective first",
			parentTitle: "Select the objective",
			parentDescription: "A sub-objective belongs to an objective",
		},
		hierarchy: {
			title: "Objectives",
			allTasks: "All tasks",
			showAll: "Show All",
			newObjective: "New objective",
			addSubObjective: "Add sub-objective",
			addTask: "Add task here",
			empty: "No objectives yet",
			emptyDescription:
				"Objectives group your sub-objectives and tasks. Create one to get started.",
			browse: "Browse objectives",
			deleteObjectiveTitle: "Delete objective?",
			deleteObjectiveDescription:
				'Delete "{{name}}"? Its sub-objectives and tasks may also be removed. This can\'t be undone.',
			deleteSubObjectiveTitle: "Delete sub-objective?",
			deleteSubObjectiveDescription:
				'Delete "{{name}}"? Its tasks may also be removed. This can\'t be undone.',
		},
		objectives: {
			createSuccess: "Objective created successfully",
			tacticalCount: "{{count}} tactical objective",
			tacticalCount_other: "{{count}} tactical objectives",
			annual: {
				createSuccess: "Annual objective created successfully",
				createError: "Failed to create annual objective",
				updateSuccess: "Annual objective updated successfully",
				updateError: "Failed to update annual objective",
				deleteSuccess: "Annual objective deleted successfully",
				deleteError: "Failed to delete annual objective",
			},
			tactical: {
				createSuccess: "Tactical objective created successfully",
				createError: "Failed to create tactical objective",
				updateSuccess: "Tactical objective updated successfully",
				updateError: "Failed to update tactical objective",
				deleteSuccess: "Tactical objective deleted successfully",
				deleteError: "Failed to delete tactical objective",
			},
		},
	},
	mcp: {
		nav: {
			title: "MCP",
		},
		page: {
			eyebrow: "Settings",
			title: "MCP",
			subtitle:
				"Connect any AI agent — such as Claude, ChatGPT or Cursor — to your farm's data and operation.",
			intro:
				"MCP is an address you connect to the AI assistant you already use. After you sign in, it can look up your animals and herds, count them, and record handling events, always respecting the same access permissions as your RuralOps account.",
		},
		url: {
			label: "Address to paste into your assistant",
			copy: "Copy",
			copied: "Copied",
			copyFailed: "Could not copy",
			addToCursor: "Add to Cursor",
		},
		connect: {
			title: "How to connect",
			after:
				"After you sign in, each user keeps the access permissions of their own account. The assistant respects those permissions and only reaches the information that user is already allowed to see.",
			copyConfig: "Cursor config",
			claude: {
				name: "Claude",
				pathSettings: "Settings",
				pathConnectors: "Connectors",
				pathAddConnector: "Add custom connector",
				step1: {
					title: "Copy the RuralOps address",
					description:
						"You will paste this address into Claude in the next step.",
				},
				step2: {
					title: "Add it as a custom connector",
					description: "In Claude Desktop or on claude.ai, open:",
				},
				step3: {
					title: "Connect and sign in",
					description:
						"Click Connect, sign in to RuralOps and approve what the assistant may see.",
				},
			},
			cursor: {
				name: "Cursor",
				pathSettings: "Settings",
				pathTools: "Tools & MCP",
				pathNewServer: "New MCP Server",
				step1: {
					title: "Copy the RuralOps address",
					description: "You will paste this address into Cursor.",
				},
				step2: {
					title: "Add a new MCP server",
					description: "In Cursor, open the settings and paste the address:",
				},
				step3: {
					title: "Sign in and start",
					description:
						"Cursor opens the RuralOps sign-in page. Approve the access and you are done.",
				},
			},
			chatgpt: {
				name: "ChatGPT",
				step1: {
					title: "Copy the RuralOps address",
					description: "You will paste this address into ChatGPT.",
				},
				step2: {
					title: "Add it under Connectors",
					description:
						"In ChatGPT settings, open Connectors (developer mode) and add the address as a remote MCP server.",
				},
				step3: {
					title: "Sign in and start",
					description:
						"Sign in to RuralOps when ChatGPT opens the sign-in page.",
				},
			},
			claudeCode: {
				name: "Claude Code",
				step1: {
					title: "Copy the command",
					description: "You will run this command in your terminal.",
				},
				step2: {
					title: "Run it in the terminal",
					description: "It adds RuralOps as an HTTP MCP server in Claude Code.",
				},
				step3: {
					title: "Sign in",
					description:
						"Run /mcp, pick ruralops and finish the sign-in in your browser.",
				},
			},
			grok: {
				name: "Grok Bot",
				step1: {
					title: "Copy the RuralOps address",
					description: "You will paste this address into Grok Bot.",
				},
				step2: {
					title: "Add it as an MCP server",
					description:
						"In Grok Bot, open the MCP settings and add the address as a remote server.",
				},
				step3: {
					title: "Sign in and start",
					description:
						"Sign in to RuralOps when Grok Bot opens the sign-in page.",
				},
			},
		},
		features: {
			title: "What a connected agent can do",
			farms: "List the organizations and farms you belong to (names and ids).",
			counts:
				"Count animals per farm — totals, status, sex of the live herd, and how many sit outside any herd.",
			animals:
				"List herds and animals, including animals with no herd, and look up one animal.",
			records:
				"Read handling records (vaccinations, treatments, weaning, pasture moves, and the other types you already log in the app).",
			write:
				"Record a handling event on a herd or an animal, after the assistant asks you to confirm. Same permission rules as tapping the button in the app.",
		},
		limits: {
			title: "What it will not do",
			otherDomains:
				"It will not read or write finances, staff, billing, or the marketplace.",
			noCost:
				"It cannot put a cost on a handling record. A cost posts to the farm ledger, and that stays something you enter in the app.",
			noDelete: "It will not delete anything, ever.",
			noSignup:
				"It will not create a RuralOps account. You must already have signed in to the app at least once.",
			noPush:
				"It will not push updates on its own. The assistant asks; RuralOps answers.",
		},
	},
	proposals: {
		nav: {
			title: "Pending Actions",
		},
		page: {
			title: "Pending Actions",
			subtitle: "Review and approve changes the assistant suggests",
			noAccess: "You don't have permission to review AI proposals.",
			emptyTitle: "Nothing to review",
			emptyDescription: "No proposals match this filter.",
			// An empty default view is an achievement, not a dead end.
			emptyInboxTitle: "All reviewed",
			emptyInboxDescription:
				"When the assistant suggests a change, it shows up here.",
			reassurance:
				"Nothing has been written yet. The assistant only suggests. A change reaches your farm when you approve it.",
			pendingCount_one: "{{count}} waiting for review",
			pendingCount_other: "{{count}} waiting for review",
			emptyOtherPending_one:
				"There is still {{count}} proposal waiting for review under another filter.",
			emptyOtherPending_other:
				"There are still {{count}} proposals waiting for review under another filter.",
		},
		pager: {
			label: "Proposal pages",
			previous: "Previous",
			next: "Next",
			position: "Page {{page}} of {{total}}",
		},
		explainer: {
			title: "How proposals work",
			reversibility:
				"Dropping a change is reversible: you can put it back whenever you like. Approving is not.",
			grouped: "Changes marked “Applied together” always go in or out as one.",
		},
		filter: {
			label: "Filter by status",
			all: "All statuses",
			more: "More statuses",
			less: "Fewer statuses",
		},
		status: {
			PENDING: "Pending",
			EXECUTED: "Approved",
			REJECTED: "Rejected",
			EXPIRED: "Expired",
		},
		origin: {
			CHAT: "Chat",
			ROUTINE: "Routine",
			ANALYSIS: "Analysis",
		},
		actions: {
			livestockMeasurementCreate: "Create livestock measurement",
			livestockMeasurementUpdate: "Update livestock measurement",
			livestockAnimalCreate: "Register livestock animal",
			livestockAnimalUpdate: "Update livestock animal",
			planningTaskCreate: "Create task",
			planningTaskUpdate: "Update task",
			planningTaskComplete: "Complete task",
			grassPaddockCreate: "Create paddock",
			grassPaddockUpdate: "Update paddock",
			grassOperationCreate: "Record pasture operation",
			grassOperationUpdate: "Update pasture operation",
			grassMeasurementCreate: "Record pasture measurement",
			grassMeasurementUpdate: "Update pasture measurement",
			equipmentCreate: "Register equipment",
			equipmentUpdate: "Update equipment",
			equipmentPartCreate: "Add part to inventory",
			equipmentPartUpdate: "Update part",
			soilAnalysisCreate: "Record soil analysis",
			soilAnalysisUpdate: "Update soil analysis",
		},
		// Payload field labels, keyed by FIELD name (not by full path): `notes`
		// means the same thing on a measurement and on a task, so one translation
		// serves every Action Type. A missing key falls back to the field name.
		fields: {
			targetId: "Record",
			changes: "Changes",
			establishmentId: "Farm",
			herdId: "Herd",
			animalId: "Animal",
			tagId: "Tag",
			electronicId: "Electronic ID",
			registrationNumber: "Registration number",
			sex: "Sex",
			birthDate: "Birth date",
			birthWeight: "Birth weight",
			breed: "Breed",
			coatColor: "Coat colour",
			originType: "Origin",
			acquisitionDate: "Acquisition date",
			acquisitionCost: "Acquisition cost",
			supplier: "Supplier",
			sireId: "Sire",
			damId: "Dam",
			sireTagId: "Sire tag",
			damTagId: "Dam tag",
			hornStatus: "Horn status",
			isCastrated: "Castrated",
			tags: "Tags",
			measurementType: "Measurement type",
			measurementDate: "Measurement date",
			numericValue: "Value",
			unit: "Unit",
			description: "Description",
			expectedCalvingDate: "Expected calving date",
			notes: "Notes",
			objectiveId: "Objective",
			subObjectiveId: "Sub-objective",
			title: "Title",
			priority: "Priority",
			scheduledDate: "Scheduled date",
			dueDate: "Due date",
			// The unit lives on the VALUE (`45 min`), not in the label — see
			// `VALUE_SUFFIX` in payloadFormatting.ts.
			estimatedDurationMin: "Estimated duration",
			actualDurationMin: "Actual duration",
			assignedToId: "Assigned to",
			domain: "Domain",
			operationType: "Operation type",
			domainData: "Domain data",
			executionNotes: "Execution notes",
			paddockId: "Paddock",
			name: "Name",
			code: "Code",
			areaHa: "Area (ha)",
			boundaryGeoJson: "Boundary",
			slope: "Slope (%)",
			drainage: "Drainage",
			soilType: "Soil type",
			useType: "Use type",
			status: "Status",
			yieldTargetTDMHaYear: "Yield target (t DM/ha/year)",
			cpMinPercent: "Minimum crude protein (%)",
			ndfMaxPercent: "Maximum NDF (%)",
			utilizationTarget: "Utilisation target (%)",
			cultivarIds: "Cultivars",
			performedAt: "Performed at",
			costTotal: "Total cost",
			operationData: "Operation detail",
			animalUnits: "Animal units",
			grazingDays: "Grazing days",
			preGrazingHeightCm: "Pre-grazing height (cm)",
			postGrazingHeightCm: "Post-grazing height (cm)",
			taskId: "Task",
			measuredAt: "Measured at",
			swardHeightCm: "Sward height (cm)",
			groundCoverPct: "Ground cover (%)",
			biomassKgHa: "Biomass (kg DM/ha)",
			regrowthDays: "Regrowth (days)",
			biomassMethod: "Biomass method",
			biomassUncertaintyPct: "Biomass uncertainty (%)",
			crudeProteinPct: "Crude protein (%)",
			ndfPct: "NDF (%)",
			adfPct: "ADF (%)",
			digestibilityPct: "Digestibility (%)",
			photoUrls: "Photos",
			gpsLatitude: "Latitude",
			gpsLongitude: "Longitude",
			// Equipment (fleet)
			locationId: "Farm",
			assetId: "Asset code",
			subCategory: "Sub-category",
			make: "Make",
			model: "Model",
			year: "Year",
			serialNumber: "Serial number",
			initialHours: "Initial hours",
			fuelType: "Fuel",
			horsepower: "Horsepower",
			ownershipType: "Ownership",
			purchaseDate: "Purchase date",
			purchasePrice: "Purchase price",
			warrantyExpiry: "Warranty expiry",
			dealer: "Dealer",
			pmIntervalHours: "PM interval (hours)",
			pmIntervalDays: "PM interval (days)",
			pmTemplateName: "PM schedule",
			assignedOperatorId: "Assigned operator",
			// Parts inventory
			partNumber: "Part number",
			oemPartNumbers: "OEM part numbers",
			currentQuantity: "Quantity in stock",
			minimumStock: "Minimum stock",
			unitCost: "Unit cost",
			location: "Bin location",
			unitOfMeasure: "Unit of measure",
			// Soil analysis
			sampledAt: "Sampled at",
			// The two depths are ONE layer; the pair becomes the `depth` row.
			depth: "Depth",
			depthTopCm: "Depth from",
			depthBottomCm: "Depth to",
			labEntradaAt: "Lab received at",
			labSaidaAt: "Lab issued at",
			labName: "Laboratory",
			labReportNumber: "Report number",
			results: "Results",
			availableWaterCapacityMm: "Available water capacity",
			methods: "Extraction methods",
			context: "Interpretation context",
			interpretationSystem: "Interpretation system",
			labUnitPreset: "Lab unit format",
			soilTypeCode: "Soil type",
			clayActivity: "Clay activity",
			raw: "Lab-specific fields",
			phWater: "pH (water)",
			phCaCl2: "pH (CaCl₂)",
			phKCl: "pH (KCl)",
			organicMatter: "Organic matter",
			organicCarbon: "Organic carbon",
			phosphorusMehlich: "P (Mehlich)",
			phosphorusResin: "P (resin)",
			phosphorusMehlich3: "P (Mehlich-3)",
			phosphorusRemaining: "Remaining P",
			phosphorusTotal: "Total P",
			potassium: "Potassium",
			sodium: "Sodium",
			calcium: "Calcium",
			magnesium: "Magnesium",
			aluminum: "Aluminium",
			hydrogenAluminum: "H + Al",
			sulfur: "Sulfur",
			boron: "Boron",
			copper: "Copper",
			iron: "Iron",
			manganese: "Manganese",
			zinc: "Zinc",
			clay: "Clay",
			silt: "Silt",
			sand: "Sand",
		},
		// Payload code values, keyed by FIELD name then by code — the same
		// addressing `proposals.fields.*` uses, so a new Action Type reusing the
		// field inherits the words. A code with no key falls back to the raw value
		// (today's behaviour), so partial coverage is safe. `common` holds codes
		// that mean the same thing on every field.
		values: {
			common: {
				ns: "Not requested",
			},
			measurementType: {
				WEIGHT: "Weight",
				BODY_CONDITION_SCORE: "Body condition score",
				HIP_HEIGHT: "Hip height",
				SCROTAL_CIRCUMFERENCE: "Scrotal circumference",
				BODY_TEMPERATURE: "Body temperature",
				PREGNANCY_CHECK: "Pregnancy check",
				CALVING: "Calving",
				BLOOD_EXAM: "Blood exam",
				GENERAL_EXAMINATION: "General examination",
			},
			domain: {
				livestock: "Livestock",
				grass: "Pasture",
				general: "General",
			},
			// 1 is the HIGHEST (see PRIORITY_OPTIONS in planning/-components).
			priority: {
				"1": "Critical",
				"2": "High",
				"3": "Medium",
				"4": "Low",
			},
			status: {
				PENDING: "Pending",
				APPROVED: "Approved",
				SCHEDULED: "Scheduled",
				IN_PROGRESS: "In progress",
				COMPLETED: "Completed",
				CANCELLED: "Cancelled",
				ACTIVE: "Active",
				SOLD: "Sold",
				DECEASED: "Dead",
				TRANSFERRED: "Transferred",
				ESTABLISHED: "Established",
				RENOVATION: "Under renovation",
				DEGRADED: "Degraded",
				REST: "Resting",
				DEFERRED: "Deferred",
				UNDER_RECOVERY: "Under recovery",
				UNDER_REPAIR: "Under repair",
				IDLE: "Idle",
				RETIRED: "Retired",
			},
			sex: {
				MALE: "Male",
				FEMALE: "Female",
			},
			originType: {
				born_on_farm: "Born on farm",
				purchased: "Purchased",
			},
			hornStatus: {
				HORNED: "Horned",
				POLLED: "Polled",
				DEHORNED: "Dehorned",
				SCURRED: "Scurred",
			},
			useType: {
				ROTATIONAL_GRAZING: "Rotational grazing",
				CONTINUOUS_GRAZING: "Continuous grazing",
				CUT_AND_CARRY: "Cut and carry",
				HAY: "Hay production",
				SILAGE: "Silage production",
				DEFERRED_STOCKPILING: "Deferred/stockpiling",
				MIXED: "Mixed use",
			},
			operationType: {
				GRAZING: "Grazing",
				CUTTING: "Cutting",
				MOWING: "Mowing",
				FERTILIZATION: "Fertilization",
				LIMING: "Liming",
				IRRIGATION: "Irrigation",
				WEED_CONTROL: "Weed control",
				PEST_CONTROL: "Pest control",
				RENOVATION: "Renovation",
				SOIL_PREPARATION: "Soil preparation",
				VACCINATION: "Vaccination",
				TREATMENT: "Treatment",
				DISEASE: "Disease",
				MORTALITY: "Mortality",
				FEEDING_PLAN: "Feeding plan",
				SUPPLEMENTATION: "Supplementation",
				GRAZING_ROTATION: "Grazing rotation",
				WEANING: "Weaning",
				CASTRATION: "Castration",
				DEHORNING: "Dehorning",
				IDENTIFICATION: "Identification",
				HOOF_TRIMMING: "Hoof trimming",
				CLEANING: "Cleaning",
				ARTIFICIAL_INSEMINATION: "Artificial insemination",
				SALE: "Sale",
				HEAT_DETECTION: "Heat detection",
				NATURAL_BREEDING: "Natural breeding",
				PREGNANCY_DIAGNOSIS: "Pregnancy diagnosis",
				ABORTION: "Abortion",
				CALVING: "Calving",
				OTHER: "Other",
			},
			biomassMethod: {
				HEIGHT_DENSITY_EQUATION: "Height-density equation",
				RISING_PLATE_METER: "Rising plate meter",
				VISUAL_ESTIMATE: "Visual estimate",
				CUT_AND_WEIGH: "Cut and weigh",
				NIRS_SENSOR: "NIRS sensor",
				SATELLITE_NDVI: "Satellite NDVI",
			},
			category: {
				POWER_UNIT: "Tractor / power unit",
				HARVESTER: "Harvester",
				PLANTER: "Planter",
				SPRAYER: "Sprayer",
				TILLAGE: "Tillage",
				TRAILER: "Trailer",
				LOADER: "Loader",
				UTILITY_VEHICLE: "Utility vehicle",
				IRRIGATION: "Irrigation",
				OTHER: "Other",
			},
			fuelType: {
				DIESEL: "Diesel",
				GASOLINE: "Gasoline",
				ELECTRIC: "Electric",
				HYBRID: "Hybrid",
				LPG: "LPG",
				NONE: "No engine",
			},
			ownershipType: {
				OWNED: "Owned",
				LEASED: "Leased",
				RENTED: "Rented",
				SHARED: "Shared",
			},
			interpretationSystem: {
				IAC: "IAC (São Paulo)",
				CFSEMG: "CFSEMG (Minas Gerais)",
				EMBRAPA_CERRADO: "Embrapa Cerrado",
			},
		},
		editor: {
			clear: "Clear",
			undoClear: "Undo clear",
			cleared: "Will be cleared",
			addItem: "Add",
			removeItem: "Remove",
			noneOption: "None",
			immutable: "Fixed",
			previewOnly: "Read-only",
		},
		conflicts: {
			staleVersion:
				"This card changed since you opened it. It has been reloaded — review it again before approving.",
			targetChanged:
				"The record this change edits was modified by someone else. Review the current values before approving.",
			actionDisabled:
				"This kind of change is currently switched off. It can't be approved until an administrator turns it back on.",
			limitExceeded:
				"Your plan allows {{limit}} for {{feature}} and this card would take you to {{requested}}. Remove a change or upgrade your plan.",
			executionFailed:
				"One change couldn't be applied, so nothing was written. The failing change is highlighted below.",
			groupIndivisible:
				"These changes are applied together — drop the whole group instead.",
			lastItem:
				"This is the only change left. Reject the card instead of dropping it.",
			expired: "This proposal has expired and can no longer be approved.",
			generic: "This couldn't be completed. Please try again.",
		},
		card: {
			homeTitle: "AI Proposals",
			homePending_one: "{{count}} proposal awaiting your review",
			homePending_other: "{{count}} proposals awaiting your review",
			summary_one: "{{count}} change",
			summary_other: "{{count}} changes",
			// The sentence the card opens with: what approving it will do, in words.
			summarySentence: "The assistant wants to {{actions}} in {{farm}}.",
			summarySentenceNoFarm: "The assistant wants to {{actions}}.",
			summaryItemSubject: "{{action}} for {{subject}}",
			summaryRepeat: "{{action}} (×{{count}})",
			summaryMore_one: "{{count}} more change",
			summaryMore_other: "{{count}} more changes",
			showDetails: "Show details",
			hideDetails: "Hide details",
			statusAnnouncement: "{{status}} · {{summary}}",
			edited: "Edited",
			excluded: "Dropped",
			exclude: "Drop",
			unexclude: "Put back",
			excludeGroup: "Drop group",
			unexcludeGroup: "Put group back",
			groupTitle: "Applied together",
			groupExplanation: "approved or dropped as one",
			lastItemHint: "Reject the card instead: this is the only change left.",
			actionDisabled: "Switched off",
			actionDisabledExplanation:
				"An administrator switched this kind of change off. It can be dropped or the card rejected, but not approved.",
			disabledBlocksApproval:
				"This card can't be approved while it contains a change that is switched off.",
			missingTargetRevision:
				"The current values of a record this card edits couldn't be read, so it can't be approved safely. Reload the page.",
			// The id itself tells the approver nothing; pretending otherwise is worse
			// than admitting the record could not be identified.
			unresolvedReference: "Record not identified",
			currentRecord: "Current record",
			originalProposal: "Original proposal",
			reasoning: "Assistant's reasoning",
			executionError: "Couldn't be applied: {{error}}",
			rejectionReason: "Reason: {{reason}}",
			viewRecord: "View record",
			// The button label IS the contract: the finger lands on a control that
			// says what it will do, not on a bare verb. That holds for BOTH taps —
			// the last one, the one that writes, is the one people make unread.
			approveWithSummary_one: "Approve {{count}} change · {{farm}}",
			approveWithSummary_other: "Approve {{count}} changes · {{farm}}",
			approveWithSummaryNoFarm_one: "Approve {{count}} change",
			approveWithSummaryNoFarm_other: "Approve {{count}} changes",
			approveConfirmTitle: "Approve and write to the farm?",
			approveConfirmBody:
				"These changes will be written to {{farm}} now, in this order. This cannot be undone.",
			approveConfirmBodyNoFarm:
				"These changes will be written now, in this order. This cannot be undone.",
			reject: "Reject",
			editTitle: "Edit change",
			editDescription:
				"Adjust the values before approving. The assistant's original stays on record.",
			rejectTitle: "Reject proposal",
			rejectDescription: "Optionally add a reason. This can't be undone.",
			rejectReasonPlaceholder: "Reason (optional)",
			rejectReasonLabel: "Reason",
		},
		toasts: {
			approving: "Approving…",
			approveSuccess: "Proposal approved and applied",
			rejecting: "Rejecting…",
			rejectSuccess: "Proposal rejected",
			excludeSuccess: "Change dropped from this card",
			unexcludeSuccess: "Change put back",
		},
	},
	validation: {
		required: "{{field}} is required",
	},
	help: {
		home: {
			backToApp: "Back to RuralOps",
			searchHint: "Search the steps and questions on this page.",
			allGuides: "Explore all guides",
		},
		// Shell / navigation
		title: "Help",
		description: "Guides and support for RuralOps",
		welcome: "Welcome to RuralOps",
		welcomeDescription:
			"Everything you need to run your farm in one place. Follow the steps below to get set up, or open the user guide for a closer look at each feature.",
		gettingStarted: "Getting Started",
		gettingStartedDescription: "Set up your farm and learn the basics",
		userGuide: "User Guide",
		userGuideDescription: "Detailed guides for every feature",
		support: "Support",
		supportDescription: "Get help from our team",
		learnHowToUse: "Learn how to use RuralOps",
		getHelp: "Get help from our team",
		gettingStartedSteps: "Getting started in 6 steps",

		// Getting Started — feature tour
		step1Title: "Set up your farm",
		step1Description:
			"Create your first establishment (farm) in Settings and add its location and area. You can manage several farms from a single organization.",
		step2Title: "Add your livestock",
		step2Description:
			"Register your herds and individual animals, then start tracking health, breeding and lineage.",
		step3Title: "Map your pastures",
		step3Description:
			"Set up grass production: create paddocks, record measurements, and log grazing and other operations.",
		step4Title: "Track equipment and finances",
		step4Description:
			"Add your machinery to the equipment registry and record income and expenses in the Financial Hub to follow profit and cash flow.",
		step5Title: "Plan your operation",
		step5Description:
			"Use Planning to set annual and tactical objectives, then break them into tasks on the board or calendar and assign them to your team.",
		step6Title: "Get insights",
		step6Description:
			"Run a feasibility analysis for a cow-calf operation and ask the AI assistant for recommendations across your farm.",

		// User Guide overview
		userGuideOverview: "User Guide",
		userGuideOverviewDescription:
			"Browse guides for each part of RuralOps. Pick a topic to learn what it does and the main things you can do there.",
		overviewLabel: "Overview",
		whatYouCanDo: "What you can do",

		// Guide — Farms
		guideEstablishmentsTitle: "Farms",
		guideEstablishmentsDescription:
			"Select, create and manage the farms in your organization",
		farmGuide: {
			intro:
				"Farms organize your livestock, paddocks, equipment and financial records. Use the selector to choose which farms you are viewing, and farm management to maintain each farm’s registration details.",
			contents: "In this guide",
			screenshotsNote:
				"Screenshots show sample data from the local application.",
			enlargeImage: "Open full-size screenshot",
			selectorTitle: "Choose a farm in the top bar",
			selectorIntro:
				"The farm selector sets the scope of the organization’s operational pages. Changing the selection does not edit a farm’s registration details.",
			selectorStep1:
				"On an operational page, open the farm selector in the top navigation bar.",
			selectorStep2:
				"Choose a farm to focus on its records. The list only includes farms you have permission to access.",
			selectorStep3:
				"Choose All Farms to view records across the farms you can access.",
			selectorNote:
				"The selector is disabled in organization settings, where you manage the organization rather than a single farm.",
			selectorCaption:
				"The top navigation selector lets you choose one farm or All Farms, with shortcuts to create and edit farms when permitted.",
			managementTitle: "Find and manage your farms",
			managementIntro:
				"Open Settings → Farms to see the farms in your organization and maintain their details.",
			managementStep1: "Use the search field to find a farm by name.",
			managementStep2:
				"Review the farm’s name, description, address and phone number. Use the pagination controls to browse more farms.",
			managementStep3:
				"Select a farm row or its pencil icon to edit it. On mobile, select the farm’s card.",
			managementCaption:
				"The Farms settings page brings together the farm list, search, pagination and Create New action.",
			createTitle: "Create a farm",
			createIntro:
				"Start from Create farm in the top bar selector, or choose Create New on the Farms settings page.",
			createStep1: "Enter a name that makes the farm easy to recognize.",
			createStep2:
				"In City, type at least two characters, then select a city from the results. Check the state to choose the correct location.",
			createStep3:
				"Add an address, phone number, email and description if useful. These fields are optional.",
			createStep4:
				"Choose Save farm. The button becomes available once you have entered a name and selected a city.",
			createNote:
				"Typing an address does not select a city. Select a city from the search results so location features, including weather, can use it. A farm created from the selector becomes the selected farm; a farm created in settings appears in the list.",
			createCaption:
				"The creation form asks for a farm name and a city selected from search results; contact details and description are optional.",
			editTitle: "Edit a farm",
			editIntro:
				"Open a farm from the settings list, or use the pencil beside its name in the top bar selector.",
			editStep1:
				"Review the form, which opens with the farm’s existing details.",
			editStep2:
				"Update the details you need to change. If an older farm has no selected city, search for and select one before saving.",
			editStep3:
				"Choose Update farm to save, or Cancel to close the form without saving your changes.",
			editNote:
				"Editing updates the farm’s registration details. To change which farm’s records you are viewing, use the top bar selector.",
			editCaption:
				"The edit form displays the farm’s current details and provides Update farm and Cancel actions.",
			permissionsTitle: "Missing a create or edit action?",
			permissionsBody:
				"Creating and editing farms requires permission to manage farms. If these actions are unavailable, ask your organization owner to review your access.",
		},

		// Guide — Livestock
		guideLivestockTitle: "Livestock",
		guideLivestockDescription: "Track your herds, animals, health and breeding",
		livestockGuide: {
			intro:
				"Use Livestock to organize herds, register animals and follow their weight, health and reproduction records. Start by choosing a farm, then open a herd or animal to work with its details.",
			contents: "In this guide",
			screenshotsNote:
				"Screenshots show sample data from the local application.",
			enlargeImage: "Open full-size screenshot",
			overviewTitle: "Choose a farm and explore Livestock",
			overviewIntro:
				"The Livestock page brings together the herds and animals in the farms you can access.",
			overviewStep1: "Open Livestock from the main navigation.",
			overviewStep2:
				"Use the top bar selector to choose one farm, or All Farms to see records across your accessible farms.",
			overviewStep3:
				"Switch between Herds and Animals. Open a herd or animal to see its details and records.",
			overviewNote:
				"When you start creating a herd, adding an animal or importing from All Farms, select the destination farm before continuing.",
			overviewCaption:
				"The Livestock page shows the selected farm scope, Herds and Animals tabs, and available creation actions.",
			herdsTitle: "Create and manage a herd",
			herdsIntro:
				"Herds group animals for day-to-day management. Create the herd first, then add its animals.",
			herdsStep1:
				"On the Herds tab, choose Add Herd and confirm the destination farm if prompted.",
			herdsStep2:
				"Enter a herd name. Add a purpose, primary breed, management system or planning details if useful, then choose Create herd.",
			herdsStep3:
				"Open the herd to review its animals and records. Use Edit to update its details and the animal controls to manage its members.",
			herdsNote:
				"The name is the only required herd field. Paddock assignment and rotation are available from the herd details when permitted.",
			herdsCaption:
				"The herd form starts with a name and offers optional purpose, breed, management and planning fields.",
			animalsTitle: "Find animals and narrow the list",
			animalsIntro:
				"Use the Animals tab to find an individual animal or a group that needs attention.",
			animalsStep1: "Search by tag or breed within the selected farm scope.",
			animalsStep2:
				"Use quick filters for groups such as calves or animals without a recent weighing. Open advanced filters to refine the search.",
			animalsStep3:
				"Open an animal from the results to view its details. Clear filters when you want to return to the wider list.",
			animalsNote:
				"When permitted, selecting animals reveals bulk actions. For an existing animal list, use Import and review the extracted information before completing the import.",
			animalsCaption:
				"The animal list combines search, quick filters and advanced filters with links to each animal’s details.",
			createTitle: "Register an animal",
			createIntro:
				"Choose Add Animal on the Animals tab. If All Farms is selected, choose the farm where the animal belongs.",
			createStep1:
				"Enter the tag number, select the sex and enter the birth date. Resolve any warning that the tag is already in use.",
			createStep2:
				"Open the optional details you need, such as breed, herd or origin, and fill in the information you have.",
			createStep3:
				"Choose Create animal to save, or Create and next to save and begin another animal.",
			createNote:
				"To record a birth and link the calf to its mother, use the calving option in Record on the mother’s detail page.",
			createCaption:
				"Animal registration starts with the tag number, sex and birth date; additional details are optional.",
			detailTitle: "Review and edit an animal",
			detailIntro:
				"The animal detail page brings its identification, weight history, care information and records into one place.",
			detailStep1:
				"Open the animal from the Animals list or its herd’s animal list and confirm the tag number.",
			detailStep2:
				"Review the weight chart, available health and reproduction information, and the record timeline.",
			detailStep3:
				"Choose Edit to correct or complete registration details, then choose Save changes. Use Cancel to leave without saving.",
			detailNote:
				"Use Record for a new weighing, vaccination or other event. Use Edit for changes to the animal’s registration details.",
			detailCaption:
				"The animal detail page shows its identity, weight and care information, with access to editing and records.",
			recordsTitle: "Record weighings, health and reproduction",
			recordsIntro:
				"Start a record from the animal or herd you are working with so the form opens with that target selected.",
			recordsStep1:
				"Open the animal or herd detail page, find its records section and choose Record.",
			recordsStep2:
				"Search for or select the record type, such as weight, vaccination, treatment or a reproduction event.",
			recordsStep3:
				"Check the target and date, complete the fields for that record type and save. Review the new entry in the record timeline.",
			recordsNote:
				"Available types depend on whether you opened an animal or a herd and, for an animal, its sex. The calving flow also registers the calf.",
			recordsCaption:
				"The record picker groups the available activities and measurements and lets you search by type.",
			permissionsTitle: "Missing a create, edit or record action?",
			permissionsBody:
				"Creating herds and animals, importing and recording changes requires permission to manage livestock. Ask your organization owner to review your access if these actions are unavailable.",
		},
		guideLivestockOverview:
			"The livestock module is your animal registry. Group animals into herds, record individual details, and keep a full history of health events, breeding and lineage for every animal.",
		guideLivestockTask1: "Create herds and register individual animals",
		guideLivestockTask2: "Record health events and medical records",
		guideLivestockTask3: "Track breeding, reproduction and lineage",
		guideLivestockTask4: "Monitor herd numbers and mortality over time",
		guideLivestockTip:
			"Livestock is farm-scoped — select a farm first to see and manage its animals.",

		// Guide — Grass Production
		guideGrassProductionTitle: "Paddocks",
		guideGrassProductionDescription:
			"Find, create and manage paddocks and their field records",
		paddockGuide: {
			intro:
				"Use Paddocks to organize each farm’s pasture areas, maintain their boundaries and follow cultivars, grazing, measurements and field operations.",
			contents: "In this guide",
			screenshotsNote:
				"Screenshots show sample data from the local application.",
			enlargeImage: "Open full-size screenshot",
			listTitle: "Find your paddocks",
			listIntro:
				"Open Paddocks from the main navigation to see pasture areas in the farms you can access.",
			listStep1:
				"Use the top bar selector to choose a farm, or All Farms to view paddocks across your accessible farms.",
			listStep2:
				"Use the search field to find a paddock. Review its name, farm, area, status and cultivars in the list.",
			listStep3:
				"Select a paddock row or card to open its details, grazing information and records.",
			listStep4:
				"Choose Create Paddock to add an area. When viewing All Farms, select the destination farm before continuing.",
			listNote:
				"Cultivar Library opens the organization’s cultivar catalog. Assign cultivars to an individual paddock from its detail page after creating it.",
			listCaption:
				"The Paddocks page shows the summary, searchable list and Create Paddock action.",
			createTitle: "Create a paddock",
			createIntro:
				"Choose Create Paddock and confirm the farm where the paddock belongs.",
			createStep1:
				"Enter a recognizable name. Add a code if useful; you can also select the suggested code.",
			createStep2:
				"Enter an area greater than zero in hectares, or draw a boundary on the map to calculate it. A boundary is optional.",
			createStep3:
				"Review the use type and status. Add soil type if useful, and expand More details for slope, yield, forage quality and utilization targets.",
			createStep4:
				"Choose Create Paddock to save. If the form highlights a missing or invalid value, correct it and submit again.",
			createNote:
				"When an area comes from the map, Enter manually lets you override it. Check the hectares before saving; the manually entered area takes precedence over the boundary calculation.",
			createCaption:
				"The creation form combines paddock identity, area and use with an optional boundary map.",
			editTitle: "Edit paddock details",
			editIntro:
				"Open a paddock from the list, then choose Edit on its detail page.",
			editStep1: "Review the existing name, code, area, use type and status.",
			editStep2:
				"Update the fields you need to change. Adjust the boundary on the map or enter the area manually as appropriate.",
			editStep3:
				"Expand More details to review slope and production or quality targets when needed.",
			editStep4:
				"Choose Save to apply your changes, or Cancel to leave the form without saving.",
			editNote:
				"With a saved boundary, the edit form recalculates the area from the map when it opens. Check the hectares before saving and use Enter manually if needed. Cultivars and field records are managed separately on the detail page.",
			editCaption:
				"The edit form opens with the paddock’s saved details and boundary, with Save and Cancel actions.",
			recordsTitle: "Follow cultivars, grazing and records",
			recordsIntro:
				"The paddock’s Overview brings together its indicators, cultivars, grazing and field history.",
			recordsStep1:
				"In Cultivars, use the manage action to select cultivars and enter their coverage. Add a missing cultivar through the catalog link, then save the assignments.",
			recordsStep2:
				"Review Current Herds and Records. Use the record filters to focus on harvest or fertility, and Show older to expand the history when available.",
			recordsStep3:
				"Choose Record in the Records card, then select a pasture measurement or an operation such as grazing, fertilization or cutting. For a measurement, enter the date and sward height; add biomass and notes if available. For an operation, review its type, date and relevant fields, then save.",
			recordsStep4:
				"Open Soil & Fertility to consult soil analyses and recommendations for this paddock.",
			recordsNote:
				"Cultivar coverage must total no more than 100%. Record the actual dates and check units so the paddock’s history remains useful for day-to-day decisions.",
			recordsCaption:
				"The paddock detail page brings together cultivars, grazing, records and the Soil & Fertility tab.",
			permissionsTitle: "Missing an action or a record?",
			permissionsBody:
				"Access depends on your farm access and permissions. Managing paddocks, viewing records and creating measurements or operations use separate permissions. Ask your organization owner to review your access if something is unavailable.",
		},
		guideGrassProductionOverview:
			"Grass production helps you manage your pastures. Define paddocks, measure available forage, and log operations such as grazing, fertilization and cutting to keep your grazing plan on track.",
		guideGrassProductionTask1:
			"Create paddocks and record their area and cultivar",
		guideGrassProductionTask2: "Add biomass and height measurements",
		guideGrassProductionTask3:
			"Log operations such as grazing, fertilization and cutting",
		guideGrassProductionTask4:
			"Review alerts, soil analysis and growth forecasts",
		guideGrassProductionTip:
			"Keep measurements up to date so forecasts and recommendations stay accurate.",

		// Guide — Equipment
		guideEquipmentTitle: "Equipment",
		guideEquipmentDescription:
			"Keep an inventory of your machinery and maintenance",
		guideEquipmentOverview:
			"The equipment module is an inventory of your farm machinery and assets. Track where each unit is, schedule and log maintenance, and keep your fleet organized across farms.",
		guideEquipmentTask1:
			"Register equipment and assign it to a farm and location",
		guideEquipmentTask2: "Record maintenance history and upcoming service",
		guideEquipmentTask3: "Track the current location of each unit",
		guideEquipmentTask4: "Review your fleet across all farms",
		guideEquipmentTip:
			"Logging maintenance as you go keeps a useful service history for each machine.",

		// Guide — Financial
		guideFinancialTitle: "Financial Hub",
		guideFinancialDescription:
			"Record transactions and track profit and cash flow",
		guideFinancialOverview:
			"The Financial Hub is where you record income and expenses and see how your farm is performing. Categorize transactions, review profit and loss, and follow cash flow over any period.",
		guideFinancialTask1: "Record income and expense transactions",
		guideFinancialTask2: "Organize transactions by category",
		guideFinancialTask3: "Review profit & loss and cash flow reports",
		guideFinancialTask4: "Analyze results by period and by farm",
		guideFinancialTip:
			"Your organization's currency is set at creation. Pick a consistent set of categories early so your reports stay comparable over time.",

		// Guide — Planning
		guidePlanningTitle: "Planning & Tasks",
		guidePlanningDescription: "Set objectives and organize work into tasks",
		guidePlanningOverview:
			"Planning turns goals into action. Define annual and tactical objectives, then break them into tasks you can track on a board, list or calendar and assign to your team.",
		guidePlanningTask1: "Create annual and tactical objectives",
		guidePlanningTask2: "Add tasks and assign them to team members",
		guidePlanningTask3: "Track work on the Kanban board, list or calendar",
		guidePlanningTask4: "Use the rotation planner to schedule grazing moves",
		guidePlanningTip:
			"Link tasks to objectives so you can see progress toward your bigger goals.",

		// Guide — Feasibility
		guideFeasibilityTitle: "Feasibility Analysis",
		guideFeasibilityDescription: "Model the viability of a cow-calf operation",
		guideFeasibilityOverview:
			"Feasibility analysis helps you decide whether a cow-calf operation makes sense before you commit. Enter your herd, forage, reproduction, nutrition and commercial assumptions and RuralOps calculates a financial verdict.",
		guideFeasibilityTask1: "Define herd size and structure",
		guideFeasibilityTask2: "Set forage, reproduction and nutrition assumptions",
		guideFeasibilityTask3: "Add commercial inputs such as prices and costs",
		guideFeasibilityTask4: "Review the calculated verdict and key figures",
		guideFeasibilityTip:
			"Try a few scenarios with different assumptions to understand what drives the result.",

		// Guide — Marketplace
		guideMarketplaceTitle: "Marketplace",
		guideMarketplaceDescription: "Buy and sell livestock with other producers",
		guideMarketplaceOverview:
			"The marketplace connects you with other producers to trade livestock. Browse offers, filter by what matters to you, list your own animals for sale, and manage incoming and outgoing requests.",
		guideMarketplaceTask1:
			"Browse offers and filter by region, breed, price and health",
		guideMarketplaceTask2: "Create an offer to sell your livestock",
		guideMarketplaceTask3: "Make a purchase request on an offer",
		guideMarketplaceTask4: "Manage your incoming and outgoing requests",
		guideMarketplaceTip:
			"Complete health and animal details on your offers to attract more serious buyers.",

		// Guide — AI Assistant
		guideAssistantTitle: "AI Assistant",
		guideAssistantDescription: "Ask questions and get farm recommendations",
		guideAssistantOverview:
			"The AI assistant answers questions about your operation and suggests next steps. Ask about a single farm or across all of them, and use the suggested prompts to get started.",
		guideAssistantTask1: "Ask questions about your farm in plain language",
		guideAssistantTask2: "Scope the conversation to one farm or all farms",
		guideAssistantTask3: "Use suggested prompts for quick insights",
		guideAssistantTask4: "Act on recommendations across your modules",
		guideAssistantTip:
			"The more data you record in RuralOps, the more useful the assistant's answers become.",

		// Support — layout / tabs
		supportTitle: "Support",
		submitTicket: "Submit Ticket",
		myTickets: "My Tickets",

		// Support — submit form
		supportFormIntro:
			"Tell us how we can help. Choose a type and describe your request.",
		supportRequestHint: "Get help with RuralOps",
		supportSuggestionHint: "Share an improvement idea",
		supportComplaintHint: "Tell us what went wrong",
		supportDescriptionPrompt:
			"What were you trying to do?\nWhat happened, and what did you expect?",
		supportPrivacyHint: "Do not include passwords or payment details.",
		supportRequiredHint: "* Required fields",
		supportGuidanceTitle: "Help us understand",
		supportGuidanceWhere: "Tell us which page or feature you were using.",
		supportGuidanceSteps: "Describe the steps and any error message you saw.",
		supportGuidanceExpected: "Explain what you expected to happen.",
		supportFollowUpTitle: "Keep the conversation together",
		supportFollowUpDescription:
			"Already opened a ticket about this? Add a comment to it. You can follow replies and status in My tickets.",
		supportSuccessTitle: "Your ticket has been sent",
		supportSuccessDescription:
			"You can follow its status and continue the conversation with our team in My tickets.",
		supportAnotherTicket: "Open another ticket",
		supportFormTitle: "Submit a support ticket",
		supportFieldTitle: "Title",
		supportFieldType: "Type",
		supportFieldDescription: "Description",
		supportPlaceholderTitle: "Briefly summarize your request",
		supportPlaceholderType: "Select a type",
		supportPlaceholderDescription: "Describe your request in detail",
		supportHintTitle: "A short, clear title helps us respond faster.",
		supportHintType: "Choose the option that best matches your ticket.",
		supportHintDescription:
			"Include steps, context or anything that helps us understand.",
		supportTypeRequest: "Request",
		supportTypeSuggestion: "Suggestion",
		supportTypeComplaint: "Complaint",
		supportCharacterCountTitle: "Title",
		supportCharacterCountDescription: "Description",
		supportSubmit: "Submit Ticket",
		supportSubmitting: "Submitting...",

		// Support — tickets list
		noTickets: "No tickets yet",
		noTicketsDescription:
			"You haven't submitted any support tickets. Create one and our team will get back to you.",
		statusOpen: "Open",
		statusWaitingForUser: "Waiting for You",
		statusWaitingForStaff: "Waiting for Support",
		statusResolved: "Resolved",
		statusClosed: "Closed",

		// Support — ticket detail
		ticketDetails: "Ticket",
		ticketInformation: "Ticket Information",
		comments: "Comments",
		noComments: "No comments yet",
		addComment: "Add a comment",
		commentPlaceholder: "Write a comment...",
		commentSubmitting: "Sending...",
		supportTeam: "Support Team",
		staff: "Staff",
		you: "You",
	},
	weather: {
		matrix: {
			today: "Today",
			moreFarms_one: "{{count}} more farm · sorted by rain expected",
			moreFarms_other: "{{count}} more farms · sorted by rain expected",
			sortedByRain: "Sorted by rain expected",
			showFewer: "Show fewer",
			seeAllFarms: "See all {{count}} farms",
			farm: "Farm",
			now: "Now",
			conditions: "Conditions",
			nextDays_one: "Next day",
			nextDays_other: "Next {{count}} days",
		},
		title: "Weather",
		details: {
			climatempo: "See on Climatempo",
			windy: "See on Windy",
		},
		refresh: "Refresh weather",
		retry: "Try again",
		noFarms: "No farms in the current filter to show weather for.",
		truncated:
			"Showing weather for the first {{shown}} farms. Select a farm to see the rest.",
		temperature: "{{value}} °C",
		range: "{{max}}° / {{min}}°",
		percent: "{{value}}%",
		millimetres: "{{value}} mm",
		kph: "{{value}} km/h",
		humidity: "Humidity",
		precipitation: "Rain",
		wind: "Wind",
		notReported: "Not reported",
		horizonShort_one:
			"This provider served only {{served}} day of the {{requested}} requested.",
		horizonShort_other:
			"This provider served only {{served}} days of the {{requested}} requested.",
		thisFarm: "This farm",
		locationMissing: {
			message:
				"This farm has no location on file yet, so we cannot look up its weather.",
			action: "Set the farm's city",
		},
		unavailable: {
			message:
				"We could not reach any weather provider right now. Your farm records are unaffected.",
		},
		condition: {
			CLEAR: "Clear",
			PARTLY_CLOUDY: "Partly cloudy",
			CLOUDY: "Cloudy",
			FOG: "Fog",
			DRIZZLE: "Drizzle",
			RAIN: "Rain",
			HEAVY_RAIN: "Heavy rain",
			SNOW: "Snow",
			THUNDERSTORM: "Thunderstorm",
			UNKNOWN: "Condition not reported",
		},
		attribution: {
			open_meteo: "Source: Open-Meteo",
			google_weather: "Source: Includes weather data from Google",
			openweathermap: "Source: OpenWeatherMap",
		},
	},
};

export default translations;
